import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  FiDatabase, FiCheckCircle, FiAlertCircle, FiGrid, FiTrendingUp,
  FiUsers, FiBarChart2, FiSearch, FiFilter, FiChevronDown, FiChevronUp,
  FiZap, FiExternalLink, FiX, FiDownload, FiList, FiMaximize2, FiMinimize2,
  FiPieChart, FiArrowRight, FiChevronRight, FiMinus, FiSliders,
  FiMapPin, FiCalendar, FiDollarSign, FiBriefcase, FiLayers, FiGlobe,
  FiTag, FiActivity, FiInfo, FiMessageSquare
} from 'react-icons/fi';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../api';
import { formatCurrency, formatDate, formatYear } from '../utils/formatters';
import { parseVDBDescription } from '../utils/parsers';
import { useDebounce } from '../hooks/useDebounce';
import GamingLoader from '../components/GamingLoader';
import ContextualAnalytics from '../components/analytics/ContextualAnalytics';
import HierarchicalLocationDropdown from '../components/HierarchicalLocationDropdown';
import NLPLogsDisplay from '../components/NLPLogsDisplay';
import PlatformLoadingChecklist from '../components/PlatformLoadingChecklist';
import { locationMatchesFilter } from '../utils/locationGrouping';
import { calculateDealsNLP, calculateCompaniesNLP, calculateInvestorsNLP } from '../utils/analytics/nlpAnalytics';
import { useNLPProcessing } from '../hooks/useNLPProcessing';
import { useTheme } from '../context/ThemeContext';
import { useDevice } from '../context/DeviceContext';
import transcendLogoDark from '../assets/transcend-logo-dark.png';
import transcendLogoLight from '../assets/transcend-logo-light.png';
import './LandingPage.css';

// Authenticated fetch wrapper — adds JWT token from localStorage
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('transcend_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
};


/**
 * HeroResultCard - Renders a search result card with entity-specific metadata and scores
 * Used in hero search results (semantic, hybrid, keyword modes)
 */
function HeroResultCard({ result, tier, onClick, showScore, searchMode }) {
  const entityType = result.entity_type || result.type || 'company';

  // Format currency helper
  const formatMoney = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && value.includes('$')) return value;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}B`;
    if (num >= 1) return `$${num.toFixed(1)}M`;
    return `$${(num * 1000).toFixed(0)}K`;
  };

  // Format date helper
  const formatDateShort = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Render entity-specific metadata
  const renderEntityMeta = () => {
    if (entityType === 'company') {
      return (
        <div className="hero-card-metadata">
          {result.primary_industry && (
            <span className="hero-card-meta"><FiTag size={11} /> {result.primary_industry}</span>
          )}
          {result.hq_location && (
            <span className="hero-card-meta"><FiMapPin size={11} /> {result.hq_location}</span>
          )}
          {(result.total_raised || result.total_raised_usd) && (
            <span className="hero-card-meta"><FiDollarSign size={11} /> {formatMoney(result.total_raised_usd) || result.total_raised}</span>
          )}
          {result.employees && (
            <span className="hero-card-meta"><FiUsers size={11} /> {result.employees}</span>
          )}
          {result.year_founded && (
            <span className="hero-card-meta"><FiCalendar size={11} /> Est. {result.year_founded}</span>
          )}
        </div>
      );
    } else if (entityType === 'deal') {
      return (
        <div className="hero-card-metadata">
          {result.deal_type && (
            <span className="hero-card-meta"><FiTag size={11} /> {result.deal_type}</span>
          )}
          {result.deal_date && (
            <span className="hero-card-meta"><FiCalendar size={11} /> {formatDateShort(result.deal_date)}</span>
          )}
          {result.deal_size_usd && (
            <span className="hero-card-meta"><FiDollarSign size={11} /> {formatMoney(result.deal_size_usd)}</span>
          )}
          {result.post_valuation && (
            <span className="hero-card-meta"><FiActivity size={11} /> Val: {formatMoney(result.post_valuation)}</span>
          )}
          {result.hq_location && (
            <span className="hero-card-meta"><FiMapPin size={11} /> {result.hq_location}</span>
          )}
          {result.investors && result.investors.length > 0 && (
            <span className="hero-card-meta"><FiBriefcase size={11} /> {result.investors.slice(0, 2).join(', ')}{result.investors.length > 2 ? '...' : ''}</span>
          )}
        </div>
      );
    } else if (entityType === 'investor') {
      return (
        <div className="hero-card-metadata">
          {result.investor_type && (
            <span className="hero-card-meta"><FiTag size={11} /> {result.investor_type}</span>
          )}
          {result.hq_location && (
            <span className="hero-card-meta"><FiMapPin size={11} /> {result.hq_location}</span>
          )}
          {result.investments_count && (
            <span className="hero-card-meta"><FiActivity size={11} /> {result.investments_count} investments</span>
          )}
          {result.aum && (
            <span className="hero-card-meta"><FiDollarSign size={11} /> AUM: {formatMoney(result.aum / 1000000)}</span>
          )}
          {result.verticals && result.verticals.length > 0 && (
            <span className="hero-card-meta"><FiLayers size={11} /> {result.verticals.slice(0, 3).join(', ')}</span>
          )}
          {result.website && (
            <span className="hero-card-meta"><FiGlobe size={11} /> {result.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</span>
          )}
        </div>
      );
    }
    // Fallback for unknown entity types
    return (
      <div className="hero-card-metadata">
        {result.hq_location && <span className="hero-card-meta"><FiMapPin size={11} /> {result.hq_location}</span>}
        {result.deal_date && <span className="hero-card-meta"><FiCalendar size={11} /> {formatDateShort(result.deal_date)}</span>}
      </div>
    );
  };

  return (
    <div
      className={`hero-result-card ${tier ? `tier${tier}` : ''}`}
      onClick={onClick}
    >
      <div className="hero-card-top">
        <span className={`entity-badge ${entityType}`}>{entityType}</span>
        {/* Show score for semantic/hybrid searches */}
        {showScore && result.score !== undefined && searchMode !== 'strict' && (
          <span className="hero-card-score" title={`Dense: ${result.dense_score?.toFixed(3) || 'N/A'}, Sparse: ${result.sparse_score?.toFixed(3) || 'N/A'}`}>
            {(result.score * 100).toFixed(0)}%
          </span>
        )}
        {result._agent_reasoning && (
          <div className="reasoning-trigger" onClick={(e) => e.stopPropagation()}>
            <span className="reasoning-icon">◇</span>
            <div className="reasoning-tooltip">
              <div className="reasoning-tooltip-header">Agent Reasoning</div>
              <p>{result._agent_reasoning}</p>
            </div>
          </div>
        )}
      </div>
      <h4>{result.name || result.companies || 'Unknown'}</h4>
      <p>{(result.description || result.deal_synopsis || '').substring(0, 100)}{(result.description || result.deal_synopsis || '').length > 100 ? '...' : ''}</p>
      {renderEntityMeta()}
    </div>
  );
}

/**
 * Component to render parsed VDB data in a structured card layout
 */
function VDBStructuredCard({ result, isExpanded, onToggle, expandKey, linkUrl }) {
  const parsed = parseVDBDescription(result.description || result.text || '');
  const { cleanDescription, fields, keywords, verticals } = parsed;

  // Build location string from parsed fields
  const locationParts = [fields.city, fields.state, fields.country].filter(Boolean);
  const locationStr = fields.location || locationParts.join(', ');

  // Source color coding - DISTINCT colors for quick visual identification
  const sourceColors = {
    'sql': { bg: '#fef3c7', text: '#92400e', icon: '🗄️', label: 'SQL' },
    'sql_lookup': { bg: '#fef3c7', text: '#92400e', icon: '🗄️', label: 'SQL' },
    'vdb_dense': { bg: '#dbeafe', text: '#1a1f3a', icon: '🧠', label: 'Dense' },
    'vdb_sparse': { bg: '#fce7f3', text: '#9d174d', icon: '⚡', label: 'Sparse' },
    'pinecone_dense': { bg: '#dbeafe', text: '#1a1f3a', icon: '🧠', label: 'Dense' },
    'pinecone_sparse': { bg: '#fce7f3', text: '#9d174d', icon: '⚡', label: 'Sparse' }
  };
  const sourceKey = result.source?.replace('pinecone_', 'vdb_') || 'vdb_dense';
  const sourceStyle = sourceColors[sourceKey] || sourceColors['vdb_dense'];

  return (
    <div
      style={{
        padding: '16px',
        background: 'white',
        borderRadius: '8px',
        marginBottom: '10px',
        border: '1px solid #e5e7eb',
        transition: 'all 0.2s'
      }}
      onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)'}
      onMouseOut={(e) => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Header with name */}
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{textDecoration: 'none', color: 'inherit'}}
        onClick={(e) => { if (linkUrl === '#') e.preventDefault(); }}
      >
        <div style={{fontWeight: 'bold', color: '#1f2937', fontSize: '16px', marginBottom: '6px'}}>
          {result.name || result.companies || 'Unnamed Entity'}
          {linkUrl !== '#' && <span style={{marginLeft: '8px', color: '#1a1f3a', fontSize: '14px'}}>→</span>}
        </div>
      </a>

      {/* Collapsed View - Clean description preview */}
      {!isExpanded && (
        <div style={{fontSize: '14px', color: '#6b7280', marginTop: '6px', lineHeight: '1.5'}}>
          {cleanDescription.substring(0, 200)}
          {cleanDescription.length > 200 && '...'}
        </div>
      )}

      {/* Expanded View - Structured data */}
      {isExpanded && (
        <div style={{marginTop: '12px'}}>
          {/* Clean description - full text */}
          {cleanDescription && (
            <div style={{
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '6px',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151',
              marginBottom: '16px'
            }}>
              {cleanDescription}
            </div>
          )}

          {/* Structured Fields Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            {fields.industry && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #1a1f3a'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Industry</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#1a1f3a', marginTop: '2px'}}>{fields.industry}</div>
              </div>
            )}

            {locationStr && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #f59e0b'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Location</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#92400e', marginTop: '2px'}}>{locationStr}</div>
              </div>
            )}

            {fields.totalRaised && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #10b981'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Total Raised</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#065f46', marginTop: '2px'}}>{fields.totalRaised}</div>
              </div>
            )}

            {fields.lastFinancingDate && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #e0e7ff 0%, #eef2ff 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #6366f1'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Last Financing</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#4338ca', marginTop: '2px'}}>{fields.lastFinancingDate}</div>
              </div>
            )}

            {fields.revenue && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #fce7f3 0%, #fdf2f8 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #ec4899'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Revenue</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#9d174d', marginTop: '2px'}}>{fields.revenue}</div>
              </div>
            )}

            {fields.valuation && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #9333ea'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Valuation</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#6b21a8', marginTop: '2px'}}>{fields.valuation}</div>
              </div>
            )}

            {fields.founded && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #64748b'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Founded</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#334155', marginTop: '2px'}}>{formatYear(fields.founded)}</div>
              </div>
            )}

            {fields.employees && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #cffafe 0%, #ecfeff 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #06b6d4'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Employees</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#0e7490', marginTop: '2px'}}>{fields.employees}</div>
              </div>
            )}

            {(fields.status || fields.financingStatus) && (
              <div style={{
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #fed7aa 0%, #fff7ed 100%)',
                borderRadius: '8px',
                borderLeft: '3px solid #f97316'
              }}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Status</div>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#c2410c', marginTop: '2px'}}>
                  {fields.status}{fields.financingStatus && ` • ${fields.financingStatus}`}
                </div>
              </div>
            )}
          </div>

          {/* Verticals as colored badges */}
          {verticals.length > 0 && (
            <div style={{marginBottom: '12px'}}>
              <div style={{fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                Verticals
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {verticals.map((vertical, i) => (
                  <span
                    key={i}
                    style={{
                      padding: '4px 10px',
                      background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                      color: '#4338ca',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {vertical}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Keywords as tags */}
          {keywords.length > 0 && (
            <div>
              <div style={{fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                Keywords
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {keywords.map((keyword, i) => (
                  <span
                    key={i}
                    style={{
                      padding: '3px 8px',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Active Investors if available */}
          {fields.activeInvestors && (
            <div style={{marginTop: '12px', padding: '10px', background: '#f9fafb', borderRadius: '6px'}}>
              <div style={{fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px'}}>Active Investors</div>
              <div style={{fontSize: '13px', color: '#374151'}}>{fields.activeInvestors}</div>
            </div>
          )}
        </div>
      )}

      {/* Show more/less button */}
      <button
        onClick={onToggle}
        style={{
          marginTop: '10px',
          background: 'none',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          color: '#1a1f3a',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '600',
          padding: '6px 12px'
        }}
      >
        {isExpanded ? '▲ Show less' : '▼ Show more'}
      </button>

      {/* Metadata footer with Color Coded Source */}
      <div style={{fontSize: '12px', color: '#9ca3af', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '16px', alignItems: 'center'}}>
        <span>📁 {result.entity_type}</span>
        {/* Source Badge - Color coded for quick visual identification */}
        <span style={{
          background: sourceStyle.bg,
          color: sourceStyle.text,
          padding: '2px 8px',
          borderRadius: '10px',
          fontWeight: '600',
          fontSize: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          {sourceStyle.icon} {sourceStyle.label}
        </span>
        <span>🎯 {result.match_type}</span>
        {result.relevance_score && <span>⚡ {(result.relevance_score * 100).toFixed(1)}% match</span>}
      </div>
    </div>
  );
}

/**
 * SQLStructuredCard - Rich display component for SQL database results
 * Shows all available metadata in a clean, organized layout
 */
function SQLStructuredCard({ result, isExpanded, onToggle, linkUrl, tier }) {
  // Get entity-specific fields
  const isCompany = result.entity_type === 'company';
  const isDeal = result.entity_type === 'deal';
  const isInvestor = result.entity_type === 'investor';

  // Tier styling - Professional, minimal labels
  const tierColors = {
    1: { bg: 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)', border: '#22c55e', text: '#15803d', label: 'Direct Match' },
    2: { bg: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)', border: '#1a1f3a', text: '#1a1f3a', label: 'Related' },
    3: { bg: 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%)', border: '#a855f7', text: '#7c3aed', label: 'Exploratory' }
  };
  const tierStyle = tierColors[tier] || tierColors[3];

  // Source color coding - DISTINCT from tier badges
  const sourceColors = {
    'sql': { bg: '#fef3c7', text: '#92400e', icon: '🗄️', label: 'SQL' },
    'sql_lookup': { bg: '#fef3c7', text: '#92400e', icon: '🗄️', label: 'SQL' },
    'vdb_dense': { bg: '#dbeafe', text: '#1a1f3a', icon: '🧠', label: 'Dense' },
    'vdb_sparse': { bg: '#fce7f3', text: '#9d174d', icon: '⚡', label: 'Sparse' }
  };
  const sourceStyle = sourceColors[result.source] || sourceColors['sql'];

  // Build location string
  const locationParts = [
    result.hq_city,
    result.hq_state || result.hq_stateprovince,
    result.hq_country || result.hq_countryterritoryregion
  ].filter(Boolean);
  const locationStr = result.hq_location || locationParts.join(', ');

  // Get verticals and keywords as arrays
  const verticals = Array.isArray(result.verticals) ? result.verticals :
    (typeof result.verticals === 'string' ? result.verticals.split(',').map(v => v.trim()).filter(Boolean) : []);
  const keywords = Array.isArray(result.keywords) ? result.keywords :
    (typeof result.keywords === 'string' ? result.keywords.split(',').map(k => k.trim()).filter(Boolean) : []);

  return (
    <div
      style={{
        padding: '16px',
        background: 'white',
        borderRadius: '10px',
        marginBottom: '12px',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${tierStyle.border}`,
        transition: 'all 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}
      onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
      onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
    >
      {/* Header Row */}
      <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px'}}>
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{textDecoration: 'none', color: 'inherit', flex: 1}}
          onClick={(e) => { if (linkUrl === '#') e.preventDefault(); }}
        >
          <div style={{fontWeight: '700', color: '#1f2937', fontSize: '17px', display: 'flex', alignItems: 'center', gap: '8px'}}>
            {result.name || result.companies || 'Unnamed Entity'}
            {linkUrl !== '#' && <FiExternalLink size={14} style={{color: '#1a1f3a'}} />}
          </div>
        </a>

        {/* Tier Badge */}
        <div style={{
          padding: '4px 10px',
          background: tierStyle.bg,
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          color: tierStyle.text,
          whiteSpace: 'nowrap'
        }}>
          {tierStyle.label}
        </div>
      </div>

      {/* Entity Type & Industry Badge */}
      <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px', alignItems: 'center'}}>
        <span style={{
          padding: '3px 8px',
          background: isCompany ? '#dbeafe' : isDeal ? '#fef3c7' : '#d1fae5',
          color: isCompany ? '#1a1f3a' : isDeal ? '#92400e' : '#065f46',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'uppercase'
        }}>
          {result.entity_type}
        </span>

        {(result.primary_industry || result.primary_industry_code) && (
          <span style={{
            padding: '3px 8px',
            background: '#f3f4f6',
            color: '#374151',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {result.primary_industry || result.primary_industry_code}
          </span>
        )}

        {isDeal && result.deal_type && (
          <span style={{
            padding: '3px 8px',
            background: '#fef3c7',
            color: '#92400e',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {result.deal_type}
          </span>
        )}

        {/* AI Reasoning Badge - Shows when agent provided reasoning */}
        {result._agent_reasoning && (
          <div className="ai-reasoning-badge-wrapper">
            <span
              className="ai-reasoning-badge"
              onClick={(e) => {
                e.stopPropagation();
                const popup = e.currentTarget.nextElementSibling;
                if (popup) {
                  popup.style.display = popup.style.display === 'block' ? 'none' : 'block';
                }
              }}
            >
              <span>🧠</span>
              AI Insight
            </span>
            <div className="ai-reasoning-popup" onClick={(e) => e.stopPropagation()}>
              <div className="ai-reasoning-popup-header">
                <span>🧠 Agent Reasoning</span>
                <button onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.closest('.ai-reasoning-popup').style.display = 'none';
                }}>×</button>
              </div>
              <p className="ai-reasoning-popup-text">{result._agent_reasoning}</p>
            </div>
          </div>
        )}
      </div>

      {/* Description Preview */}
      {!isExpanded && (
        <div style={{fontSize: '14px', color: '#6b7280', lineHeight: '1.5', marginBottom: '10px'}}>
          {(result.description || result.deal_synopsis || '').substring(0, 180)}
          {(result.description || result.deal_synopsis || '').length > 180 && '...'}
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div style={{marginTop: '12px'}}>
          {/* Full Description */}
          {(result.description || result.deal_synopsis) && (
            <div style={{
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '8px',
              fontSize: '14px',
              lineHeight: '1.7',
              color: '#374151',
              marginBottom: '16px'
            }}>
              {result.description || result.deal_synopsis}
            </div>
          )}

          {/* Metadata Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '10px',
            marginBottom: '16px'
          }}>
            {locationStr && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)', borderRadius: '8px', borderLeft: '3px solid #f59e0b'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px'}}>📍 Location</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#78350f', marginTop: '2px'}}>{locationStr}</div>
              </div>
            )}

            {(result.total_raised || result.total_raised_usd) && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%)', borderRadius: '8px', borderLeft: '3px solid #10b981'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.5px'}}>💰 Total Raised</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '2px'}}>{result.total_raised || `$${(result.total_raised_usd / 1000000).toFixed(1)}M`}</div>
              </div>
            )}

            {isDeal && result.deal_size_usd && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)', borderRadius: '8px', borderLeft: '3px solid #1a1f3a'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#1a1f3a', textTransform: 'uppercase', letterSpacing: '0.5px'}}>💵 Deal Size</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#1e3a6e', marginTop: '2px'}}>${(result.deal_size_usd / 1000000).toFixed(1)}M</div>
              </div>
            )}

            {isDeal && result.deal_date && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #e0e7ff 0%, #eef2ff 100%)', borderRadius: '8px', borderLeft: '3px solid #6366f1'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.5px'}}>📅 Deal Date</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#4f46e5', marginTop: '2px'}}>{result.deal_date}</div>
              </div>
            )}

            {isDeal && result.post_valuation && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%)', borderRadius: '8px', borderLeft: '3px solid #9333ea'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.5px'}}>📈 Valuation</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#7c3aed', marginTop: '2px'}}>${(result.post_valuation / 1000000).toFixed(1)}M</div>
              </div>
            )}

            {result.year_founded && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%)', borderRadius: '8px', borderLeft: '3px solid #64748b'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px'}}>🏢 Founded</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#334155', marginTop: '2px'}}>{formatYear(result.year_founded)}</div>
              </div>
            )}

            {result.employees && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #cffafe 0%, #ecfeff 100%)', borderRadius: '8px', borderLeft: '3px solid #06b6d4'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#0e7490', textTransform: 'uppercase', letterSpacing: '0.5px'}}>👥 Employees</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#0891b2', marginTop: '2px'}}>{result.employees}</div>
              </div>
            )}

            {(result.financing_status || result.company_financing_status) && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #fed7aa 0%, #fff7ed 100%)', borderRadius: '8px', borderLeft: '3px solid #f97316'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.5px'}}>📊 Status</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#ea580c', marginTop: '2px'}}>{result.financing_status || result.company_financing_status}</div>
              </div>
            )}

            {isInvestor && result.investor_type && (
              <div style={{padding: '10px 12px', background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)', borderRadius: '8px', borderLeft: '3px solid #1a1f3a'}}>
                <div style={{fontSize: '10px', fontWeight: '700', color: '#1a1f3a', textTransform: 'uppercase', letterSpacing: '0.5px'}}>🏦 Type</div>
                <div style={{fontSize: '13px', fontWeight: '600', color: '#1e3a6e', marginTop: '2px'}}>{result.investor_type}</div>
              </div>
            )}
          </div>

          {/* Verticals */}
          {verticals.length > 0 && (
            <div style={{marginBottom: '12px'}}>
              <div style={{fontSize: '11px', fontWeight: '700', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                Verticals
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {verticals.map((v, i) => (
                  <span key={i} style={{
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                    color: '#4338ca',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {v}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Keywords */}
          {keywords.length > 0 && (
            <div style={{marginBottom: '12px'}}>
              <div style={{fontSize: '11px', fontWeight: '700', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                Keywords
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                {keywords.slice(0, 15).map((k, i) => (
                  <span key={i} style={{
                    padding: '3px 8px',
                    background: '#f3f4f6',
                    color: '#4b5563',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: '1px solid #e5e7eb'
                  }}>
                    {k}
                  </span>
                ))}
                {keywords.length > 15 && (
                  <span style={{fontSize: '11px', color: '#9ca3af'}}>+{keywords.length - 15} more</span>
                )}
              </div>
            </div>
          )}

          {/* Investors (for deals) */}
          {isDeal && result.investors && result.investors.length > 0 && (
            <div style={{marginBottom: '12px'}}>
              <div style={{fontSize: '11px', fontWeight: '700', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                Investors {result.lead_investors && result.lead_investors.length > 0 && '(Lead highlighted)'}
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {result.investors.slice(0, 10).map((inv, i) => {
                  const isLead = result.lead_investors && result.lead_investors.includes(inv);
                  return (
                    <span key={i} style={{
                      padding: '4px 10px',
                      background: isLead ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : '#f9fafb',
                      color: isLead ? '#92400e' : '#4b5563',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: isLead ? '600' : '500',
                      border: isLead ? '1px solid #fbbf24' : '1px solid #e5e7eb'
                    }}>
                      {isLead && '⭐ '}{inv}
                    </span>
                  );
                })}
                {result.investors.length > 10 && (
                  <span style={{fontSize: '11px', color: '#9ca3af', alignSelf: 'center'}}>+{result.investors.length - 10} more</span>
                )}
              </div>
            </div>
          )}

          {/* Active Investors (for companies) */}
          {isCompany && result.active_investors && result.active_investors.length > 0 && (
            <div style={{marginBottom: '12px'}}>
              <div style={{fontSize: '11px', fontWeight: '700', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                Active Investors
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {result.active_investors.slice(0, 8).map((inv, i) => (
                  <span key={i} style={{
                    padding: '4px 10px',
                    background: '#f9fafb',
                    color: '#4b5563',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    border: '1px solid #e5e7eb'
                  }}>
                    {inv}
                  </span>
                ))}
                {result.active_investors.length > 8 && (
                  <span style={{fontSize: '11px', color: '#9ca3af', alignSelf: 'center'}}>+{result.active_investors.length - 8} more</span>
                )}
              </div>
            </div>
          )}

          {/* Relevance Reasons (if available) */}
          {result._relevance_reasons && result._relevance_reasons.length > 0 && (
            <div style={{padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0'}}>
              <div style={{fontSize: '10px', fontWeight: '700', color: '#166534', marginBottom: '4px', textTransform: 'uppercase'}}>Why this matched</div>
              <div style={{fontSize: '12px', color: '#15803d'}}>
                {result._relevance_reasons.slice(0, 5).map(r => r.split(':')[0].replace('_', ' ')).join(' • ')}
              </div>
            </div>
          )}

          {/* Agent Reasoning - AI-generated explanation (for ≤10 direct matches) */}
          {result._agent_reasoning && (
            <div style={{
              padding: '12px 14px',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(168, 85, 247, 0.25)',
              marginTop: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11px',
                fontWeight: '700',
                color: '#7c3aed',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                <span style={{fontSize: '14px'}}>🧠</span>
                Agent Reasoning
              </div>
              <div style={{fontSize: '13px', color: '#4c1d95', lineHeight: '1.5'}}>
                {result._agent_reasoning}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expand/Collapse Button */}
      <button
        onClick={onToggle}
        style={{
          marginTop: '10px',
          background: 'none',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          color: '#1a1f3a',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '600',
          padding: '6px 14px',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => { e.target.style.background = '#eff6ff'; e.target.style.borderColor = '#1a1f3a'; }}
        onMouseOut={(e) => { e.target.style.background = 'none'; e.target.style.borderColor = '#e5e7eb'; }}
      >
        {isExpanded ? '▲ Show less' : '▼ Show details'}
      </button>

      {/* Source Footer with Color Coding */}
      <div style={{fontSize: '11px', color: '#9ca3af', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center'}}>
        <span>📁 {result.entity_type}</span>
        {/* Source Badge - Color coded for quick visual identification */}
        <span style={{
          background: sourceStyle.bg,
          color: sourceStyle.text,
          padding: '2px 8px',
          borderRadius: '10px',
          fontWeight: '600',
          fontSize: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          {sourceStyle.icon} {sourceStyle.label}
        </span>
        {result._relevance_score && <span>⚡ Score: {result._relevance_score}</span>}
        {result.pitchbook_id && <span>ID: {result.pitchbook_id}</span>}
      </div>
    </div>
  );
}

// Multi-Select Dropdown Component (extracted to avoid hooks-in-hooks issue)
function MultiSelectDropdown({
  label,
  filterType,
  options = [],
  selectedValues = [],
  openDropdown,
  setOpenDropdown,
  backgroundLoading,
  dropdownRef,
  onClearSelection,
  onToggleCheckbox
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const isOpen = openDropdown === filterType;
  const selectedCount = selectedValues.length;
  const safeOptions = Array.isArray(options) ? options : [];
  const isDisabled = backgroundLoading;

  // Filter options based on search term
  const filteredOptions = safeOptions.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  return (
    <div className="filter-item filter-dropdown" ref={isOpen ? dropdownRef : null}>
      <label>{label} {isDisabled && <span style={{color: '#999', fontSize: '11px'}}>(loading...)</span>}</label>
      <button
        className="dropdown-trigger"
        onClick={() => !isDisabled && setOpenDropdown(isOpen ? null : filterType)}
        disabled={isDisabled}
        style={{ opacity: isDisabled ? 0.6 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
      >
        <span className="dropdown-text">
          {isDisabled ? 'Loading data...' : selectedCount > 0 ? `${selectedCount} selected` : 'Select options'}
        </span>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </button>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-header">
            <span className="dropdown-title">{label}</span>
            {selectedCount > 0 && (
              <button
                className="clear-selection-btn"
                onClick={() => onClearSelection(filterType)}
              >
                <FiX size={14} /> Clear
              </button>
            )}
          </div>
          <div className="dropdown-search">
            <FiSearch size={14} />
            <input
              type="text"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="dropdown-search-input"
              autoFocus
            />
          </div>
          <div className="dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <label key={option} className="dropdown-option">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    onChange={() => onToggleCheckbox(filterType, option)}
                  />
                  <span>{option}</span>
                </label>
              ))
            ) : (
              <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                {searchTerm ? `No results for "${searchTerm}"` : 'No options available'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Mobile Detail Popup - Full-screen modal for result details
 */
function MobileDetailPopup({ result, onClose }) {
  if (!result) return null;

  const entityType = result.entity_type || result.type || 'company';

  // Determine name based on entity type - same logic as MobileResultCard
  let name;
  if (entityType === 'deal') {
    const targetCompany = (typeof result.companies === 'string' ? result.companies.split(',')[0]?.trim() : null) || result.company_name || result.name;
    const dealType = result.deal_type || '';
    name = targetCompany ? `${targetCompany}${dealType ? ` - ${dealType}` : ''}` : (result.name || 'Unknown Deal');
  } else if (entityType === 'investor') {
    name = result.name || result.investor_name || 'Unknown Investor';
  } else {
    name = result.name || result.company_name || 'Unknown Company';
  }

  const description = result.description || result.deal_synopsis || '';

  // Generate link URL for "View Full Profile"
  const getLinkUrl = () => {
    if (entityType === 'company' && result.pitchbook_id) {
      return `/company/${result.pitchbook_id}`;
    } else if (entityType === 'deal' && (result.pitchbook_deal_id || result.pitchbook_id)) {
      return `/deal/${result.pitchbook_deal_id || result.pitchbook_id}`;
    } else if (entityType === 'investor' && (result.pitchbook_investor_id || result.pitchbook_id)) {
      return `/investor/${result.pitchbook_investor_id || result.pitchbook_id}`;
    }
    return null;
  };

  const linkUrl = getLinkUrl();

  // Format currency
  const formatMoney = (value) => {
    if (!value) return null;
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  // Entity icon based on type
  const getEntityIcon = () => {
    if (entityType === 'company') return <FiBriefcase size={20} />;
    if (entityType === 'deal') return <FiDollarSign size={20} />;
    if (entityType === 'investor') return <FiUsers size={20} />;
    return <FiGrid size={20} />;
  };

  return (
    <div className="mobile-detail-overlay" onClick={onClose}>
      <div className="mobile-detail-popup" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mobile-detail-header">
          <div className="mobile-detail-title-row">
            <span className="mobile-detail-icon">{getEntityIcon()}</span>
            <h2 className="mobile-detail-name">{name}</h2>
          </div>
          <button className="mobile-detail-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="mobile-detail-content">
          {/* Entity Type Badge */}
          <div className="mobile-detail-badge-row">
            <span className={`mobile-detail-badge ${entityType}`}>
              {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
            </span>
            {result.relevance_score && (
              <span className="mobile-detail-score">
                {Math.round(result.relevance_score * 100)}% match
              </span>
            )}
          </div>

          {/* Description */}
          {description && (
            <div className="mobile-detail-section">
              <h3>Description</h3>
              <p>{description}</p>
            </div>
          )}

          {/* Company-specific details */}
          {entityType === 'company' && (
            <>
              {(result.primary_industry || result.primary_industry_code) && (
                <div className="mobile-detail-section">
                  <h3>Industry</h3>
                  <p>{result.primary_industry || result.primary_industry_code}</p>
                </div>
              )}
              {(result.hq_location || result.hq_city) && (
                <div className="mobile-detail-section">
                  <h3>Location</h3>
                  <p>{result.hq_location || result.hq_city}</p>
                </div>
              )}
              {result.total_raised && (
                <div className="mobile-detail-section">
                  <h3>Total Raised</h3>
                  <p className="mobile-detail-value">{formatMoney(result.total_raised)}</p>
                </div>
              )}
              {result.employees && (
                <div className="mobile-detail-section">
                  <h3>Employees</h3>
                  <p>{result.employees.toLocaleString()}</p>
                </div>
              )}
              {result.year_founded && (
                <div className="mobile-detail-section">
                  <h3>Founded</h3>
                  <p>{result.year_founded}</p>
                </div>
              )}
              {result.website && (
                <div className="mobile-detail-section">
                  <h3>Website</h3>
                  <a href={result.website.startsWith('http') ? result.website : `https://${result.website}`} target="_blank" rel="noopener noreferrer" className="mobile-detail-link">
                    {result.website}
                  </a>
                </div>
              )}
            </>
          )}

          {/* Deal-specific details */}
          {entityType === 'deal' && (
            <>
              {result.companies && (
                <div className="mobile-detail-section">
                  <h3>Company</h3>
                  <p>{result.companies}</p>
                </div>
              )}
              {result.deal_type && (
                <div className="mobile-detail-section">
                  <h3>Deal Type</h3>
                  <p>{result.deal_type}{result.deal_type_2 ? ` / ${result.deal_type_2}` : ''}</p>
                </div>
              )}
              {result.deal_size_usd && (
                <div className="mobile-detail-section">
                  <h3>Deal Size</h3>
                  <p className="mobile-detail-value">{formatMoney(result.deal_size_usd)}</p>
                </div>
              )}
              {result.deal_date && (
                <div className="mobile-detail-section">
                  <h3>Date</h3>
                  <p>{new Date(result.deal_date).toLocaleDateString()}</p>
                </div>
              )}
              {(result.leadsole_investors || result.investors) && (
                <div className="mobile-detail-section">
                  <h3>Investors</h3>
                  <p>{result.leadsole_investors || (Array.isArray(result.investors) ? result.investors.join(', ') : result.investors)}</p>
                </div>
              )}
            </>
          )}

          {/* Investor-specific details */}
          {entityType === 'investor' && (
            <>
              {result.investor_type && (
                <div className="mobile-detail-section">
                  <h3>Type</h3>
                  <p>{result.investor_type}</p>
                </div>
              )}
              {(result.hq_location || result.hq_city) && (
                <div className="mobile-detail-section">
                  <h3>Location</h3>
                  <p>{result.hq_location || result.hq_city}</p>
                </div>
              )}
              {result.aum && (
                <div className="mobile-detail-section">
                  <h3>AUM</h3>
                  <p className="mobile-detail-value">{formatMoney(result.aum)}</p>
                </div>
              )}
              {result.investments_count && (
                <div className="mobile-detail-section">
                  <h3>Investments</h3>
                  <p>{result.investments_count} deals</p>
                </div>
              )}
            </>
          )}

          {/* Keywords/Verticals */}
          {(result.verticals || result.keywords) && (
            <div className="mobile-detail-section">
              <h3>Tags</h3>
              <div className="mobile-detail-tags">
                {(Array.isArray(result.verticals)
                  ? result.verticals
                  : Array.isArray(result.keywords)
                    ? result.keywords
                    : typeof result.verticals === 'string'
                      ? result.verticals.split(',')
                      : typeof result.keywords === 'string'
                        ? result.keywords.split(',')
                        : []
                ).slice(0, 6).map((tag, i) => (
                  <span key={i} className="mobile-detail-tag">{String(tag).trim()}</span>
                ))}
              </div>
            </div>
          )}

          {/* AI Reasoning - Full display in detail view */}
          {result._agent_reasoning && (
            <div className="mobile-detail-section mobile-detail-reasoning">
              <h3>
                <span style={{marginRight: '6px'}}>🧠</span>
                Agent Reasoning
              </h3>
              <p className="mobile-detail-reasoning-text">{result._agent_reasoning}</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="mobile-detail-footer">
          {linkUrl && (
            <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="mobile-detail-view-btn">
              <FiExternalLink size={18} />
              View Full Profile
            </a>
          )}
          <button className="mobile-detail-done-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile Result Card - Compact, touch-friendly result display
 */
function MobileResultCard({ result, tier, onSelect }) {
  const tierColors = {
    1: { bg: '#dcfce7', border: '#22c55e', text: '#166534', label: 'Direct' },
    2: { bg: '#dbeafe', border: '#1a1f3a', text: '#1a1f3a', label: 'Related' },
    3: { bg: '#f3e8ff', border: '#a855f7', text: '#7c3aed', label: 'Exploratory' }
  };
  const tierStyle = tierColors[tier] || tierColors[2];

  const entityType = result.entity_type || 'company';

  // Use Feather icon components instead of emojis - render as text placeholder
  // The actual icons will be rendered via CSS or inline SVG
  const entityLabels = { company: 'Company', deal: 'Deal', investor: 'Investor' };

  // Determine name based on entity type
  // For deals, use 'companies' field (target company name) + deal_type
  // For investors, use 'name' or 'investor_name'
  // For companies, use 'name' or 'company_name'
  let name;
  if (entityType === 'deal') {
    const targetCompany = (typeof result.companies === 'string' ? result.companies.split(',')[0]?.trim() : null) || result.company_name || result.name;
    const dealType = result.deal_type || '';
    name = targetCompany ? `${targetCompany}${dealType ? ` - ${dealType}` : ''}` : (result.name || 'Unknown Deal');
  } else if (entityType === 'investor') {
    name = result.name || result.investor_name || 'Unknown Investor';
  } else {
    name = result.name || result.company_name || 'Unknown Company';
  }

  const description = result.description || result.deal_synopsis || result.primary_industry || '';

  return (
    <div
      className="mobile-result-card"
      onClick={() => onSelect && onSelect(result)}
      style={{
        borderLeft: `4px solid ${tierStyle.border}`,
        background: tierStyle.bg
      }}
    >
      <div className="mobile-card-header">
        <span className="mobile-entity-icon">
          {entityType === 'company' && <FiBriefcase size={14} />}
          {entityType === 'deal' && <FiDollarSign size={14} />}
          {entityType === 'investor' && <FiUsers size={14} />}
        </span>
        <span className="mobile-card-name">{name}</span>
        <span
          className="mobile-tier-label"
          style={{ background: tierStyle.border, color: 'white' }}
        >
          {tierStyle.label}
        </span>
      </div>
      {description && (
        <p className="mobile-card-description">
          {description.length > 100 ? description.substring(0, 100) + '...' : description}
        </p>
      )}
      {/* AI Reasoning Preview for Mobile */}
      {result._agent_reasoning && (
        <div className="mobile-card-reasoning">
          <FiActivity size={12} className="mobile-reasoning-icon" />
          <span className="mobile-reasoning-text">
            {result._agent_reasoning.length > 80 ? result._agent_reasoning.substring(0, 80) + '...' : result._agent_reasoning}
          </span>
        </div>
      )}
      <div className="mobile-card-meta">
        <span className="mobile-card-type">{entityLabels[entityType] || entityType}</span>
        {result._agent_reasoning && (
          <span className="mobile-card-ai-badge">AI</span>
        )}
        {result.source && (
          <span className="mobile-card-source">
            {result.source.includes('sql') && <FiDatabase size={12} />}
            {result.source.includes('dense') && <FiActivity size={12} />}
            {result.source.includes('sparse') && <FiZap size={12} />}
          </span>
        )}
        <FiChevronRight size={16} className="mobile-card-arrow" />
      </div>
    </div>
  );
}

/**
 * ResearchAgentBubble - Morphing particle bubble toggle for Research Agent
 * Shows organic particle animation when active, static constellation when inactive
 * Displays tooltip on hover
 */
const BUBBLE_PARTICLE_COUNT = 16;

function getBubbleShapePositions(shape) {
  const center = 20;

  switch (shape) {
    case 'constellation':
      // Network/constellation pattern - research icon
      return [
        // Central cluster
        { x: 20, y: 18 }, { x: 18, y: 22 }, { x: 22, y: 22 },
        { x: 16, y: 18 }, { x: 24, y: 18 }, { x: 20, y: 24 },
        // Outer nodes
        { x: 12, y: 12 }, { x: 28, y: 12 }, { x: 12, y: 28 }, { x: 28, y: 28 },
        // Connecting nodes
        { x: 14, y: 20 }, { x: 26, y: 20 }, { x: 20, y: 10 }, { x: 20, y: 30 },
        { x: 16, y: 14 }, { x: 24, y: 26 }
      ];
    case 'mario':
      // Mario silhouette
      return [
        // Cap
        { x: 16, y: 12 }, { x: 20, y: 10 }, { x: 24, y: 12 },
        { x: 14, y: 14 }, { x: 26, y: 14 },
        // Face
        { x: 16, y: 18 }, { x: 20, y: 16 }, { x: 24, y: 18 },
        { x: 18, y: 22 }, { x: 22, y: 22 },
        // Body
        { x: 18, y: 28 }, { x: 22, y: 28 }, { x: 20, y: 26 },
        // Waving arm
        { x: 28, y: 16 }, { x: 30, y: 12 }, { x: 32, y: 10 }
      ];
    case 'samus':
      // Morph ball
      return Array.from({ length: BUBBLE_PARTICLE_COUNT }, (_, i) => {
        const angle = (i / BUBBLE_PARTICLE_COUNT) * Math.PI * 2;
        const r = i < 10 ? 10 : 5;
        return { x: center + Math.cos(angle) * r, y: center + Math.sin(angle) * r };
      });
    case 'ship':
      // Starship
      return [
        // Nose
        { x: 32, y: 20 }, { x: 28, y: 18 }, { x: 28, y: 22 },
        // Body
        { x: 24, y: 20 }, { x: 20, y: 20 }, { x: 16, y: 20 },
        // Wings
        { x: 22, y: 14 }, { x: 18, y: 10 }, { x: 22, y: 26 }, { x: 18, y: 30 },
        // Engine
        { x: 12, y: 18 }, { x: 12, y: 22 }, { x: 8, y: 20 },
        // Exhaust
        { x: 6, y: 20 }, { x: 4, y: 18 }, { x: 4, y: 22 }
      ];
    default:
      return Array.from({ length: BUBBLE_PARTICLE_COUNT }, (_, i) => ({
        x: center + (Math.random() - 0.5) * 24,
        y: center + (Math.random() - 0.5) * 24
      }));
  }
}

function ResearchAgentBubble({ isActive, onClick }) {
  const [morphPhase, setMorphPhase] = useState('constellation');
  const [particles, setParticles] = useState(() =>
    getBubbleShapePositions('constellation').map((pos, i) => ({
      ...pos,
      id: i,
      size: 1.0 + Math.random() * 0.4,
      opacity: 0.6 + Math.random() * 0.4
    }))
  );
  const [targetPositions, setTargetPositions] = useState(() => getBubbleShapePositions('constellation'));
  const animationRef = useRef(null);
  const phaseTimeoutRef = useRef(null);

  // Run morphing animation when active
  useEffect(() => {
    if (!isActive) {
      if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      setMorphPhase('constellation');
      setTargetPositions(getBubbleShapePositions('constellation'));
      return;
    }

    const phases = ['constellation', 'mario', 'samus', 'ship'];
    const durations = [2500, 2500, 2500, 2500];
    let currentIndex = 0;

    const cyclePhase = () => {
      currentIndex = (currentIndex + 1) % phases.length;
      const nextPhase = phases[currentIndex];
      setMorphPhase(nextPhase);
      setTargetPositions(getBubbleShapePositions(nextPhase));
      phaseTimeoutRef.current = setTimeout(cyclePhase, durations[currentIndex]);
    };

    phaseTimeoutRef.current = setTimeout(cyclePhase, durations[0]);

    return () => {
      if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
    };
  }, [isActive]);

  // Continuous particle animation when active
  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setParticles(prev => prev.map((particle, i) => {
        const target = targetPositions[i] || { x: 20, y: 20 };
        const distX = target.x - particle.x;
        const distY = target.y - particle.y;
        const distance = Math.sqrt(distX * distX + distY * distY);

        const easing = distance > 3 ? 4.5 : 2.0;
        const dx = distX * easing * deltaTime;
        const dy = distY * easing * deltaTime;

        const wobbleAmount = distance < 2 ? 0.25 : 0.08;
        const wobbleX = Math.sin(currentTime * 0.002 + i * 0.8) * wobbleAmount;
        const wobbleY = Math.cos(currentTime * 0.0018 + i * 1.3) * wobbleAmount;

        const sizePulse = 1 + Math.sin(currentTime * 0.003 + i * 0.5) * 0.15;

        return {
          ...particle,
          x: particle.x + dx + wobbleX,
          y: particle.y + dy + wobbleY,
          size: (0.9 + (i % 3) * 0.15) * sizePulse,
          opacity: 0.7 + Math.sin(currentTime * 0.002 + i * 0.3) * 0.25
        };
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isActive, targetPositions]);

  return (
    <button
      className={`research-agent-bubble ${isActive ? 'active' : ''}`}
      onClick={onClick}
      title="Research Agent"
    >
      <div className="bubble-avatar-container">
        {isActive ? (
          <svg viewBox="0 0 40 40" className={`bubble-avatar morphing morph-${morphPhase}`}>
            <defs>
              <linearGradient id="bubbleGradActive" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1a2744" />
                <stop offset="100%" stopColor="#243656" />
              </linearGradient>
              <filter id="bubbleGlow">
                <feGaussianBlur stdDeviation="0.6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <radialGradient id="bubbleCenterGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="white" stopOpacity="0.2" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="20" cy="20" r="18" fill="url(#bubbleGradActive)" />
            <circle cx="20" cy="20" r="12" fill="url(#bubbleCenterGlow)" className="center-glow" />
            <g className="particle-field">
              {particles.map((p) => (
                <circle
                  key={p.id}
                  cx={p.x}
                  cy={p.y}
                  r={p.size}
                  fill="white"
                  opacity={p.opacity}
                  filter="url(#bubbleGlow)"
                />
              ))}
            </g>
            <g className="particle-connections" opacity="0.15">
              {particles.slice(0, 8).map((p, i) => {
                const next = particles[(i + 1) % 8];
                const dist = Math.hypot(next.x - p.x, next.y - p.y);
                if (dist < 12) {
                  return (
                    <line
                      key={`conn-${i}`}
                      x1={p.x}
                      y1={p.y}
                      x2={next.x}
                      y2={next.y}
                      stroke="white"
                      strokeWidth="0.3"
                    />
                  );
                }
                return null;
              })}
            </g>
          </svg>
        ) : (
          <svg viewBox="0 0 40 40" className="bubble-avatar static">
            <defs>
              <linearGradient id="bubbleGradStatic" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f1f5f9" />
                <stop offset="100%" stopColor="#e2e8f0" />
              </linearGradient>
            </defs>
            <circle cx="20" cy="20" r="18" fill="url(#bubbleGradStatic)" stroke="#cbd5e1" strokeWidth="1.5" />
            <g className="constellation-network">
              <line x1="14" y1="14" x2="20" y2="18" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />
              <line x1="20" y1="18" x2="26" y2="14" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />
              <line x1="20" y1="18" x2="15" y2="24" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />
              <line x1="20" y1="18" x2="25" y2="24" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />
              <circle cx="20" cy="18" r="2.5" fill="#475569" fillOpacity="0.9" />
              <circle cx="14" cy="14" r="1.8" fill="#475569" fillOpacity="0.7" />
              <circle cx="26" cy="14" r="1.8" fill="#475569" fillOpacity="0.7" />
              <circle cx="15" cy="24" r="1.5" fill="#475569" fillOpacity="0.6" />
              <circle cx="25" cy="24" r="1.5" fill="#475569" fillOpacity="0.6" />
            </g>
          </svg>
        )}
      </div>
      <span className="bubble-tooltip">Research Agent</span>
    </button>
  );
}

function LandingPage({ onOpenChat, onUpdateSearchContext }) {
  const { isDarkMode } = useTheme();
  const [apiHealth, setApiHealth] = useState(null);
  const [activeTab, setActiveTab] = useState('deals'); // Focus on deals as priority
  const [searchQuery, setSearchQuery] = useState('');
  // Debounce search query by 300ms to prevent excessive filtering on every keystroke
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false); // Separate loading for tab switches

  // Unified Search System state variables
  const [searchMode, setSearchMode] = useState('strict'); // 'strict', 'semantic', 'hybrid'
  const [searchDebugInfo, setSearchDebugInfo] = useState(null); // Debug info from backend
  const [searchAlpha, setSearchAlpha] = useState(0.5);    // Balance for hybrid mode
  const [showAdvanced, setShowAdvanced] = useState(false); // Show/hide advanced options
  const [showLessLikelyPrompt, setShowLessLikelyPrompt] = useState(false); // For strict mode no results
  const [searchLoading, setSearchLoading] = useState(false); // Loading state for search operations
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false); // Start collapsed for clean look
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false); // Hide search modes by default
  const [displayLimit, setDisplayLimit] = useState(2500); // 2.5K initial (optimized for AI)
  const [backgroundLoading, setBackgroundLoading] = useState(false); // Track background data loading
  const [loadingProgress, setLoadingProgress] = useState({ deals: 0, companies: 0, investors: 0 }); // Progress tracking
  const [showFiltersReady, setShowFiltersReady] = useState(false); // Show "Filters Ready!" message
  const [loadingBadgeExpanded, setLoadingBadgeExpanded] = useState(false); // Loading notification badge state
  const loadingCompleteRef = useRef({ deals: false, companies: false, investors: false }); // Track which entities finished loading
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Device detection - using DeviceContext for consistent detection
  // Note: isMobile now includes tablets, which use the mobile UI
  const { isMobile } = useDevice();

  // NLP Processing hook - starts background processing when data loads
  const { startBackgroundProcessing, cleanup: cleanupNLP } = useNLPProcessing();

  // Data states
  const [deals, setDeals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [allDeals, setAllDeals] = useState([]); // Keep unfiltered data
  const [allCompanies, setAllCompanies] = useState([]);
  const [allInvestors, setAllInvestors] = useState([]);

  // Deal filters (priority) - Using arrays for multi-select
  const [dealFilters, setDealFilters] = useState({
    dealType: [], // Array for multiple Deal Type selections
    dealType2: [], // Array for multiple Deal Type 2 selections
    financingStatus: [], // Array for multiple selections
    investors: [], // Array for investor name filtering
    minSize: '',
    maxSize: '',
    dateFrom: '',
    dateTo: ''
  });

  // Pagination state (for display, not data fetching)
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  // Auto-scroll state
  const [isNearBottom, setIsNearBottom] = useState(false);

  // Expanded description state
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());

  // Company filters - now with multi-select arrays
  const [companyFilters, setCompanyFilters] = useState({
    industry: [],  // Changed to array for multi-select
    location: [],  // Changed to array for multi-select
    minRevenue: '',
    maxRevenue: ''
  });

  // Investor filters - now with multi-select array
  const [investorFilters, setInvestorFilters] = useState({
    investorType: [],  // Changed to array for multi-select
    minAUM: '',
    maxAUM: ''
  });

  // Unique values for dropdowns
  const [uniqueValues, setUniqueValues] = useState({
    dealTypes: [],
    dealTypes2: [],
    dealStatuses: [],
    financingStatuses: [],
    dealInvestors: [], // Unique investor names from deals
    industries: [],
    locations: [],
    investorTypes: []
  });


  // CSV Export function
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Get all unique keys from the data
    const keys = Object.keys(data[0]);

    // Create CSV header
    const csvHeader = keys.join(',');

    // Create CSV rows
    const csvRows = data.map(row => {
      return keys.map(key => {
        const value = row[key];
        // Handle values that contain commas, quotes, or newlines
        if (value === null || value === undefined) return '';
        const stringValue = String(value).replace(/"/g, '""'); // Escape quotes
        return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
          ? `"${stringValue}"`
          : stringValue;
      }).join(',');
    });

    // Combine header and rows
    const csvContent = [csvHeader, ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // AI Search state
  const [showAISearch, setShowAISearch] = useState(false);
  const [aiSearchQuery, setAISearchQuery] = useState('');
  const [aiSearchLoading, setAISearchLoading] = useState(false);
  const [aiSearchResults, setAISearchResults] = useState(null);
  const [aiSearchError, setAISearchError] = useState(null);
  const [aiSearchProgress, setAISearchProgress] = useState([]);
  const [expandedResults, setExpandedResults] = useState({});
  const [showThinking, setShowThinking] = useState(true); // Toggle for logs/thinking display
  const [expandedSummary, setExpandedSummary] = useState(false); // Toggle for full summary
  const [showVDBDense, setShowVDBDense] = useState(false); // VDB Dense section collapsed by default
  const [showVDBSparse, setShowVDBSparse] = useState(false); // VDB Sparse section collapsed by default
  const [agentResultsView, setAgentResultsView] = useState('cards'); // 'cards' or 'table' view for results
  const [agentResultsLimit, setAgentResultsLimit] = useState(20); // Limit cards to 20, expandable
  const [agentPanelExpanded, setAgentPanelExpanded] = useState(false); // Expanded panel mode (85vw)
  const [showAgentAnalytics, setShowAgentAnalytics] = useState(false); // Show result analytics charts
  const [agentMinimized, setAgentMinimized] = useState(false); // Minimized mode - shows floating badge on right

  // Database stats (hero section)
  const [heroStats, setHeroStats] = useState(null);

  // Hero Search state (search-first experience)
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [heroSearchMode, setHeroSearchMode] = useState('hybrid'); // 'semantic' | 'strict' | 'hybrid'
  const [heroSearchAlpha, setHeroSearchAlpha] = useState(0.5);
  const [heroEntityFilter, setHeroEntityFilter] = useState('all'); // 'all' | 'companies' | 'deals' | 'investors'
  const [heroSearchResults, setHeroSearchResults] = useState(null);
  const [heroSearchLoading, setHeroSearchLoading] = useState(false);
  const [heroSearchError, setHeroSearchError] = useState(null);
  const [heroSearchDebugInfo, setHeroSearchDebugInfo] = useState(null); // Debug info from Pinecone
  const [heroResultsViewMode, setHeroResultsViewMode] = useState('table'); // 'cards' | 'table' - table default for elegance
  const [heroTableSort, setHeroTableSort] = useState({ field: null, direction: 'asc' }); // Sorting for table view
  const [showTabulateModal, setShowTabulateModal] = useState(false); // Tabulate modal for data exploration
  const [heroDebugCollapsed, setHeroDebugCollapsed] = useState(true); // Debug panel collapsed by default
  const [useResearchAgent, setUseResearchAgent] = useState(false); // Toggle for AI agent vs fast search

  // Common Searches state (preset filters for Deal Round, Tech Core, and Platforms)
  const [commonSearchesExpanded, setCommonSearchesExpanded] = useState(true);
  const [selectedDealRounds, setSelectedDealRounds] = useState([]); // ['Seed', 'Series A', etc.]
  const [selectedTechCore, setSelectedTechCore] = useState([]); // ['AI/ML', 'XR/MR/AR/VR', 'Blockchain/Web3']
  const [selectedPlatforms, setSelectedPlatforms] = useState([]); // ['Mobile', 'PC', 'Console'] - Game Studios only

  // Agent Console state (retro terminal showing search progress)
  const [agentConsoleLogs, setAgentConsoleLogs] = useState([]);
  const [agentConsoleVisible, setAgentConsoleVisible] = useState(false);
  const agentConsoleRef = useRef(null);

  // Lazy loading state for Data Explorer
  const [dataLoadTriggered, setDataLoadTriggered] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0); // 0-100 scroll progress
  const dataExplorerRef = useRef(null);
  const heroSectionRef = useRef(null);

  // Dropdown states for multi-select filters
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const dropdownRef = useRef(null);

  // Modal for showing all filter values
  const [showFilterModal, setShowFilterModal] = useState(false);

  useEffect(() => {
    checkHealth();
    // Don't fetch data on mount - lazy load when user scrolls to Data Explorer
    // fetchInitialData() is now triggered by Intersection Observer

    // Fetch database stats for hero section
    api.getStats().then(data => {
      if (data) setHeroStats(data);
    }).catch(err => console.error('Stats fetch failed:', err));
  }, []);

  // Add reload warning to prevent accidental data loss
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (allCompanies.length > 100 || allDeals.length > 100 || allInvestors.length > 50) {
        e.preventDefault();
        e.returnValue = 'You have loaded data that will be lost if you reload. Are you sure?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [allCompanies.length, allDeals.length, allInvestors.length]);

  // Scroll detection for auto-loading (INNOVATION!)
  useEffect(() => {
    const handleScroll = () => {
      if (!tableContainerRef.current) return;

      const container = tableContainerRef.current;
      const scrollPosition = window.scrollY + window.innerHeight;
      const containerBottom = container.offsetTop + container.offsetHeight;

      // When user scrolls to 80% of the table
      const threshold = containerBottom - (container.offsetHeight * 0.2);

      if (scrollPosition >= threshold && !loadingMore && hasMore && activeTab === 'deals') {
        setIsNearBottom(true);
        // Auto-load more deals when near bottom
        loadMoreDeals();
      } else if (scrollPosition < threshold) {
        setIsNearBottom(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, activeTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup NLP processing on unmount
  useEffect(() => {
    return () => {
      cleanupNLP();
    };
  }, [cleanupNLP]);

  // Scroll progress tracking for hero section - requires intentional scroll
  useEffect(() => {
    if (dataLoadTriggered) return;

    const handleScroll = () => {
      // Try both the container ref and window scroll
      const scrollContainer = scrollContainerRef.current;
      const heroSection = heroSectionRef.current;

      if (!heroSection) return;

      // Get scroll position - check container first, then window
      let scrollTop = 0;
      if (scrollContainer && scrollContainer.scrollTop > 0) {
        scrollTop = scrollContainer.scrollTop;
      } else {
        scrollTop = window.scrollY || document.documentElement.scrollTop;
      }

      const heroHeight = heroSection.offsetHeight;

      // Calculate progress (0-100) - requires scrolling 120% of hero section (very intentional)
      // This means user must scroll past the hero section + 20% buffer to trigger data load
      const scrollThreshold = heroHeight * 1.2;
      const progress = Math.min(100, (scrollTop / scrollThreshold) * 100);

      setScrollProgress(progress);

      // Only trigger data load when scroll reaches 100% (which is now 120% of hero height)
      if (progress >= 100 && !dataLoadTriggered) {
        console.log('📊 Scroll threshold reached - triggering data load');
        setDataLoadTriggered(true);
        fetchInitialData();
      }
    };

    // Listen to both container scroll and window scroll
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [dataLoadTriggered]);

  const checkHealth = async () => {
    try {
      const health = await api.checkHealth();
      setApiHealth(health);
    } catch (err) {
      console.error('Health check failed:', err);
      setApiHealth({ status: 'unhealthy', database_connected: false });
    }
  };

  // AGGRESSIVE LOADING STRATEGY - Use that VPC power!
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'https://transcend-hyrule-827956171027.us-central1.run.app';

      // Phase 1: Balanced initial load - optimized for AI processing
      // 2.5K per entity = 7.5K total (faster load, still comprehensive)
      const [dealsResponse, companiesResponse, investorsResponse] = await Promise.all([
        authFetch(`${API_BASE}/api/v1/deals/full?limit=2500&offset=0`),
        authFetch(`${API_BASE}/api/v1/companies/full?limit=2500&offset=0`),
        authFetch(`${API_BASE}/api/v1/investors/full?limit=2500&offset=0`)
      ]);

      const dealsData = await dealsResponse.json();
      const companiesData = await companiesResponse.json();
      const investorsData = await investorsResponse.json();

      console.log(`✅ Phase 1 LOAD: ${dealsData.length} deals, ${companiesData.length} companies, ${investorsData.length} investors (${dealsData.length + companiesData.length + investorsData.length} total)`);

      setAllDeals(dealsData);
      setDeals(dealsData);
      setAllCompanies(companiesData);
      setCompanies(companiesData);
      setAllInvestors(investorsData);
      setInvestors(investorsData);
      setDisplayLimit(2500); // 2.5K loaded immediately
      setLoadingProgress({ deals: dealsData.length, companies: companiesData.length, investors: investorsData.length });

      // DON'T extract unique values yet - wait for background loading to complete
      // extractUniqueValues(dealsData, companiesData, investorsData);

      setLoading(false); // UI is ready!
      setBackgroundLoading(true); // Start background loading state

      // Phase 2: AGGRESSIVE background loading - load EVERYTHING!
      console.log('🚀 Phase 2: Starting aggressive background load for ALL entity types...');
      loadAllDealsInBackground(dealsData.length);
      loadAllCompaniesInBackground(companiesData.length);
      loadAllInvestorsInBackground(investorsData.length);

    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
    }
  };

  // Background loading - with duplicate prevention
  const loadAllDealsInBackground = async (currentCount) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'https://transcend-hyrule-827956171027.us-central1.run.app';
    const CHUNK_SIZE = 1000;
    const MAX_DEALS = 12000; // Safety limit (actual is ~9,937)
    let offset = currentCount;
    let totalLoaded = currentCount;
    let keepLoading = true;

    console.log(`🚀 DEALS: Loading remaining from ${currentCount}`);

    // Check if we might already have all data (safety check)
    if (currentCount >= MAX_DEALS) {
      console.log(`✅ Deals already complete or at max: ${currentCount}`);
      loadingCompleteRef.current.deals = true;

      // Trigger check with latest state
      setAllDeals(currentDeals => {
        setAllCompanies(currentCompanies => {
          setAllInvestors(currentInvestors => {
            checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
            return currentInvestors;
          });
          return currentCompanies;
        });
        return currentDeals;
      });
      return;
    }

    while (keepLoading && totalLoaded < MAX_DEALS) {
      try {
        console.log(`📥 Chunk ${Math.floor(offset/CHUNK_SIZE) + 1}: Loading ${CHUNK_SIZE} deals from offset ${offset}`);

        const response = await authFetch(`${API_BASE}/api/v1/deals/full?limit=${CHUNK_SIZE}&offset=${offset}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const newDeals = await response.json();

        if (newDeals.length > 0) {
          totalLoaded += newDeals.length;

          // Update state with new deals
          setAllDeals(prev => {
            const updated = [...prev, ...newDeals];
            console.log(`✅ Total in memory: ${updated.length.toLocaleString()} deals (${Math.round(updated.length/1000)}K)`);
            setLoadingProgress(prevProgress => ({ ...prevProgress, deals: updated.length }));
            return updated;
          });

          setDeals(prev => [...prev, ...newDeals]);

          offset += CHUNK_SIZE;

          // If we got less than chunk size, we're done
          if (newDeals.length < CHUNK_SIZE) {
            keepLoading = false;
            setHasMore(false);
            console.log(`🎉 DEALS COMPLETE! Loaded ${totalLoaded.toLocaleString()} total deals`);
            loadingCompleteRef.current.deals = true;

            // Use setAllDeals to trigger check with latest state
            setAllDeals(currentDeals => {
              setAllCompanies(currentCompanies => {
                setAllInvestors(currentInvestors => {
                  checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
                  return currentInvestors;
                });
                return currentCompanies;
              });
              return currentDeals;
            });
          }

          // Reduce delay for faster loading (50ms instead of 100ms)
          await new Promise(resolve => setTimeout(resolve, 50));
        } else {
          keepLoading = false;
          setHasMore(false);
          console.log(`🎉 DEALS COMPLETE! Loaded ${totalLoaded.toLocaleString()} total deals`);
          loadingCompleteRef.current.deals = true;

          // Use setAllDeals to trigger check with latest state
          setAllDeals(currentDeals => {
            setAllCompanies(currentCompanies => {
              setAllInvestors(currentInvestors => {
                checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
                return currentInvestors;
              });
              return currentCompanies;
            });
            return currentDeals;
          });
        }
      } catch (err) {
        console.error('❌ Background loading error:', err);
        console.error('Failed at offset:', offset, 'Total loaded so far:', totalLoaded);
        keepLoading = false;
        setHasMore(false);
      }
    }
  };

  // Background loading for COMPANIES - with proper safeguards
  const loadAllCompaniesInBackground = async (currentCount) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'https://transcend-hyrule-827956171027.us-central1.run.app';
    const CHUNK_SIZE = 1000;
    const ACTUAL_TOTAL = 6296; // Exact count from database - HARD STOP
    let offset = currentCount;
    let keepLoading = true;
    let loadedSoFar = currentCount;

    console.log(`🚀 COMPANIES: Loading from ${currentCount} to exactly ${ACTUAL_TOTAL}`);

    // CRITICAL: Stop if we already have all data
    if (currentCount >= ACTUAL_TOTAL) {
      console.log(`✅ Companies already complete: ${currentCount}`);
      loadingCompleteRef.current.companies = true;

      // Trigger check with latest state
      setAllDeals(currentDeals => {
        setAllCompanies(currentCompanies => {
          setAllInvestors(currentInvestors => {
            checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
            return currentInvestors;
          });
          return currentCompanies;
        });
        return currentDeals;
      });
      return;
    }

    while (keepLoading && offset < ACTUAL_TOTAL) {
      try {
        // Calculate how many to load (don't exceed ACTUAL_TOTAL)
        const limit = Math.min(CHUNK_SIZE, ACTUAL_TOTAL - offset);

        const response = await authFetch(`${API_BASE}/api/v1/companies/full?limit=${limit}&offset=${offset}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const newCompanies = await response.json();

        if (newCompanies.length > 0) {
          // Add new companies to state (simple append - API returns unique by offset)
          setAllCompanies(prev => {
            const spaceLeft = ACTUAL_TOTAL - prev.length;
            const toAdd = newCompanies.slice(0, spaceLeft);
            const updated = [...prev, ...toAdd];
            console.log(`✅ Adding ${toAdd.length} companies (have ${updated.length}, max ${ACTUAL_TOTAL})`);
            setLoadingProgress(prevProgress => ({ ...prevProgress, companies: updated.length }));
            return updated;
          });

          setCompanies(prev => {
            const spaceLeft = ACTUAL_TOTAL - prev.length;
            const toAdd = newCompanies.slice(0, spaceLeft);
            return [...prev, ...toAdd];
          });

          loadedSoFar += newCompanies.length;
          offset += newCompanies.length;

          // STOP CONDITIONS
          if (loadedSoFar >= ACTUAL_TOTAL || newCompanies.length < limit || offset >= ACTUAL_TOTAL) {
            keepLoading = false;
            console.log(`🎉 COMPANIES COMPLETE! Loaded exactly ${Math.min(loadedSoFar, ACTUAL_TOTAL)} companies`);
            loadingCompleteRef.current.companies = true;

            // Trigger check with latest state
            setAllDeals(currentDeals => {
              setAllCompanies(currentCompanies => {
                setAllInvestors(currentInvestors => {
                  checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
                  return currentInvestors;
                });
                return currentCompanies;
              });
              return currentDeals;
            });
          }

          await new Promise(resolve => setTimeout(resolve, 50));
        } else {
          keepLoading = false;
          console.log(`🎉 COMPANIES COMPLETE! No more data at offset ${offset}`);
          loadingCompleteRef.current.companies = true;

          // Trigger check with latest state
          setAllDeals(currentDeals => {
            setAllCompanies(currentCompanies => {
              setAllInvestors(currentInvestors => {
                checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
                return currentInvestors;
              });
              return currentCompanies;
            });
            return currentDeals;
          });
        }
      } catch (err) {
        console.error('❌ Companies loading error:', err);
        keepLoading = false;
      }
    }
  };

  // Background loading for INVESTORS - with proper safeguards
  const loadAllInvestorsInBackground = async (currentCount) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'https://transcend-hyrule-827956171027.us-central1.run.app';
    const CHUNK_SIZE = 250;
    const ACTUAL_TOTAL = 250; // Exact count from database - HARD STOP
    let offset = currentCount;
    let keepLoading = true;
    let loadedSoFar = currentCount;

    console.log(`🚀 INVESTORS: Loading from ${currentCount} to exactly ${ACTUAL_TOTAL}`);

    // CRITICAL: Stop if we already have all data
    if (currentCount >= ACTUAL_TOTAL) {
      console.log(`✅ Investors already complete: ${currentCount}`);
      loadingCompleteRef.current.investors = true;

      // Trigger check with latest state
      setAllDeals(currentDeals => {
        setAllCompanies(currentCompanies => {
          setAllInvestors(currentInvestors => {
            checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
            return currentInvestors;
          });
          return currentCompanies;
        });
        return currentDeals;
      });
      return;
    }

    while (keepLoading && offset < ACTUAL_TOTAL) {
      try {
        // Calculate how many to load (don't exceed ACTUAL_TOTAL)
        const limit = Math.min(CHUNK_SIZE, ACTUAL_TOTAL - offset);

        const response = await authFetch(`${API_BASE}/api/v1/investors/full?limit=${limit}&offset=${offset}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const newInvestors = await response.json();

        if (newInvestors.length > 0) {
          // CRITICAL: Check we're not exceeding total before adding
          setAllInvestors(prev => {
            if (prev.length >= ACTUAL_TOTAL) {
              console.log(`⚠️ Already have ${prev.length} investors, stopping`);
              return prev;
            }
            const spaceLeft = ACTUAL_TOTAL - prev.length;
            const toAdd = newInvestors.slice(0, spaceLeft);
            const updated = [...prev, ...toAdd];
            console.log(`✅ Adding ${toAdd.length} investors (have ${prev.length}, max ${ACTUAL_TOTAL})`);
            setLoadingProgress(prevProgress => ({ ...prevProgress, investors: updated.length }));
            return updated;
          });

          setInvestors(prev => {
            const spaceLeft = ACTUAL_TOTAL - prev.length;
            const toAdd = newInvestors.slice(0, spaceLeft);
            return [...prev, ...toAdd];
          });

          loadedSoFar += newInvestors.length;
          offset += newInvestors.length;

          // STOP CONDITIONS
          if (loadedSoFar >= ACTUAL_TOTAL || newInvestors.length < limit || offset >= ACTUAL_TOTAL) {
            keepLoading = false;
            console.log(`🎉 INVESTORS COMPLETE! Loaded exactly ${Math.min(loadedSoFar, ACTUAL_TOTAL)} investors`);
            loadingCompleteRef.current.investors = true;

            // Trigger check with latest state
            setAllDeals(currentDeals => {
              setAllCompanies(currentCompanies => {
                setAllInvestors(currentInvestors => {
                  checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
                  return currentInvestors;
                });
                return currentCompanies;
              });
              return currentDeals;
            });
          }

          await new Promise(resolve => setTimeout(resolve, 50));
        } else {
          keepLoading = false;
          console.log(`🎉 INVESTORS COMPLETE! No more data at offset ${offset}`);
          loadingCompleteRef.current.investors = true;

          // Trigger check with latest state
          setAllDeals(currentDeals => {
            setAllCompanies(currentCompanies => {
              setAllInvestors(currentInvestors => {
                checkLoadingComplete(currentDeals, currentCompanies, currentInvestors);
                return currentInvestors;
              });
              return currentCompanies;
            });
            return currentDeals;
          });
        }
      } catch (err) {
        console.error('❌ Investors loading error:', err);
        keepLoading = false;
      }
    }
  };

  // Load more deals - BUTTON CLICK approach (enterprise style)
  const loadMoreDeals = async () => {
    if (loadingMore) return;

    setLoadingMore(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'https://transcend-hyrule-827956171027.us-central1.run.app';
      const offset = allDeals.length;
      const batchSize = 50;

      const response = await authFetch(`${API_BASE}/api/v1/deals/full?limit=${batchSize}&offset=${offset}`);
      const newDeals = await response.json();

      console.log('📥 Loaded more:', newDeals.length, 'deals (total:', offset + newDeals.length, ')');

      if (newDeals.length > 0) {
        setAllDeals(prev => [...prev, ...newDeals]);
        setDeals(prev => [...prev, ...newDeals]);
        setDisplayLimit(prev => prev + batchSize);
        setHasMore(newDeals.length === batchSize); // If we got full batch, assume more available

        // Don't extract unique values here - only after all background loading complete
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more deals:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // Prepare NLP data for all tabs (for background processing)
  const allNLPData = useMemo(() => {
    return {
      deals: allDeals.length > 0 ? calculateDealsNLP(allDeals) : null,
      companies: allCompanies.length > 0 ? calculateCompaniesNLP(allCompanies) : null,
      investors: allInvestors.length > 0 ? calculateInvestorsNLP(allInvestors) : null
    };
  }, [allDeals, allCompanies, allInvestors]);

  // Check if all background loading is complete, then extract unique values
  const checkLoadingComplete = (currentDeals, currentCompanies, currentInvestors) => {
    const { deals, companies, investors } = loadingCompleteRef.current;

    console.log('🔍 Checking loading status:', {
      deals: deals ? '✅ Complete' : '⏳ Loading',
      companies: companies ? '✅ Complete' : '⏳ Loading',
      investors: investors ? '✅ Complete' : '⏳ Loading'
    });

    if (deals && companies && investors) {
      console.log('🎉 ALL DATA LOADED! Extracting unique values for filters...');
      console.log(`📊 Final counts: ${currentDeals.length} deals, ${currentCompanies.length} companies, ${currentInvestors.length} investors`);

      // Use the passed-in current arrays to ensure we have latest data
      extractUniqueValues(currentDeals, currentCompanies, currentInvestors);
      setBackgroundLoading(false);

      // Show success message to user
      setShowFiltersReady(true);
      setTimeout(() => setShowFiltersReady(false), 5000); // Hide after 5 seconds

      console.log('✅ FILTERS READY! You can now use all filter dropdowns.');
      console.log('🔓 Filter dropdowns have been unlocked.');

      // ⚠️ AUTOMATIC NLP PROCESSING DISABLED TO REDUCE LATENCY & COSTS
      // Users can manually trigger NLP processing in the Data Explorer > Analytics tab
      console.log('ℹ️ Automatic NLP processing disabled. Use Data Explorer > Analytics tab to run analysis on-demand.');
    } else {
      console.log('⏳ Still loading... waiting for all entities to complete.');
    }
  };

  const extractUniqueValues = (deals, companies, investors) => {
    // Ensure we have arrays to work with
    const safeDeals = Array.isArray(deals) ? deals : [];
    const safeCompanies = Array.isArray(companies) ? companies : [];
    const safeInvestors = Array.isArray(investors) ? investors : [];

    const dealTypes = [...new Set(safeDeals.map(d => d.deal_type).filter(Boolean))];
    const dealTypes2 = [...new Set(safeDeals.map(d => d.deal_type_2).filter(Boolean))];
    const financingStatuses = [...new Set(safeDeals.map(d => d.financing_status).filter(Boolean))];

    // Extract unique investors from deals (both investors and lead_sole_investors arrays)
    const dealInvestorsSet = new Set();
    safeDeals.forEach(d => {
      if (Array.isArray(d.investors)) {
        d.investors.forEach(inv => inv && dealInvestorsSet.add(inv.trim()));
      }
      if (Array.isArray(d.lead_sole_investors)) {
        d.lead_sole_investors.forEach(inv => inv && dealInvestorsSet.add(inv.trim()));
      }
      if (Array.isArray(d.lead_investors)) {
        d.lead_investors.forEach(inv => inv && dealInvestorsSet.add(inv.trim()));
      }
    });
    const dealInvestors = [...dealInvestorsSet].filter(Boolean);

    // Check multiple possible fields for industries and locations
    const industries = [...new Set(safeCompanies.map(c =>
      c.primary_industry_code || c.primary_industry || c.industry
    ).filter(Boolean))];

    const locations = [...new Set(safeCompanies.map(c =>
      c.hq_city || c.hq_location || c.city || c.location
    ).filter(Boolean))];

    const investorTypes = [...new Set(safeInvestors.map(i =>
      i.investor_type || i.type
    ).filter(Boolean))];

    const uniqueVals = {
      dealTypes: dealTypes.sort(),
      dealTypes2: dealTypes2.sort(),
      financingStatuses: financingStatuses.sort(),
      dealInvestors: dealInvestors.sort(),
      industries: industries.sort(),
      locations: locations.sort(),
      investorTypes: investorTypes.sort()
    };

    console.log('📊 Unique values extracted:', {
      dealTypes: uniqueVals.dealTypes.length,
      dealTypes2: uniqueVals.dealTypes2.length,
      financingStatuses: uniqueVals.financingStatuses.length,
      dealInvestors: uniqueVals.dealInvestors.length,
      industries: uniqueVals.industries.length,
      locations: uniqueVals.locations.length,
      investorTypes: uniqueVals.investorTypes.length
    });

    // Debug: Log first few values to verify mapping
    if (safeCompanies.length > 0) {
      console.log('🔍 Sample company fields:', {
        first_company: safeCompanies[0],
        industries_sample: uniqueVals.industries.slice(0, 5),
        locations_sample: uniqueVals.locations.slice(0, 5)
      });
    }

    setUniqueValues(uniqueVals);
  };

  // Toggle description expansion
  const toggleDescription = (dealId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealId)) {
        newSet.delete(dealId);
      } else {
        newSet.add(dealId);
      }
      return newSet;
    });
  };

  // Filter functions - use debouncedSearchQuery for performance
  const filterDeals = () => {
    let filtered = [...allDeals];

    if (debouncedSearchQuery) {
      // Search across multiple fields for better results (fix for Playtika issue)
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.companies?.toLowerCase().includes(query) ||
        d.deal_synopsis?.toLowerCase().includes(query) ||
        d.verticals?.some(v => v.toLowerCase().includes(query)) ||
        d.keywords?.toLowerCase().includes(query) ||
        d.investors?.some(i => i.toLowerCase().includes(query))
      );
    }

    // Multi-select checkbox filtering for Deal Type
    if (dealFilters.dealType.length > 0) {
      filtered = filtered.filter(d => dealFilters.dealType.includes(d.deal_type));
    }

    // Multi-select checkbox filtering for Deal Type 2
    if (dealFilters.dealType2.length > 0) {
      filtered = filtered.filter(d => dealFilters.dealType2.includes(d.deal_type_2));
    }

    // Multi-select checkbox filtering for Financing Status
    if (dealFilters.financingStatus.length > 0) {
      filtered = filtered.filter(d => dealFilters.financingStatus.includes(d.financing_status));
    }

    // Multi-select filtering for Investors
    if (dealFilters.investors && dealFilters.investors.length > 0) {
      filtered = filtered.filter(d => {
        const dealInvestors = [
          ...(Array.isArray(d.investors) ? d.investors : []),
          ...(Array.isArray(d.lead_sole_investors) ? d.lead_sole_investors : []),
          ...(Array.isArray(d.lead_investors) ? d.lead_investors : [])
        ];
        return dealFilters.investors.some(inv =>
          dealInvestors.some(di => di && di.toLowerCase().includes(inv.toLowerCase()))
        );
      });
    }

    if (dealFilters.minSize) {
      filtered = filtered.filter(d =>
        d.deal_size_usd && d.deal_size_usd >= parseFloat(dealFilters.minSize)
      );
    }

    if (dealFilters.maxSize) {
      filtered = filtered.filter(d =>
        d.deal_size_usd && d.deal_size_usd <= parseFloat(dealFilters.maxSize)
      );
    }

    if (dealFilters.dateFrom) {
      filtered = filtered.filter(d =>
        d.deal_date && new Date(d.deal_date) >= new Date(dealFilters.dateFrom)
      );
    }

    if (dealFilters.dateTo) {
      filtered = filtered.filter(d =>
        d.deal_date && new Date(d.deal_date) <= new Date(dealFilters.dateTo)
      );
    }

    setDeals(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const filterCompanies = () => {
    // CRITICAL: Deduplicate by pitchbook_id first to prevent duplicate rows
    const seen = new Set();
    let filtered = allCompanies.filter(c => {
      if (seen.has(c.pitchbook_id)) return false;
      seen.add(c.pitchbook_id);
      return true;
    });

    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
      );
    }

    // Multi-select filtering for industry
    if (companyFilters.industry.length > 0) {
      filtered = filtered.filter(c =>
        companyFilters.industry.includes(c.primary_industry || c.primary_industry_code)
      );
    }

    // Multi-select filtering for location
    if (companyFilters.location.length > 0) {
      filtered = filtered.filter(c =>
        companyFilters.location.includes(c.hq_city || c.hq_location)
      );
    }

    if (companyFilters.minRevenue) {
      filtered = filtered.filter(c =>
        c.revenue && c.revenue >= parseFloat(companyFilters.minRevenue)
      );
    }

    if (companyFilters.maxRevenue) {
      filtered = filtered.filter(c =>
        c.revenue && c.revenue <= parseFloat(companyFilters.maxRevenue)
      );
    }

    // Show ALL companies - no artificial limits!
    setCompanies(filtered);
    setCurrentPage(1); // Reset pagination
  };

  const filterInvestors = () => {
    let filtered = [...allInvestors];

    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(i =>
        i.name?.toLowerCase().includes(query) ||
        i.description?.toLowerCase().includes(query)
      );
    }

    // Multi-select filtering for investor type
    if (investorFilters.investorType.length > 0) {
      filtered = filtered.filter(i =>
        investorFilters.investorType.includes(i.investor_type)
      );
    }

    if (investorFilters.minAUM) {
      filtered = filtered.filter(i =>
        i.aum && i.aum >= parseFloat(investorFilters.minAUM) * 1000000
      );
    }

    if (investorFilters.maxAUM) {
      filtered = filtered.filter(i =>
        i.aum && i.aum <= parseFloat(investorFilters.maxAUM) * 1000000
      );
    }

    // Show ALL investors - no artificial limits!
    setInvestors(filtered);
    setCurrentPage(1); // Reset pagination
  };

  // Apply filters when they change - uses debounced search query to prevent lag on every keystroke
  useEffect(() => {
    if (activeTab === 'deals') filterDeals();
  }, [debouncedSearchQuery, dealFilters]);

  useEffect(() => {
    if (activeTab === 'companies') filterCompanies();
  }, [debouncedSearchQuery, companyFilters]);

  useEffect(() => {
    if (activeTab === 'investors') filterInvestors();
  }, [debouncedSearchQuery, investorFilters]);

  const handleAISearch = async () => {
    if (!aiSearchQuery.trim()) return;

    setAISearchLoading(true);
    setAISearchError(null);
    setAISearchResults(null);
    setAISearchProgress([]);

    // Enhanced progress showing actual 3-step search process
    const progressSteps = [
      { delay: 100, message: '▸ Initializing AI search agent...', type: 'init', detail: 'Gemini 3.5 Flash agent starting up' },
      { delay: 400, message: '◆ Analyzing query and extracting keywords...', type: 'thinking', detail: `Query: "${aiSearchQuery.trim()}"` },
      { delay: 900, message: '◆ Planning 3-step search strategy...', type: 'thinking', detail: 'Step 1: SQL keywords → Step 2: Dense vectors → Step 3: Sparse vectors' },
      { delay: 1500, message: '① STEP 1: SQL Keywords Search', type: 'searching', detail: 'Searching "keywords" column in SQL database (8-10 results expected)' },
      { delay: 2800, message: '② STEP 2: Dense Vector Search (Semantic)', type: 'searching', detail: 'Using llama-text-embed-v2 for conceptual similarity (8-10 results)' },
      { delay: 4500, message: '③ STEP 3: Sparse Vector Search (SPLADE)', type: 'searching', detail: 'Using SPLADE sparse vectors for keyword variations, alpha=0.6-0.8 (8-10 results)' },
      { delay: 6200, message: '◇ Merging results from 3 sources...', type: 'processing', detail: 'Deduplicating by ID and ranking by relevance score' },
      { delay: 7400, message: '✓ Results ready', type: 'success', detail: 'Combined results from SQL, Dense (VDB), and Sparse (VDB) searches' },
    ];

    // Show progress messages with timestamps
    const startTime = Date.now();
    progressSteps.forEach(step => {
      setTimeout(() => {
        if (aiSearchLoading) {
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
          setAISearchProgress(prev => [...prev, { ...step, timestamp: `${elapsed}s` }]);
        }
      }, step.delay);
    });

    try {
      const result = await api.agentSearch({
        query: aiSearchQuery.trim(),
        active_tab: activeTab,
        max_results: 20
      });

      setAISearchProgress(prev => [...prev, { message: '✅ Search complete!', type: 'success' }]);
      setTimeout(() => {
        setAISearchResults(result);
      }, 300);
      console.log('AI Search Results:', result);
    } catch (error) {
      console.error('AI Search Error:', error);

      // Build detailed error message based on error type
      let errorMsg = 'AI search failed';
      let errorDetails = null;

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Search timed out (60s limit)';
        errorDetails = 'Try a simpler query or break it into smaller parts. Complex queries like "companies that could buy X" require multiple reasoning steps.';
      } else if (error.response?.status === 504) {
        errorMsg = 'Gemini API timeout';
        errorDetails = error.response?.data?.detail || 'The AI took too long to process. Try a more specific query.';
      } else if (error.response?.status === 429) {
        errorMsg = 'Rate limit exceeded';
        errorDetails = 'Too many requests. Please wait a moment and try again.';
      } else if (error.response?.status === 503) {
        errorMsg = 'API connection error';
        errorDetails = error.response?.data?.detail || 'Cannot connect to AI service. Check network connection.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
        // Extract timing info if present
        const timeMatch = errorMsg.match(/after (\d+\.?\d*)s/);
        if (timeMatch) {
          errorDetails = `Search ran for ${timeMatch[1]}s before failing.`;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      setAISearchError({ message: errorMsg, details: errorDetails });
      setAISearchProgress(prev => [...prev, {
        message: `❌ ${errorMsg}`,
        type: 'error',
        detail: errorDetails
      }]);
    } finally {
      setAISearchLoading(false);
    }
  };

  // Helper to add log to agent console
  const addConsoleLog = (type, message, status = 'running') => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setAgentConsoleLogs(prev => [...prev, { id: Date.now(), type, message, status, timestamp }]);
    // Auto-scroll console to bottom
    setTimeout(() => {
      if (agentConsoleRef.current) {
        agentConsoleRef.current.scrollTop = agentConsoleRef.current.scrollHeight;
      }
    }, 50);
  };

  // Update last log status
  const updateLastLogStatus = (status) => {
    setAgentConsoleLogs(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      updated[updated.length - 1] = { ...updated[updated.length - 1], status };
      return updated;
    });
  };

  // Hero Search handler - powers the search-first experience
  // Supports two modes: Fast Search (default) and Research Agent (toggle)
  const handleHeroSearch = async () => {
    if (!heroSearchQuery.trim()) return;

    setHeroSearchLoading(true);
    setHeroSearchError(null);
    setHeroSearchResults(null);
    setHeroSearchDebugInfo(null); // Reset debug info

    const queryText = heroSearchQuery.trim();
    const entityText = heroEntityFilter === 'all' ? 'all entities' : heroEntityFilter;

    // Determine entity type for API
    const entityTypeMap = {
      'companies': 'company',
      'deals': 'deal',
      'investors': 'investor',
      'all': null
    };

    try {
      console.log('🔍 Hero search:', { query: queryText, mode: heroSearchMode, entity: heroEntityFilter, useAgent: useResearchAgent });

      if (useResearchAgent) {
        // ========== RESEARCH AGENT MODE ==========
        // Show console and do full agent workflow
        setAgentConsoleLogs([]);
        setAgentConsoleVisible(true);

        // Log: Initialize
        addConsoleLog('init', `Initializing search agent...`, 'running');
        await new Promise(r => setTimeout(r, 200));
        updateLastLogStatus('done');

        // Log: Query parsing
        addConsoleLog('parse', `Parsing query: "${queryText}"`, 'running');
        await new Promise(r => setTimeout(r, 300));
        updateLastLogStatus('done');

        // Log: Complexity analysis
        addConsoleLog('analyze', `Analyzing query complexity...`, 'running');
        await new Promise(r => setTimeout(r, 250));
        updateLastLogStatus('done');

        // Log: Target entities
        addConsoleLog('target', `Target: ${entityText}`, 'done');

        // Log: Model connection
        addConsoleLog('model', `Connecting to Gemini 3.5 Flash...`, 'running');
        await new Promise(r => setTimeout(r, 400));
        updateLastLogStatus('done');

        // Log: Tool selection
        addConsoleLog('tools', `Agent selecting search tools...`, 'running');

        // Make agent API call
        const result = await api.agentSearch({
          query: queryText,
          active_tab: heroEntityFilter === 'all' ? null : heroEntityFilter,
          max_results: 30
        });

        // Log: Tools used based on actual result
        updateLastLogStatus('done');

        // Display detailed tool calls if available
        if (result.tool_calls && result.tool_calls.length > 0) {
          for (const tc of result.tool_calls) {
            const toolType = tc.tool.includes('sql') ? 'sql' :
                            tc.tool.includes('semantic') ? 'vdb' :
                            tc.tool.includes('keyword') ? 'vdb' : 'tools';
            const toolLabel = tc.tool === 'search_sql' ? 'SQL' :
                             tc.tool === 'search_sql_by_name' ? 'LOOKUP' :
                             tc.tool === 'search_vdb_semantic' ? 'VDB_SEM' :
                             tc.tool === 'search_vdb_keyword' ? 'VDB_KW' : tc.tool;
            const queryArg = tc.args.query || tc.args.name || '';
            const entityArg = tc.args.entity_type || 'all';
            addConsoleLog(toolType, `${toolLabel}("${queryArg}", ${entityArg}) → ${tc.result_count} results`, 'done');
            // Show sample results if any
            if (tc.sample_results && tc.sample_results.length > 0) {
              const samples = tc.sample_results.slice(0, 3).join(', ');
              addConsoleLog('breakdown', `  └─ ${samples}${tc.result_count > 3 ? '...' : ''}`, 'done');
            }
          }
        } else if (result.sources_used && result.sources_used.length > 0) {
          // Fallback to sources_used if no detailed tool_calls
          for (const source of result.sources_used) {
            if (source.includes('SQL')) {
              addConsoleLog('sql', `Querying SQL Database...`, 'done');
            } else if (source.includes('Semantic') || source.includes('Dense')) {
              addConsoleLog('vdb', `Searching VDB [semantic vectors]...`, 'done');
            } else if (source.includes('Keyword') || source.includes('Sparse')) {
              addConsoleLog('vdb', `Searching VDB [keyword expansion]...`, 'done');
            } else if (source.includes('Lookup')) {
              addConsoleLog('lookup', `Entity profile lookup...`, 'done');
            }
          }
        } else {
          addConsoleLog('sql', `Querying SQL Database...`, 'done');
          addConsoleLog('vdb', `Searching VDB [vectors]...`, 'done');
        }

        // Log: Results tiering
        addConsoleLog('tier', `Tiering results by relevance...`, 'running');
        await new Promise(r => setTimeout(r, 150));
        updateLastLogStatus('done');

        // Log: Final results
        const totalResults = result.total_found || result.results?.length || 0;
        const tier1Count = result.tiered_results?.tier1?.length || 0;
        const tier2Count = result.tiered_results?.tier2?.length || 0;
        const tier3Count = result.tiered_results?.tier3?.length || 0;

        addConsoleLog('result', `Found ${totalResults} results`, 'done');
        if (tier1Count > 0 || tier2Count > 0 || tier3Count > 0) {
          addConsoleLog('breakdown', `├─ Tier 1 (Direct): ${tier1Count}`, 'done');
          addConsoleLog('breakdown', `├─ Tier 2 (Related): ${tier2Count}`, 'done');
          addConsoleLog('breakdown', `└─ Tier 3 (Exploratory): ${tier3Count}`, 'done');
        }

        // Log: Complete
        addConsoleLog('complete', `SEARCH_COMPLETE`, 'success');

        console.log('✅ Agent search results:', result);
        setHeroSearchResults(result);

      } else {
        // ========== FAST SEARCH MODE (Default) ==========
        // No agent, no console - just fast hybrid/semantic/keyword search
        setAgentConsoleVisible(false);

        let result;

        // Build filters object including Common Searches
        const searchFilters = {
          entity_type: entityTypeMap[heroEntityFilter],
          // Common Searches filters
          ...(selectedDealRounds.length > 0 && { deal_rounds: selectedDealRounds }),
          ...(selectedTechCore.length > 0 && { tech_core: selectedTechCore }),
          ...(selectedPlatforms.length > 0 && { platforms: selectedPlatforms })
        };

        console.log('🔍 Search filters:', searchFilters);

        if (heroSearchMode === 'hybrid') {
          // Hybrid search - uses both dense (semantic) and sparse (BM25) vectors
          result = await api.hybridSearch({
            query: queryText,
            filters: searchFilters,
            search_mode: 'hybrid',
            use_dense: true,
            use_sparse: true,
            alpha: heroSearchAlpha,
            top_k: 30
          });
        } else if (heroSearchMode === 'strict') {
          // Strict/keyword search - SQL ILIKE only
          result = await api.hybridSearch({
            query: queryText,
            filters: searchFilters,
            search_mode: 'strict',
            top_k: 30
          });
        } else if (heroSearchMode === 'lexical') {
          // Lexical search - sparse BM25 vectors only (no dense/semantic)
          result = await api.hybridSearch({
            query: queryText,
            filters: searchFilters,
            search_mode: 'hybrid',
            use_dense: false,
            use_sparse: true,
            alpha: 1.0, // Full weight on sparse
            top_k: 30
          });
        } else {
          // Semantic search - dense vectors only (llama embeddings)
          result = await api.hybridSearch({
            query: queryText,
            filters: searchFilters,
            search_mode: 'semantic',
            use_dense: true,
            use_sparse: false,
            alpha: 0.0,
            top_k: 30
          });
        }

        // CRITICAL: Capture debug info from backend response
        if (result.debug_info) {
          console.log('🔍 Backend debug_info:', result.debug_info);
          setHeroSearchDebugInfo(result.debug_info);
        } else {
          console.log('⚠️ No debug_info in response. Response keys:', Object.keys(result));
          // Still set something so we know the search ran
          setHeroSearchDebugInfo({
            mode: heroSearchMode,
            warning: 'No debug_info returned from backend',
            response_keys: Object.keys(result)
          });
        }

        // Transform fast search results to match expected format
        // Fast search returns flat results, we'll put them all in tier1 (direct matches)
        const transformedResult = {
          results: result.results || result,
          total_found: result.total_found || result.total || (result.results || result).length,
          tiered_results: {
            tier1: (result.results || result).slice(0, 15), // Direct matches
            tier2: (result.results || result).slice(15, 25), // Related
            tier3: (result.results || result).slice(25, 30)  // Exploratory
          },
          sources_used: [heroSearchMode === 'strict' ? 'Exact Match (SQL)' : heroSearchMode === 'lexical' ? 'Lexical (BM25)' : heroSearchMode === 'semantic' ? 'Semantic (AI Embeddings)' : 'Hybrid (Lexical + Semantic)'],
          debug_info: result.debug_info // Pass through debug info
        };

        console.log('✅ Fast search results:', transformedResult);
        setHeroSearchResults(transformedResult);
      }

    } catch (error) {
      console.error('❌ Hero search error:', error);
      // ALWAYS set debug info on error so we can see what happened
      setHeroSearchDebugInfo({
        mode: heroSearchMode,
        error: error.message || 'Unknown error',
        error_type: error.name || 'Error',
        response_status: error.response?.status,
        response_data: error.response?.data,
        stack: error.stack?.substring(0, 500)
      });
      if (useResearchAgent) {
        updateLastLogStatus('error');
        addConsoleLog('error', `Error: ${error.response?.data?.detail || error.message || 'Search failed'}`, 'error');
      }
      setHeroSearchError({
        message: error.response?.data?.detail || error.message || 'Search failed',
        details: 'Please try a different query or check your connection.'
      });
    } finally {
      setHeroSearchLoading(false);
    }
  };

  // Navigate to result detail page from hero search - opens in new tab
  const handleHeroResultClick = (result) => {
    const entityType = result.entity_type || result.type || 'company';
    let url = '';

    if (entityType === 'company' && result.pitchbook_id) {
      url = `/company/${result.pitchbook_id}`;
    } else if (entityType === 'deal' && result.pitchbook_deal_id) {
      url = `/deal/${result.pitchbook_deal_id}`;
    } else if (entityType === 'investor' && result.pitchbook_investor_id) {
      url = `/investor/${result.pitchbook_investor_id}`;
    } else {
      // Fallback - try to find the right ID
      const id = result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || result.id;
      if (id) {
        url = `/${entityType}/${id}`;
      }
    }

    // Open in new tab
    if (url) {
      window.open(url, '_blank');
    }
  };

  const viewInDataExplorer = () => {
    // Pass current filters to Data Explorer
    const params = new URLSearchParams();
    params.append('tab', activeTab);
    if (searchQuery) params.append('search', searchQuery);

    if (activeTab === 'deals') {
      Object.entries(dealFilters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          params.append(key, JSON.stringify(value));
        } else if (value && !Array.isArray(value)) {
          params.append(key, value);
        }
      });
    } else if (activeTab === 'companies') {
      Object.entries(companyFilters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          params.append(key, JSON.stringify(value));
        } else if (value && !Array.isArray(value)) {
          params.append(key, value);
        }
      });
    } else if (activeTab === 'investors') {
      Object.entries(investorFilters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          params.append(key, JSON.stringify(value));
        } else if (value && !Array.isArray(value)) {
          params.append(key, value);
        }
      });
    }

    navigate(`/data-explorer?${params.toString()}`);
  };

  // Export Agent Results to Data Explorer - stores actual SQL results in localStorage
  const exportAgentResultsToDataExplorer = () => {
    if (!aiSearchResults) return;

    // Collect all results from tiers
    const allResults = [
      ...(aiSearchResults.tiered_results?.tier1 || []),
      ...(aiSearchResults.tiered_results?.tier2 || []),
      ...(aiSearchResults.tiered_results?.tier3 || []),
      ...(aiSearchResults.results || [])
    ];

    // Group results by entity type
    const groupedResults = {
      companies: allResults.filter(r => r.entity_type === 'company'),
      deals: allResults.filter(r => r.entity_type === 'deal'),
      investors: allResults.filter(r => r.entity_type === 'investor')
    };

    // Store in localStorage for Data Explorer to pick up
    localStorage.setItem('transcend_agent_results', JSON.stringify({
      query: aiSearchResults.query || aiSearchQuery,
      timestamp: new Date().toISOString(),
      results: groupedResults,
      totalFound: aiSearchResults.total_found || allResults.length,
      thinking: aiSearchResults.thinking,
      sourcesUsed: aiSearchResults.sources_used
    }));

    // Determine which tab to open based on majority entity type
    const tabCounts = {
      companies: groupedResults.companies.length,
      deals: groupedResults.deals.length,
      investors: groupedResults.investors.length
    };
    const primaryTab = Object.entries(tabCounts).sort((a, b) => b[1] - a[1])[0][0];

    // Open Data Explorer with agent results flag
    window.open(`/data-explorer?source=agent&tab=${primaryTab}`, '_blank');
  };

  // Handle tab switching with loading indicator and filter reset
  const handleTabSwitch = async (tab) => {
    // Only show loading indicator if switching to a tab with no data yet
    const hasData =
      (tab === 'deals' && deals.length > 0) ||
      (tab === 'companies' && companies.length > 0) ||
      (tab === 'investors' && investors.length > 0);

    if (!hasData) {
      setTabLoading(true);
    }

    setActiveTab(tab);

    // Reset pagination and search when switching tabs
    setCurrentPage(1);
    setRowsPerPage(50); // Reset to initial view
    setSearchQuery('');
    setShowLessLikelyPrompt(false);

    // Reset filters based on tab
    if (tab === 'deals') {
      setDealFilters({
        dealType: [],
        dealType2: [],
        financingStatus: [],
        investors: [],
        minSize: '',
        maxSize: '',
        dateFrom: '',
        dateTo: ''
      });
      // Restore full deals list
      setDeals(allDeals);
    } else if (tab === 'companies') {
      setCompanyFilters({
        industry: [],
        location: [],
        minRevenue: '',
        maxRevenue: ''
      });
      // Restore full companies list
      setCompanies(allCompanies);
    } else if (tab === 'investors') {
      setInvestorFilters({
        investorType: [],
        minAUM: '',
        maxAUM: ''
      });
      // Restore full investors list
      setInvestors(allInvestors);
    }

    // Brief delay only if showing loader (for UX smoothness)
    if (!hasData) {
      await new Promise(resolve => setTimeout(resolve, 150));
      setTabLoading(false);
    }
  };

  // Unified Search Handler Functions
  const handleUnifiedSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearchLoading(true);
    setShowLessLikelyPrompt(false);

    try {
      switch (searchMode) {
        case 'strict':
          await performStrictSearch();
          break;
        case 'semantic':
          await performSemanticSearch();
          break;
        case 'hybrid':
          await performHybridSearch();
          break;
      }
    } catch (err) {
      console.error('Search error:', err);
      // Could show an alert or just log the error
    } finally {
      setSearchLoading(false);
    }
  };

  const performStrictSearch = async () => {
    const query = searchQuery.trim();

    // Tab-aware field selection
    let searchFields = [];
    if (activeTab === 'deals') {
      searchFields = ['companies', 'deal_synopsis', 'investors'];
    } else if (activeTab === 'companies') {
      searchFields = ['name']; // Strict: Name only
    } else if (activeTab === 'investors') {
      searchFields = ['name']; // Strict: Name only
    }

    // For now, use the existing client-side filtering as a fallback
    // Will be replaced with backend call when endpoint is ready
    if (activeTab === 'deals') {
      const filtered = allDeals.filter(d =>
        d.companies?.toLowerCase().includes(query.toLowerCase()) ||
        d.deal_synopsis?.toLowerCase().includes(query.toLowerCase()) ||
        d.investors?.some(i => i.toLowerCase().includes(query.toLowerCase()))
      );

      if (filtered.length === 0) {
        setShowLessLikelyPrompt(true);
      } else {
        setDeals(filtered);
      }
    } else if (activeTab === 'companies') {
      const queryLower = query.toLowerCase();
      // Deduplicate first
      const seen = new Set();
      const uniqueCompanies = allCompanies.filter(c => {
        if (seen.has(c.pitchbook_id)) return false;
        seen.add(c.pitchbook_id);
        return true;
      });
      const filtered = uniqueCompanies.filter(c =>
        c.name?.toLowerCase().includes(queryLower) ||
        c.description?.toLowerCase().includes(queryLower) ||
        c.primary_industry_code?.toLowerCase().includes(queryLower)
      );

      if (filtered.length === 0) {
        setShowLessLikelyPrompt(true);
      } else {
        setCompanies(filtered);
      }
    } else if (activeTab === 'investors') {
      const filtered = allInvestors.filter(i =>
        i.name?.toLowerCase().includes(query.toLowerCase())
      );

      if (filtered.length === 0) {
        setShowLessLikelyPrompt(true);
      } else {
        setInvestors(filtered);
      }
    }
  };

  const handleShowLessLikely = async () => {
    setShowLessLikelyPrompt(false);
    setSearchLoading(true);

    try {
      // Relaxed search: Include description, keywords, etc.
      const query = searchQuery.trim().toLowerCase();

      if (activeTab === 'deals') {
        const filtered = allDeals.filter(d =>
          d.companies?.toLowerCase().includes(query) ||
          d.deal_synopsis?.toLowerCase().includes(query) ||
          d.investors?.some(i => i.toLowerCase().includes(query)) ||
          d.verticals?.some(v => v.toLowerCase().includes(query)) ||
          d.keywords?.toLowerCase().includes(query) ||
          d.description?.toLowerCase().includes(query)
        );
        setDeals(filtered);
      } else if (activeTab === 'companies') {
        const filtered = allCompanies.filter(c =>
          c.name?.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query) ||
          c.primary_industry?.toLowerCase().includes(query) ||
          c.website?.toLowerCase().includes(query)
        );
        setCompanies(filtered);
      } else if (activeTab === 'investors') {
        const filtered = allInvestors.filter(i =>
          i.name?.toLowerCase().includes(query) ||
          i.description?.toLowerCase().includes(query) ||
          i.investor_type?.toLowerCase().includes(query) ||
          i.hq_location?.toLowerCase().includes(query)
        );
        setInvestors(filtered);
      }
    } catch (err) {
      console.error('Relaxed search error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const performSemanticSearch = async () => {
    try {
      const response = await api.hybridSearch({
        query: searchQuery.trim(),
        search_mode: 'semantic',
        use_dense: true,
        use_sparse: false,
        alpha: 0.0,
        top_k: 50,
        filters: { entity_type: activeTab === 'companies' ? 'company' : activeTab.slice(0, -1) }
      });

      console.log('Semantic search results:', response);

      // Capture debug info from backend for troubleshooting
      if (response.debug_info) {
        setSearchDebugInfo(response.debug_info);
        console.log('Debug info:', response.debug_info);
      }

      // Map backend results to frontend format (results are now flat, no metadata wrapper)
      const mappedResults = response.results?.map(r => {
        // Results are flat with SQL data at top level
        return {
          ...r,
          id: r.sql_id || r.id || r.pitchbook_id || r.pitchbook_deal_id || r.pitchbook_investor_id,
          _search_score: r.score,
          _search_type: 'semantic'
        };
      }) || [];

      // Filter by active tab
      const filteredResults = mappedResults.filter(r => {
        if (activeTab === 'deals') return r.type === 'deal' || !r.type;
        if (activeTab === 'companies') return r.type === 'company' || r.type === 'companie' || !r.type;
        if (activeTab === 'investors') return r.type === 'investor' || !r.type;
        return true;
      });

      if (activeTab === 'deals') setDeals(filteredResults);
      else if (activeTab === 'companies') setCompanies(filteredResults);
      else if (activeTab === 'investors') setInvestors(filteredResults);

      if (filteredResults.length === 0) {
        setShowLessLikelyPrompt(true);
      }
    } catch (err) {
      console.error('Semantic search error:', err);
      // Fallback to strict search if Pinecone is unavailable
      await performStrictSearch();
    }
  };

  const performHybridSearch = async () => {
    try {
      const response = await api.hybridSearch({
        query: searchQuery.trim(),
        search_mode: 'hybrid',
        use_dense: true,
        use_sparse: true,
        alpha: searchAlpha,
        top_k: 50,
        filters: { entity_type: activeTab === 'companies' ? 'company' : activeTab.slice(0, -1) }
      });

      console.log('Hybrid search results:', response);

      // Capture debug info from backend for troubleshooting
      if (response.debug_info) {
        setSearchDebugInfo(response.debug_info);
        console.log('Debug info:', response.debug_info);
      }

      // Map backend results to frontend format (results are now flat, no metadata wrapper)
      const mappedResults = response.results?.map(r => {
        // Results are flat with SQL data at top level
        return {
          ...r,
          id: r.sql_id || r.id || r.pitchbook_id || r.pitchbook_deal_id || r.pitchbook_investor_id,
          _search_score: r.score,
          _search_type: 'hybrid',
          _search_alpha: searchAlpha
        };
      }) || [];

      // Filter by active tab
      const filteredResults = mappedResults.filter(r => {
        if (activeTab === 'deals') return r.type === 'deal' || !r.type;
        if (activeTab === 'companies') return r.type === 'company' || r.type === 'companie' || !r.type;
        if (activeTab === 'investors') return r.type === 'investor' || !r.type;
        return true;
      });

      if (activeTab === 'deals') setDeals(filteredResults);
      else if (activeTab === 'companies') setCompanies(filteredResults);
      else if (activeTab === 'investors') setInvestors(filteredResults);

      if (filteredResults.length === 0) {
        setShowLessLikelyPrompt(true);
      }
    } catch (err) {
      console.error('Hybrid search error:', err);
      // Fallback to strict search if Pinecone is unavailable
      await performStrictSearch();
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSearchMode('strict'); // Reset to default search mode
    setShowLessLikelyPrompt(false); // Hide any prompts
    setDealFilters({
      dealType: [],
      dealType2: [],
      financingStatus: [],
      investors: [],
      minSize: '',
      maxSize: '',
      dateFrom: '',
      dateTo: ''
    });
    setCompanyFilters({
      industry: [],
      location: [],
      minRevenue: '',
      maxRevenue: ''
    });
    setInvestorFilters({
      investorType: [],
      minAUM: '',
      maxAUM: ''
    });
    setCurrentPage(1);
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];

    // Search query
    if (searchQuery) {
      active.push({ type: 'search', label: `Search: "${searchQuery}"` });
    }

    // Deal filters
    if (activeTab === 'deals') {
      if (dealFilters.dealType.length > 0) {
        active.push({ type: 'dealType', label: `Deal Type: ${dealFilters.dealType.join(', ')}` });
      }
      if (dealFilters.dealType2.length > 0) {
        active.push({ type: 'dealType2', label: `Deal Type 2: ${dealFilters.dealType2.join(', ')}` });
      }
      if (dealFilters.financingStatus.length > 0) {
        active.push({ type: 'financingStatus', label: `Status: ${dealFilters.financingStatus.join(', ')}` });
      }
      if (dealFilters.investors && dealFilters.investors.length > 0) {
        active.push({ type: 'investors', label: `Investors: ${dealFilters.investors.slice(0, 2).join(', ')}${dealFilters.investors.length > 2 ? ` +${dealFilters.investors.length - 2} more` : ''}` });
      }
      if (dealFilters.minSize) {
        active.push({ type: 'minSize', label: `Min Size: $${dealFilters.minSize}M` });
      }
      if (dealFilters.maxSize) {
        active.push({ type: 'maxSize', label: `Max Size: $${dealFilters.maxSize}M` });
      }
      if (dealFilters.dateFrom) {
        active.push({ type: 'dateFrom', label: `From: ${dealFilters.dateFrom}` });
      }
      if (dealFilters.dateTo) {
        active.push({ type: 'dateTo', label: `To: ${dealFilters.dateTo}` });
      }
    }

    // Company filters
    if (activeTab === 'companies') {
      if (companyFilters.industry.length > 0) {
        active.push({ type: 'industry', label: `Industry: ${companyFilters.industry.join(', ')}` });
      }
      if (companyFilters.location.length > 0) {
        active.push({ type: 'location', label: `Location: ${companyFilters.location.join(', ')}` });
      }
      if (companyFilters.minRevenue) {
        active.push({ type: 'minRevenue', label: `Min Revenue: $${companyFilters.minRevenue}M` });
      }
      if (companyFilters.maxRevenue) {
        active.push({ type: 'maxRevenue', label: `Max Revenue: $${companyFilters.maxRevenue}M` });
      }
    }

    // Investor filters
    if (activeTab === 'investors') {
      if (investorFilters.investorType.length > 0) {
        active.push({ type: 'investorType', label: `Type: ${investorFilters.investorType.join(', ')}` });
      }
      if (investorFilters.minAUM) {
        active.push({ type: 'minAUM', label: `Min AUM: $${investorFilters.minAUM}M` });
      }
      if (investorFilters.maxAUM) {
        active.push({ type: 'maxAUM', label: `Max AUM: $${investorFilters.maxAUM}M` });
      }
    }

    return active;
  };

  // Checkbox toggle handler - now supports all entity types
  const toggleCheckbox = (filterType, value) => {
    if (filterType === 'dealType') {
      setDealFilters(prev => ({
        ...prev,
        dealType: prev.dealType.includes(value)
          ? prev.dealType.filter(v => v !== value)
          : [...prev.dealType, value]
      }));
    } else if (filterType === 'dealType2') {
      setDealFilters(prev => ({
        ...prev,
        dealType2: prev.dealType2.includes(value)
          ? prev.dealType2.filter(v => v !== value)
          : [...prev.dealType2, value]
      }));
    } else if (filterType === 'financingStatus') {
      setDealFilters(prev => ({
        ...prev,
        financingStatus: prev.financingStatus.includes(value)
          ? prev.financingStatus.filter(v => v !== value)
          : [...prev.financingStatus, value]
      }));
    } else if (filterType === 'investors') {
      setDealFilters(prev => ({
        ...prev,
        investors: (prev.investors || []).includes(value)
          ? (prev.investors || []).filter(v => v !== value)
          : [...(prev.investors || []), value]
      }));
    } else if (filterType === 'industry') {
      setCompanyFilters(prev => ({
        ...prev,
        industry: prev.industry.includes(value)
          ? prev.industry.filter(v => v !== value)
          : [...prev.industry, value]
      }));
    } else if (filterType === 'location') {
      setCompanyFilters(prev => ({
        ...prev,
        location: prev.location.includes(value)
          ? prev.location.filter(v => v !== value)
          : [...prev.location, value]
      }));
    } else if (filterType === 'investorType') {
      setInvestorFilters(prev => ({
        ...prev,
        investorType: prev.investorType.includes(value)
          ? prev.investorType.filter(v => v !== value)
          : [...prev.investorType, value]
      }));
    }
  };

  // Helper function for clearing dropdown selections
  const handleClearSelection = (filterType) => {
    if (filterType === 'dealType') setDealFilters(prev => ({ ...prev, dealType: [] }));
    else if (filterType === 'dealType2') setDealFilters(prev => ({ ...prev, dealType2: [] }));
    else if (filterType === 'financingStatus') setDealFilters(prev => ({ ...prev, financingStatus: [] }));
    else if (filterType === 'investors') setDealFilters(prev => ({ ...prev, investors: [] }));
    else if (filterType === 'industry') setCompanyFilters(prev => ({ ...prev, industry: [] }));
    else if (filterType === 'location') setCompanyFilters(prev => ({ ...prev, location: [] }));
    else if (filterType === 'investorType') setInvestorFilters(prev => ({ ...prev, investorType: [] }));
  };

  // ============================================================================
  // MOBILE STATE - Unified search with filters toggle
  // ============================================================================
  const [mobileActiveTab, setMobileActiveTab] = useState('search'); // 'search' only (Agent is floating bubble)
  const [mobileSearchMode, setMobileSearchMode] = useState('hybrid'); // 'semantic', 'strict', 'hybrid' - default hybrid like desktop
  const [mobileShowFilters, setMobileShowFilters] = useState(false); // Toggle for advanced filters
  const [mobileShowModeInfo, setMobileShowModeInfo] = useState(null); // Which mode info popup to show
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [mobileSearchResults, setMobileSearchResults] = useState(null);
  const [mobileSearchLoading, setMobileSearchLoading] = useState(false);
  const [mobileSearchError, setMobileSearchError] = useState(null);
  const [mobileEntityType, setMobileEntityType] = useState('all'); // 'all', 'companies', 'deals', 'investors'

  // Mobile Advanced Filters State
  const [mobileFilterIndustry, setMobileFilterIndustry] = useState('');
  const [mobileFilterLocation, setMobileFilterLocation] = useState('');
  const [mobileFilterDealType, setMobileFilterDealType] = useState('');
  const [mobileFilterMinRaised, setMobileFilterMinRaised] = useState('');
  const [mobileFilterMaxRaised, setMobileFilterMaxRaised] = useState('');
  const [mobileFilterYearFrom, setMobileFilterYearFrom] = useState('');
  const [mobileFilterYearTo, setMobileFilterYearTo] = useState('');
  const [mobileFilterInvestorType, setMobileFilterInvestorType] = useState('');

  // Mobile Detail Popup State
  const [mobileSelectedResult, setMobileSelectedResult] = useState(null);

  // Mobile Search Handler - Uses same API calls as desktop for consistency
  const handleMobileSearch = async () => {
    if (!mobileSearchQuery.trim()) return;

    setMobileSearchLoading(true);
    setMobileSearchError(null);
    setMobileSearchResults(null);

    const queryText = mobileSearchQuery.trim();

    // Map entity types to match backend expectations (same as desktop)
    const entityTypeMap = {
      'companies': 'company',
      'deals': 'deal',
      'investors': 'investor',
      'all': null
    };

    try {
      let result;

      // Build filters object - same structure as desktop
      const searchFilters = {
        entity_type: entityTypeMap[mobileEntityType]
      };

      // Add optional filters if the filters panel is shown and has values
      if (mobileShowFilters) {
        if (mobileFilterIndustry) searchFilters.industry = mobileFilterIndustry;
        if (mobileFilterLocation) searchFilters.location = mobileFilterLocation;
        if (mobileFilterDealType) searchFilters.deal_type = mobileFilterDealType;
        if (mobileFilterMinRaised) searchFilters.min_raised = parseInt(mobileFilterMinRaised) * 1000000;
        if (mobileFilterMaxRaised) searchFilters.max_raised = parseInt(mobileFilterMaxRaised) * 1000000;
        if (mobileFilterYearFrom) searchFilters.year_from = parseInt(mobileFilterYearFrom);
        if (mobileFilterYearTo) searchFilters.year_to = parseInt(mobileFilterYearTo);
        if (mobileFilterInvestorType) searchFilters.investor_type = mobileFilterInvestorType;
      }

      console.log('📱 Mobile search:', { query: queryText, mode: mobileSearchMode, filters: searchFilters });

      // Use SAME API calls as desktop handleHeroSearch
      if (mobileSearchMode === 'hybrid') {
        // Hybrid search - uses both dense (semantic) and sparse (BM25) vectors
        result = await api.hybridSearch({
          query: queryText,
          filters: searchFilters,
          search_mode: 'hybrid',
          use_dense: true,
          use_sparse: true,
          alpha: 0.5,
          top_k: 30
        });
      } else if (mobileSearchMode === 'strict') {
        // Lexical search - sparse BM25 vectors only (no dense/semantic)
        result = await api.hybridSearch({
          query: queryText,
          filters: searchFilters,
          search_mode: 'hybrid',
          use_dense: false,
          use_sparse: true,
          alpha: 1.0, // Full weight on sparse
          top_k: 30
        });
      } else {
        // Semantic search (default) - dense vectors only
        result = await api.hybridSearch({
          query: queryText,
          filters: searchFilters,
          search_mode: 'semantic',
          use_dense: true,
          use_sparse: false,
          alpha: 0.0,
          top_k: 30
        });
      }

      // Transform results to match expected mobile format with tiers
      const transformedResult = {
        results: result.results || result,
        total_found: result.total_found || result.total || (result.results || result).length,
        tiered_results: {
          tier1: (result.results || result).slice(0, 15),
          tier2: (result.results || result).slice(15, 25),
          tier3: (result.results || result).slice(25, 30)
        },
        debug_info: result.debug_info
      };

      console.log('✅ Mobile search results:', transformedResult);
      setMobileSearchResults(transformedResult);
    } catch (err) {
      console.error('📱 Mobile search error:', err);
      setMobileSearchError(err.response?.data?.detail || err.message || 'Search failed. Please try again.');
    } finally {
      setMobileSearchLoading(false);
    }
  };

  // Clear mobile filters
  const clearMobileFilters = () => {
    setMobileFilterIndustry('');
    setMobileFilterLocation('');
    setMobileFilterDealType('');
    setMobileFilterMinRaised('');
    setMobileFilterMaxRaised('');
    setMobileFilterYearFrom('');
    setMobileFilterYearTo('');
    setMobileFilterInvestorType('');
    setMobileSearchResults(null);
  };

  // ============================================================================
  // MOBILE VIEW (including tablets) - Agent-first, no heavy data loading
  // ============================================================================
  if (isMobile) {
    return (
      <div className="mobile-landing mobile-elegant">
        {/* Mobile Header - Elegant Constellation Logo */}
        <div className="mobile-header-elegant">
          <div className="mobile-logo-elegant">
            <svg viewBox="0 0 40 40" className="mobile-constellation-logo">
              <defs>
                <linearGradient id="mobileHeaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1a2744" />
                  <stop offset="100%" stopColor="#2C3E50" />
                </linearGradient>
              </defs>
              <rect x="2" y="2" width="36" height="36" rx="8" fill="url(#mobileHeaderGrad)" />
              <g className="constellation-nodes">
                <circle cx="20" cy="18" r="2.5" fill="white" fillOpacity="0.95" />
                <circle cx="11" cy="11" r="1.8" fill="white" fillOpacity="0.8" />
                <circle cx="29" cy="11" r="1.8" fill="white" fillOpacity="0.8" />
                <circle cx="11" cy="27" r="1.5" fill="white" fillOpacity="0.7" />
                <circle cx="29" cy="27" r="1.5" fill="white" fillOpacity="0.7" />
              </g>
              <g className="constellation-lines">
                <line x1="11" y1="11" x2="20" y2="18" stroke="white" strokeWidth="0.8" strokeOpacity="0.35" />
                <line x1="29" y1="11" x2="20" y2="18" stroke="white" strokeWidth="0.8" strokeOpacity="0.35" />
                <line x1="11" y1="27" x2="20" y2="18" stroke="white" strokeWidth="0.6" strokeOpacity="0.25" />
                <line x1="29" y1="27" x2="20" y2="18" stroke="white" strokeWidth="0.6" strokeOpacity="0.25" />
              </g>
            </svg>
            <span className="mobile-logo-text-elegant">Transcend</span>
          </div>
        </div>

        {/* ============= UNIFIED SEARCH INTERFACE ============= */}
        <div className="mobile-search-unified">
          {/* Search Mode Toggle - Elegant Pills with Info */}
          <div className="mobile-mode-pills-container">
            <div className="mobile-mode-pills">
              <button
                className={`mobile-mode-pill ${mobileSearchMode === 'hybrid' ? 'active' : ''}`}
                onClick={() => setMobileSearchMode('hybrid')}
              >
                <FiZap size={14} />
                <span>Hybrid</span>
              </button>
              <button
                className={`mobile-mode-pill ${mobileSearchMode === 'semantic' ? 'active' : ''}`}
                onClick={() => setMobileSearchMode('semantic')}
              >
                <FiActivity size={14} />
                <span>Semantic</span>
              </button>
              <button
                className={`mobile-mode-pill ${mobileSearchMode === 'strict' ? 'active' : ''}`}
                onClick={() => setMobileSearchMode('strict')}
              >
                <FiLayers size={14} />
                <span>Lexical</span>
              </button>
            </div>
            <button
              className="mobile-mode-info-btn"
              onClick={() => setMobileShowModeInfo(mobileShowModeInfo ? null : true)}
              aria-label="Search mode information"
            >
              <FiInfo size={16} />
            </button>
          </div>

          {/* Mode Info Popup */}
          {mobileShowModeInfo && (
            <div className="mobile-mode-info-popup">
              <div className="mobile-mode-info-header">
                <span>Search Modes</span>
                <button onClick={() => setMobileShowModeInfo(null)}><FiX size={16} /></button>
              </div>
              <div className="mobile-mode-info-content">
                <div className="mobile-mode-info-item">
                  <FiZap size={14} className="mode-icon hybrid" />
                  <div>
                    <strong>Hybrid</strong>
                    <p>Best of both: combines semantic understanding with exact keyword matching for optimal results.</p>
                  </div>
                </div>
                <div className="mobile-mode-info-item">
                  <FiActivity size={14} className="mode-icon semantic" />
                  <div>
                    <strong>Semantic</strong>
                    <p>AI-powered conceptual search that understands meaning, synonyms, and related concepts.</p>
                  </div>
                </div>
                <div className="mobile-mode-info-item">
                  <FiLayers size={14} className="mode-icon lexical" />
                  <div>
                    <strong>Lexical</strong>
                    <p>Traditional exact-match search for specific names, terms, or precise keyword matching.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Search Input */}
          <div className="mobile-search-box-elegant">
            <div className="mobile-search-input-elegant">
              <FiSearch className="mobile-search-icon-elegant" size={18} />
              <input
                type="text"
                placeholder={mobileSearchMode === 'strict' ? 'Enter exact name or keyword...' : 'Search companies, deals, investors...'}
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !mobileSearchLoading && handleMobileSearch()}
                disabled={mobileSearchLoading}
              />
              {mobileSearchQuery && (
                <button className="mobile-clear-elegant" onClick={() => setMobileSearchQuery('')}>
                  <FiX size={16} />
                </button>
              )}
            </div>
            <button
              className="mobile-go-btn-elegant"
              onClick={handleMobileSearch}
              disabled={mobileSearchLoading || !mobileSearchQuery.trim()}
            >
              {mobileSearchLoading ? (
                <div className="mobile-spinner-elegant" />
              ) : (
                <FiArrowRight size={20} />
              )}
            </button>
          </div>

          {/* Entity Type Selector - Minimal */}
          <div className="mobile-entity-row">
            {['all', 'companies', 'deals', 'investors'].map(type => (
              <button
                key={type}
                className={`mobile-entity-chip ${mobileEntityType === type ? 'active' : ''}`}
                onClick={() => setMobileEntityType(type)}
              >
                {type === 'all' && <FiGrid size={14} />}
                {type === 'companies' && <FiBriefcase size={14} />}
                {type === 'deals' && <FiDollarSign size={14} />}
                {type === 'investors' && <FiUsers size={14} />}
                <span>{type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}</span>
              </button>
            ))}
          </div>

          {/* Filters Toggle */}
          <button
            className={`mobile-filters-toggle ${mobileShowFilters ? 'active' : ''}`}
            onClick={() => setMobileShowFilters(!mobileShowFilters)}
          >
            <FiSliders size={16} />
            <span>Filters</span>
            {mobileShowFilters ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </button>

          {/* Collapsible Filters Panel */}
          {mobileShowFilters && (
            <div className="mobile-filters-panel">
              {/* Industry */}
              {(mobileEntityType === 'all' || mobileEntityType === 'companies' || mobileEntityType === 'deals') && (
                <div className="mobile-filter-row">
                  <label><FiTag size={14} /> Industry</label>
                  <select value={mobileFilterIndustry} onChange={(e) => setMobileFilterIndustry(e.target.value)}>
                    <option value="">Any</option>
                    <option value="Gaming">Gaming</option>
                    <option value="Software">Software</option>
                    <option value="Mobile Gaming">Mobile Gaming</option>
                    <option value="Esports">Esports</option>
                    <option value="VR/AR">VR/AR</option>
                  </select>
                </div>
              )}

              {/* Location */}
              <div className="mobile-filter-row">
                <label><FiMapPin size={14} /> Location</label>
                <input
                  type="text"
                  placeholder="e.g., California, London..."
                  value={mobileFilterLocation}
                  onChange={(e) => setMobileFilterLocation(e.target.value)}
                />
              </div>

              {/* Deal Type */}
              {(mobileEntityType === 'all' || mobileEntityType === 'deals') && (
                <div className="mobile-filter-row">
                  <label><FiLayers size={14} /> Deal Type</label>
                  <select value={mobileFilterDealType} onChange={(e) => setMobileFilterDealType(e.target.value)}>
                    <option value="">Any</option>
                    <option value="Seed">Seed</option>
                    <option value="Series A">Series A</option>
                    <option value="Series B">Series B</option>
                    <option value="Series C">Series C</option>
                    <option value="M&A">M&A</option>
                  </select>
                </div>
              )}

              {/* Funding Range */}
              {(mobileEntityType === 'all' || mobileEntityType === 'companies' || mobileEntityType === 'deals') && (
                <div className="mobile-filter-row">
                  <label><FiDollarSign size={14} /> Funding ($M)</label>
                  <div className="mobile-range-inputs">
                    <input type="number" placeholder="Min" value={mobileFilterMinRaised} onChange={(e) => setMobileFilterMinRaised(e.target.value)} />
                    <span>-</span>
                    <input type="number" placeholder="Max" value={mobileFilterMaxRaised} onChange={(e) => setMobileFilterMaxRaised(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Year Range */}
              <div className="mobile-filter-row">
                <label><FiCalendar size={14} /> Year</label>
                <div className="mobile-range-inputs">
                  <input type="number" placeholder="From" value={mobileFilterYearFrom} onChange={(e) => setMobileFilterYearFrom(e.target.value)} />
                  <span>-</span>
                  <input type="number" placeholder="To" value={mobileFilterYearTo} onChange={(e) => setMobileFilterYearTo(e.target.value)} />
                </div>
              </div>

              {/* Investor Type */}
              {(mobileEntityType === 'all' || mobileEntityType === 'investors') && (
                <div className="mobile-filter-row">
                  <label><FiUsers size={14} /> Investor Type</label>
                  <select value={mobileFilterInvestorType} onChange={(e) => setMobileFilterInvestorType(e.target.value)}>
                    <option value="">Any</option>
                    <option value="VC">Venture Capital</option>
                    <option value="PE">Private Equity</option>
                    <option value="Corporate">Corporate</option>
                    <option value="Angel">Angel</option>
                  </select>
                </div>
              )}

              {/* Clear Filters Button */}
              {(mobileFilterIndustry || mobileFilterLocation || mobileFilterDealType || mobileFilterMinRaised || mobileFilterMaxRaised || mobileFilterYearFrom || mobileFilterYearTo || mobileFilterInvestorType) && (
                <button className="mobile-clear-filters" onClick={clearMobileFilters}>
                  <FiX size={14} /> Clear All Filters
                </button>
              )}
            </div>
          )}

          {/* Active Filters Summary - Compact Tags */}
          {(mobileFilterIndustry || mobileFilterLocation || mobileFilterDealType || mobileFilterMinRaised || mobileFilterMaxRaised || mobileFilterYearFrom || mobileFilterYearTo || mobileFilterInvestorType) && !mobileShowFilters && (
            <div className="mobile-active-filters-compact">
              {mobileFilterIndustry && <span className="filter-chip">{mobileFilterIndustry}</span>}
              {mobileFilterLocation && <span className="filter-chip">{mobileFilterLocation}</span>}
              {mobileFilterDealType && <span className="filter-chip">{mobileFilterDealType}</span>}
              {mobileFilterMinRaised && <span className="filter-chip">${mobileFilterMinRaised}M+</span>}
              {mobileFilterYearFrom && <span className="filter-chip">{mobileFilterYearFrom}+</span>}
              {mobileFilterInvestorType && <span className="filter-chip">{mobileFilterInvestorType}</span>}
            </div>
          )}
        </div>

        {/* Loading State - Elegant */}
        {mobileSearchLoading && (
          <div className="mobile-loading-elegant">
            <svg viewBox="0 0 48 48" className="mobile-loading-constellation">
              <defs>
                <linearGradient id="loadingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1a2744" />
                  <stop offset="100%" stopColor="#2C3E50" />
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="22" fill="url(#loadingGrad)" className="loading-bg" />
              <g className="loading-particles">
                <circle cx="24" cy="14" r="2" fill="white" fillOpacity="0.9" />
                <circle cx="14" cy="28" r="1.5" fill="white" fillOpacity="0.7" />
                <circle cx="34" cy="28" r="1.5" fill="white" fillOpacity="0.7" />
              </g>
            </svg>
            <p className="loading-text">Searching...</p>
          </div>
        )}

        {/* Error State */}
        {mobileSearchError && (
          <div className="mobile-error-elegant">
            <FiAlertCircle size={20} />
            <p>{mobileSearchError}</p>
            <button onClick={() => setMobileSearchError(null)}>Dismiss</button>
          </div>
        )}

        {/* Results - Elegant Cards with Tiered Display */}
        {mobileSearchResults && !mobileSearchLoading && (
          <div className="mobile-results-elegant">
            <div className="mobile-results-header-elegant">
              <span className="results-count">{mobileSearchResults.total_found || mobileSearchResults.results?.length || 0} results</span>
              <span className="results-mode">{mobileSearchMode === 'hybrid' ? 'Hybrid' : mobileSearchMode === 'semantic' ? 'Semantic' : 'Lexical'}</span>
            </div>
            <div className="mobile-results-list-elegant">
              {/* Tier 1 - Direct matches */}
              {(mobileSearchResults.tiered_results?.tier1 || []).map((result, idx) => (
                <MobileResultCard key={`t1-${idx}`} result={result} tier={1} onSelect={setMobileSelectedResult} />
              ))}
              {/* Tier 2 - Related */}
              {(mobileSearchResults.tiered_results?.tier2 || []).map((result, idx) => (
                <MobileResultCard key={`t2-${idx}`} result={result} tier={2} onSelect={setMobileSelectedResult} />
              ))}
              {/* Tier 3 - Exploratory */}
              {(mobileSearchResults.tiered_results?.tier3 || []).map((result, idx) => (
                <MobileResultCard key={`t3-${idx}`} result={result} tier={3} onSelect={setMobileSelectedResult} />
              ))}
              {/* Fallback if no tiered results but has flat results */}
              {!mobileSearchResults.tiered_results && (mobileSearchResults.results || []).slice(0, 30).map((result, idx) => (
                <MobileResultCard key={`flat-${idx}`} result={result} tier={2} onSelect={setMobileSelectedResult} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State - Elegant Suggestions */}
        {!mobileSearchResults && !mobileSearchLoading && !mobileSearchError && (
          <div className="mobile-suggestions-elegant">
            <h3>Try searching for</h3>
            <div className="suggestion-chips">
              <button onClick={() => { setMobileSearchQuery('Mobile gaming companies'); handleMobileSearch(); }}>
                Mobile gaming companies
              </button>
              <button onClick={() => { setMobileSearchQuery('Series B deals'); handleMobileSearch(); }}>
                Series B deals
              </button>
              <button onClick={() => { setMobileSearchQuery('Gaming VCs'); handleMobileSearch(); }}>
                Gaming VCs
              </button>
            </div>
          </div>
        )}

        {/* Mobile Detail Popup */}
        {mobileSelectedResult && (
          <MobileDetailPopup
            result={mobileSelectedResult}
            onClose={() => setMobileSelectedResult(null)}
          />
        )}
      </div>
    );
  }

  // ============================================================================
  // ============================================================================
  // DESKTOP VIEW - Search-first experience with lazy data loading
  // ============================================================================
  return (
    <div className="landing-page" ref={scrollContainerRef}>
      {/* ===== HERO SEARCH SECTION ===== */}
      <section className="hero-search-section" ref={heroSectionRef}>
        {/* Scroll Progress Bar - right side */}
        {!dataLoadTriggered && (
          <div className="scroll-progress-container">
            <div className="scroll-progress-track">
              <div
                className="scroll-progress-fill"
                style={{ height: `${scrollProgress}%` }}
              />
            </div>
            <span className="scroll-progress-label">
              {scrollProgress < 100 ? `${Math.round(scrollProgress)}%` : 'Ready!'}
            </span>
          </div>
        )}

        {/* Branding */}
        <div className="hero-branding">
          <img src={isDarkMode ? transcendLogoLight : transcendLogoDark} alt="Transcend" className="hero-logo-img" />
          <p className="hero-tagline">AI-powered gaming industry intelligence</p>
        </div>

        {/* Database Stats */}
        {heroStats && (
          <div className="hero-stats-bar">
            <div className="hero-stat">
              <FiDatabase size={14} />
              <span className="hero-stat-value">{(heroStats.total_records || 0).toLocaleString()}</span>
              <span className="hero-stat-label">Records</span>
            </div>
            <div className="hero-stat">
              <FiBriefcase size={14} />
              <span className="hero-stat-value">{(heroStats.companies || 0).toLocaleString()}</span>
              <span className="hero-stat-label">Companies</span>
            </div>
            <div className="hero-stat">
              <FiDollarSign size={14} />
              <span className="hero-stat-value">{(heroStats.deals || 0).toLocaleString()}</span>
              <span className="hero-stat-label">Deals</span>
            </div>
            <div className="hero-stat">
              <FiUsers size={14} />
              <span className="hero-stat-value">{(heroStats.investors || 0).toLocaleString()}</span>
              <span className="hero-stat-label">Investors</span>
            </div>
            <div className="hero-stat">
              <FiMessageSquare size={14} />
              <span className="hero-stat-value">{heroStats.agent_count || 0}</span>
              <span className="hero-stat-label">Agents</span>
            </div>
          </div>
        )}

        {/* Hero Search Bar */}
        <div className="hero-search-container">
          <div className="hero-search-input-row">
            <FiSearch size={20} className="hero-search-icon" />
            <input
              type="text"
              placeholder="Search companies, deals, investors..."
              value={heroSearchQuery}
              onChange={(e) => setHeroSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !heroSearchLoading && handleHeroSearch()}
              disabled={heroSearchLoading}
            />
            {heroSearchQuery && !heroSearchLoading && (
              <button className="hero-clear-btn" onClick={() => { setHeroSearchQuery(''); setHeroSearchResults(null); }}>
                <FiX size={16} />
              </button>
            )}
            <button
              className="hero-search-btn"
              onClick={handleHeroSearch}
              disabled={heroSearchLoading || !heroSearchQuery.trim()}
            >
              {heroSearchLoading ? (
                <>
                  <div className="hero-spinner"></div>
                  Searching...
                </>
              ) : (
                <>
                  <FiZap size={16} />
                  Search
                </>
              )}
            </button>
          </div>

          {/* Controls: Agent Toggle + Mode Toggle + Entity Pills */}
          <div className="hero-search-controls">
            {/* Research Agent Bubble Toggle */}
            <div className="hero-agent-toggle">
              <ResearchAgentBubble
                isActive={useResearchAgent}
                onClick={() => setUseResearchAgent(!useResearchAgent)}
              />
            </div>

            {/* Search Mode Toggle (only visible when agent is OFF) */}
            {!useResearchAgent && (
              <div className="hero-mode-toggle">
                <button
                  className={heroSearchMode === 'strict' ? 'active' : ''}
                  onClick={() => setHeroSearchMode('strict')}
                >
                  Exact Match
                </button>
                <button
                  className={heroSearchMode === 'lexical' ? 'active' : ''}
                  onClick={() => setHeroSearchMode('lexical')}
                >
                  Lexical
                </button>
                <button
                  className={heroSearchMode === 'semantic' ? 'active' : ''}
                  onClick={() => setHeroSearchMode('semantic')}
                >
                  Semantic
                </button>
                <button
                  className={heroSearchMode === 'hybrid' ? 'active' : ''}
                  onClick={() => setHeroSearchMode('hybrid')}
                >
                  Hybrid
                </button>
                <div className="search-mode-info">
                  <FiInfo size={14} className="search-mode-info-icon" />
                  <div className="search-mode-tooltip">
                    <strong>How do you want to search?</strong>
                    <p><span className="mode-name">Exact Match:</span> Finds only results containing your exact words. Best when you know the specific company name, investor, or term you're looking for.</p>
                    <p><span className="mode-name">Lexical:</span> Smart keyword search. Finds results with your words even if they appear in different order or context. Great for exploring specific topics.</p>
                    <p><span className="mode-name">Semantic:</span> Understands meaning, not just words. Type "AI gaming companies" and find results even if they say "machine learning video game studios." Best for discovery.</p>
                    <p><span className="mode-name">Hybrid:</span> Best of both worlds. Combines keyword precision with meaning-based discovery. Recommended for most searches.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Common Searches Toggle */}
            {!useResearchAgent && (
              <button
                className={`common-searches-toggle ${commonSearchesExpanded ? 'expanded' : ''}`}
                onClick={() => setCommonSearchesExpanded(!commonSearchesExpanded)}
              >
                <FiFilter size={14} />
                <span>Common Searches</span>
                <FiChevronDown
                  size={14}
                  className={`toggle-chevron ${commonSearchesExpanded ? 'rotated' : ''}`}
                />
                {(selectedDealRounds.length > 0 || selectedTechCore.length > 0 || selectedPlatforms.length > 0) && (
                  <span className="filter-badge">
                    {selectedDealRounds.length + selectedTechCore.length + selectedPlatforms.length}
                  </span>
                )}
              </button>
            )}

            {/* Minimalistic Entity Filter Dropdown */}
            <div className="hero-entity-dropdown">
              <select
                value={heroEntityFilter}
                onChange={(e) => setHeroEntityFilter(e.target.value)}
                className="entity-select"
              >
                <option value="all">All Types</option>
                <option value="companies">Companies</option>
                <option value="deals">Deals</option>
                <option value="investors">Investors</option>
              </select>
              <FiChevronDown className="entity-select-icon" size={14} />
            </div>

            {!useResearchAgent && heroSearchMode === 'hybrid' && (
              <div className="hero-alpha-slider">
                <span>Lexical</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={heroSearchAlpha}
                  onChange={(e) => setHeroSearchAlpha(parseFloat(e.target.value))}
                />
                <span>Semantic</span>
              </div>
            )}
          </div>

          {/* Common Searches Expanded Panel */}
          {!useResearchAgent && commonSearchesExpanded && (
            <div className="common-searches-panel">
              {/* Deal Round Row */}
              <div className="common-search-row">
                <span className="common-search-label">Deal Round</span>
                <div className="common-search-chips">
                  {['Seed', 'Series A', 'Series B', 'Series C', 'Later Stage'].map(round => (
                    <button
                      key={round}
                      className={`common-chip ${selectedDealRounds.includes(round) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedDealRounds(prev =>
                          prev.includes(round)
                            ? prev.filter(r => r !== round)
                            : [...prev, round]
                        );
                      }}
                    >
                      {round}
                    </button>
                  ))}
                  <div className="common-chip-info">
                    <FiInfo size={12} />
                    <div className="common-chip-tooltip">
                      Filters by financing round type. Companies: first_financing_deal_type. Deals: deal_type_2.
                    </div>
                  </div>
                </div>
              </div>

              {/* Technology Core Row */}
              <div className="common-search-row">
                <span className="common-search-label">Technology Core</span>
                <div className="common-search-chips">
                  {[
                    { key: 'AI/ML', label: 'AI/ML' },
                    { key: 'XR/MR/AR/VR', label: 'XR/VR/AR' },
                    { key: 'Blockchain/DeFi/Web3', label: 'Blockchain/Web3' }
                  ].map(tech => (
                    <button
                      key={tech.key}
                      className={`common-chip ${selectedTechCore.includes(tech.key) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTechCore(prev =>
                          prev.includes(tech.key)
                            ? prev.filter(t => t !== tech.key)
                            : [...prev, tech.key]
                        );
                      }}
                    >
                      {tech.label}
                    </button>
                  ))}
                  <div className="common-chip-info">
                    <FiInfo size={12} />
                    <div className="common-chip-tooltip">
                      Filter by transcend_technology_core column. AI/ML: 520, XR/VR/AR: 506, Blockchain: 1,408 companies.
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Semantics Row (Gaming Platforms - Game Studios only) */}
              <div className="common-search-row">
                <span className="common-search-label">Platform (Studios)</span>
                <div className="common-search-chips">
                  {['Mobile', 'PC', 'Console', 'VR/AR', 'Web'].map(platform => (
                    <button
                      key={platform}
                      className={`common-chip ${selectedPlatforms.includes(platform) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedPlatforms(prev =>
                          prev.includes(platform)
                            ? prev.filter(p => p !== platform)
                            : [...prev, platform]
                        );
                      }}
                    >
                      {platform}
                    </button>
                  ))}
                  <div className="common-chip-info">
                    <FiInfo size={12} />
                    <div className="common-chip-tooltip">
                      Gaming platform semantic search. Auto-filters to Game Studios (transcend_segment). Uses description + keywords for matching.
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Filters Summary */}
              {(selectedDealRounds.length > 0 || selectedTechCore.length > 0 || selectedPlatforms.length > 0) && (
                <div className="active-filters-summary">
                  <span className="active-filters-label">Active Filters:</span>
                  <div className="active-filters-tags">
                    {selectedDealRounds.map(round => (
                      <span key={round} className="active-filter-tag round">
                        {round}
                        <button onClick={() => setSelectedDealRounds(prev => prev.filter(r => r !== round))}>×</button>
                      </span>
                    ))}
                    {selectedTechCore.map(tech => (
                      <span key={tech} className="active-filter-tag tech">
                        {tech === 'XR/MR/AR/VR' ? 'XR/VR/AR' : tech === 'Blockchain/DeFi/Web3' ? 'Blockchain/Web3' : tech}
                        <button onClick={() => setSelectedTechCore(prev => prev.filter(t => t !== tech))}>×</button>
                      </span>
                    ))}
                    {selectedPlatforms.map(platform => (
                      <span key={platform} className="active-filter-tag platform">
                        {platform}
                        <button onClick={() => setSelectedPlatforms(prev => prev.filter(p => p !== platform))}>×</button>
                      </span>
                    ))}
                  </div>
                  <button
                    className="common-search-clear"
                    onClick={() => {
                      setSelectedDealRounds([]);
                      setSelectedTechCore([]);
                      setSelectedPlatforms([]);
                    }}
                  >
                    <FiX size={12} />
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Agent Console + Results Layout */}
        {(heroSearchLoading || heroSearchResults || agentConsoleVisible) && (
          <div className="hero-content-layout">
            {/* Left: Results Area */}
            <div className="hero-results-area">
              {heroSearchLoading && (
                <div className="hero-loading">
                  <div className="hero-loading-spinner"></div>
                  <p>Searching across {heroEntityFilter === 'all' ? 'all entities' : heroEntityFilter}...</p>
                </div>
              )}

              {heroSearchError && (
                <div className="hero-error">
                  <FiAlertCircle size={20} />
                  <span>{heroSearchError.message}</span>
                </div>
              )}

              {heroSearchResults && !heroSearchLoading && (
          <div className="hero-results-container">
            <div className="hero-results-header">
              <div className="hero-results-header-left">
                <span className="hero-results-count">
                  {heroSearchResults.total_found || heroSearchResults.results?.length || 0} Results
                </span>
                {heroSearchResults.tiered_results && (
                  <div className="hero-tier-badges">
                    {heroSearchResults.tiered_results.tier1?.length > 0 && (
                      <span className="tier-badge tier1">{heroSearchResults.tiered_results.tier1.length} Direct</span>
                    )}
                    {heroSearchResults.tiered_results.tier2?.length > 0 && (
                      <span className="tier-badge tier2">{heroSearchResults.tiered_results.tier2.length} Related</span>
                    )}
                    {heroSearchResults.tiered_results.tier3?.length > 0 && (
                      <span className="tier-badge tier3">{heroSearchResults.tiered_results.tier3.length} Exploratory</span>
                    )}
                  </div>
                )}
              </div>
              {/* View Mode Toggle + Tabulate + Debug */}
              <div className="hero-results-header-right">
                <div className="hero-results-view-toggle">
                  <button
                    className={`view-toggle-btn ${heroResultsViewMode === 'cards' ? 'active' : ''}`}
                    onClick={() => setHeroResultsViewMode('cards')}
                    title="Card View"
                  >
                    <FiGrid size={16} />
                  </button>
                  <button
                    className={`view-toggle-btn ${heroResultsViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setHeroResultsViewMode('table')}
                    title="Table View"
                  >
                    <FiList size={16} />
                  </button>
                </div>
                <button
                  className="tabulate-btn"
                  onClick={() => setShowTabulateModal(true)}
                  title="Open full data explorer"
                >
                  <FiMaximize2 size={14} />
                  <span>Tabulate</span>
                </button>
                {useResearchAgent && heroSearchResults && onOpenChat && (
                  <button
                    className="chat-with-agent-btn"
                    onClick={() => onOpenChat(heroSearchResults)}
                    title="Continue conversation with the research agent"
                  >
                    {/* Research Agent constellation icon */}
                    <svg viewBox="0 0 24 24" width="16" height="16" className="research-agent-icon">
                      <defs>
                        <linearGradient id="chatBtnGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="currentColor" />
                          <stop offset="100%" stopColor="currentColor" stopOpacity="0.7" />
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="10" fill="url(#chatBtnGrad)" opacity="0.15" />
                      <line x1="8" y1="8" x2="12" y2="11" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" />
                      <line x1="12" y1="11" x2="16" y2="8" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" />
                      <line x1="12" y1="11" x2="9" y2="15" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" />
                      <line x1="12" y1="11" x2="15" y2="15" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" />
                      <circle cx="12" cy="11" r="2.5" fill="currentColor" />
                      <circle cx="8" cy="8" r="1.5" fill="currentColor" opacity="0.8" />
                      <circle cx="16" cy="8" r="1.5" fill="currentColor" opacity="0.8" />
                      <circle cx="9" cy="15" r="1.2" fill="currentColor" opacity="0.6" />
                      <circle cx="15" cy="15" r="1.2" fill="currentColor" opacity="0.6" />
                    </svg>
                    <span>Chat</span>
                  </button>
                )}
                {heroSearchDebugInfo && (
                  <button
                    className={`header-debug-btn ${!heroDebugCollapsed ? 'active' : ''}`}
                    onClick={() => setHeroDebugCollapsed(!heroDebugCollapsed)}
                    title="Toggle Debug Info"
                  >
                    <FiInfo size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Debug Info Panel - inline below header when expanded */}
            {heroSearchDebugInfo && !heroDebugCollapsed && (
              <div className="hero-debug-inline">
                <div className="debug-row"><strong>Mode:</strong> {heroSearchDebugInfo.mode || heroSearchMode}</div>
                {heroSearchDebugInfo.pinecone_available !== undefined && (
                  <div className="debug-row"><strong>Pinecone:</strong> {heroSearchDebugInfo.pinecone_available ? '✓' : '✗'}</div>
                )}
                {heroSearchDebugInfo.index_name && (
                  <div className="debug-row"><strong>Index:</strong> {heroSearchDebugInfo.index_name}</div>
                )}
                {heroSearchDebugInfo.pinecone_matches !== undefined && (
                  <div className="debug-row"><strong>VDB:</strong> {heroSearchDebugInfo.pinecone_matches}</div>
                )}
                {heroSearchDebugInfo.companies_found !== undefined && (
                  <div className="debug-row"><strong>Companies:</strong> {heroSearchDebugInfo.companies_found}</div>
                )}
                {heroSearchDebugInfo.deals_found !== undefined && (
                  <div className="debug-row"><strong>Deals:</strong> {heroSearchDebugInfo.deals_found}</div>
                )}
                {heroSearchDebugInfo.investors_found !== undefined && (
                  <div className="debug-row"><strong>Investors:</strong> {heroSearchDebugInfo.investors_found}</div>
                )}
                {heroSearchDebugInfo.error && (
                  <div className="debug-row debug-error"><strong>Error:</strong> {heroSearchDebugInfo.error}</div>
                )}
              </div>
            )}

            {/* Table View - Simple overview (use Tabulate for sorting/filtering) */}
            {heroResultsViewMode === 'table' ? (
              <div className="hero-results-table-container">
                <table className="hero-results-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Industry/Type</th>
                      <th>Location</th>
                      <th>Details</th>
                      {heroSearchMode !== 'strict' && heroSearchMode !== 'lexical' && <th>Score</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(heroSearchResults.tiered_results
                      ? [
                          ...(heroSearchResults.tiered_results.tier1 || []),
                          ...(heroSearchResults.tiered_results.tier2 || []),
                          ...(heroSearchResults.tiered_results.tier3 || [])
                        ]
                      : (heroSearchResults.results || [])
                    ).slice(0, 30).map((result, idx) => {
                      const entityType = result.entity_type || result.type || 'company';
                      const tier = heroSearchResults.tiered_results?.tier1?.includes(result) ? 1
                        : heroSearchResults.tiered_results?.tier2?.includes(result) ? 2
                        : heroSearchResults.tiered_results?.tier3?.includes(result) ? 3 : null;
                      return (
                        <tr
                          key={result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || idx}
                          className={`table-row ${tier ? `tier${tier}` : ''}`}
                        >
                          <td><span className={`entity-badge-mini ${entityType}`}>{entityType}</span></td>
                          <td
                            className="table-name clickable"
                            onClick={() => handleHeroResultClick(result)}
                          >
                            {result.name || result.companies || 'Unknown'}
                          </td>
                          <td>{result.primary_industry || result.deal_type || result.investor_type || '-'}</td>
                          <td>{result.hq_location || '-'}</td>
                          <td className="table-details">
                            {entityType === 'company' && (result.total_raised || result.employees) && (
                              <>{result.total_raised || ''} {result.employees ? `• ${result.employees} emp` : ''}</>
                            )}
                            {entityType === 'deal' && (
                              <>{result.deal_size_usd ? `$${result.deal_size_usd.toFixed(1)}M` : ''} {result.deal_date ? `• ${formatDate(result.deal_date)}` : ''}</>
                            )}
                            {entityType === 'investor' && (
                              <>{result.investments_count ? `${result.investments_count} inv` : ''} {result.aum ? `• AUM: $${(result.aum/1000000000).toFixed(1)}B` : ''}</>
                            )}
                          </td>
                          {heroSearchMode !== 'strict' && heroSearchMode !== 'lexical' && (
                            <td className="table-score">{result.score ? `${(result.score * 100).toFixed(0)}%` : '-'}</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="table-footer-hint">
                  Click name to view details • Use <strong>Tabulate</strong> for sorting & filtering
                </div>
              </div>
            ) : (
              /* Card View - Tiered Results */
              <>
                {heroSearchResults.tiered_results ? (
                  <div className="hero-tiered-results">
                    {/* Tier 1: Direct Matches */}
                    {heroSearchResults.tiered_results.tier1?.length > 0 && (
                      <div className="hero-tier-section">
                        <div className="hero-tier-header tier1">
                          <span className="tier-indicator">◆</span>
                          <span className="tier-label">Direct Matches</span>
                          <span className="tier-count">{heroSearchResults.tiered_results.tier1.length}</span>
                        </div>
                        <div className="hero-results-grid">
                          {heroSearchResults.tiered_results.tier1.slice(0, 10).map((result, idx) => (
                            <HeroResultCard
                              key={result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || `t1-${idx}`}
                              result={result}
                              tier={1}
                              onClick={() => handleHeroResultClick(result)}
                              showScore={heroSearchMode !== 'strict'}
                              searchMode={heroSearchMode}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tier 2: Related */}
                    {heroSearchResults.tiered_results.tier2?.length > 0 && (
                      <div className="hero-tier-section">
                        <div className="hero-tier-header tier2">
                          <span className="tier-indicator">◈</span>
                          <span className="tier-label">Related</span>
                          <span className="tier-count">{heroSearchResults.tiered_results.tier2.length}</span>
                        </div>
                        <div className="hero-results-grid">
                          {heroSearchResults.tiered_results.tier2.slice(0, 8).map((result, idx) => (
                            <HeroResultCard
                              key={result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || `t2-${idx}`}
                              result={result}
                              tier={2}
                              onClick={() => handleHeroResultClick(result)}
                              showScore={heroSearchMode !== 'strict'}
                              searchMode={heroSearchMode}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tier 3: Exploratory */}
                    {heroSearchResults.tiered_results.tier3?.length > 0 && (
                      <div className="hero-tier-section">
                        <div className="hero-tier-header tier3">
                          <span className="tier-indicator">○</span>
                          <span className="tier-label">Exploratory</span>
                          <span className="tier-count">{heroSearchResults.tiered_results.tier3.length}</span>
                        </div>
                        <div className="hero-results-grid">
                          {heroSearchResults.tiered_results.tier3.slice(0, 6).map((result, idx) => (
                            <HeroResultCard
                              key={result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || `t3-${idx}`}
                              result={result}
                              tier={3}
                              onClick={() => handleHeroResultClick(result)}
                              showScore={heroSearchMode !== 'strict'}
                              searchMode={heroSearchMode}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Fallback: Flat results grid (no tiering available) */
                  <div className="hero-results-grid">
                    {(heroSearchResults.results || []).slice(0, 20).map((result, idx) => (
                      <HeroResultCard
                        key={result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || idx}
                        result={result}
                        onClick={() => handleHeroResultClick(result)}
                        showScore={heroSearchMode !== 'strict'}
                        searchMode={heroSearchMode}
                      />
                    ))}
                  </div>
                )}

                {(heroSearchResults.results?.length || 0) > 20 && (
                  <div className="hero-results-more">
                    Showing 20 of {heroSearchResults.results.length} results
                  </div>
                )}
              </>
            )}
          </div>
              )}
            </div>

            {/* Right: Agent Console - Retro Terminal */}
            {agentConsoleVisible && (
              <div className="agent-console">
                <div className="console-header">
                  <div className="console-title">
                    <span className="console-icon">▸</span>
                    <span>TRANSCEND_AGENT</span>
                  </div>
                  <div className="console-controls">
                    <button
                      className="console-close"
                      onClick={() => setAgentConsoleVisible(false)}
                      title="Close console"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="console-body" ref={agentConsoleRef}>
                  {agentConsoleLogs.map((log) => (
                    <div key={log.id} className={`console-line ${log.status}`}>
                      <span className="console-timestamp">[{log.timestamp}]</span>
                      <span className={`console-prefix ${log.type}`}>
                        {log.type === 'init' && '>>'}
                        {log.type === 'parse' && '::'}
                        {log.type === 'analyze' && '~~'}
                        {log.type === 'target' && '->'}
                        {log.type === 'model' && '◈◈'}
                        {log.type === 'tools' && '++'}
                        {log.type === 'sql' && 'db'}
                        {log.type === 'vdb' && 'vx'}
                        {log.type === 'lookup' && '[]'}
                        {log.type === 'tier' && '##'}
                        {log.type === 'result' && '>>'}
                        {log.type === 'breakdown' && '  '}
                        {log.type === 'complete' && '◉◉'}
                        {log.type === 'error' && '!!'}
                      </span>
                      <span className="console-message">{log.message}</span>
                      <span className={`console-status ${log.status}`}>
                        {log.status === 'running' && <span className="status-spinner">◠</span>}
                        {log.status === 'done' && '·'}
                        {log.status === 'success' && '■'}
                        {log.status === 'error' && '×'}
                      </span>
                    </div>
                  ))}
                  {heroSearchLoading && agentConsoleLogs.length > 0 && (
                    <div className="console-cursor">█</div>
                  )}
                </div>
                <div className="console-footer">
                  <span className="console-status-text">
                    {heroSearchLoading ? 'EXEC' : 'IDLE'}
                  </span>
                  <span className="console-model">GEMINI_3_FLASH</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Minimalistic Footer Scroll Indicator */}
        {!dataLoadTriggered && (
          <div className="scroll-footer">
            <FiChevronDown size={16} className="bounce" />
            <span>Data View</span>
          </div>
        )}
      </section>

      {/* ===== DATA VIEW SECTION (Lazy Loaded) ===== */}
      <section className="data-explorer-section" ref={dataExplorerRef}>
        {!dataLoadTriggered ? (
          <div className="data-explorer-placeholder">
            <FiDatabase size={48} />
            <h2>Data View</h2>
            <p>Full dataset loads when you scroll here</p>
            <button
              className="load-data-btn"
              onClick={() => { setDataLoadTriggered(true); fetchInitialData(); }}
            >
              Load Data Now
            </button>
          </div>
        ) : (
          <>
          <div className="landing-container">
            {/* Data View Header */}
            <div className="data-explorer-header">
              <h2>Data View</h2>
              <p className="data-explorer-stats">
                {allCompanies.length + allDeals.length + allInvestors.length} Total Records
                {backgroundLoading && ' (Loading more...)'}
              </p>
            </div>

            {/* Entity Tabs */}
        <div className="entity-tabs">
          <button
            className={`entity-tab ${activeTab === 'deals' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('deals')}
          >
            <FiTrendingUp size={18} />
            Deals
            <span className="tab-count">{deals.length}</span>
          </button>
          <button
            className={`entity-tab ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('companies')}
          >
            <FiGrid size={18} />
            Companies
            <span className="tab-count">{allCompanies.length > 0 ? allCompanies.length : companies.length}</span>
          </button>
          <button
            className={`entity-tab ${activeTab === 'investors' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('investors')}
          >
            <FiUsers size={18} />
            Investors
            <span className="tab-count">{allInvestors.length > 0 ? allInvestors.length : investors.length}</span>
          </button>
        </div>

        {/* Search Section - Minimalistic Design */}
        <div className="search-filter-section" style={{ background: 'transparent', boxShadow: 'none', padding: '0' }}>
          {/* Clean Search Bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'white',
            borderRadius: '12px',
            padding: '8px 16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e5e7eb'
          }}>
            <FiSearch size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUnifiedSearch()}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '15px',
                padding: '8px 0',
                background: 'transparent'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <FiX size={16} />
              </button>
            )}
            <button
              onClick={handleUnifiedSearch}
              disabled={!searchQuery.trim() || searchLoading}
              style={{
                background: '#2C3E50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: searchQuery.trim() && !searchLoading ? 'pointer' : 'not-allowed',
                opacity: searchQuery.trim() && !searchLoading ? 1 : 0.5,
                transition: 'all 0.2s'
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Subtle Options Row - Minimized */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '12px',
            padding: '0 4px'
          }}>
            {/* Left: Active filters indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {getActiveFilters().length > 0 && (
                <span
                  onClick={() => setShowFilterModal(true)}
                  style={{
                    fontSize: '13px',
                    color: '#2C3E50',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <FiFilter size={14} />
                  {getActiveFilters().length} filter{getActiveFilters().length > 1 ? 's' : ''} active
                </span>
              )}
              {(searchQuery || getActiveFilters().length > 0) && (
                <button
                  onClick={clearFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: '13px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Right: Toggle buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: showAdvancedSearch ? '#2C3E50' : '#9ca3af',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px'
                }}
              >
                <FiSliders size={14} />
                Options
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  background: showFilters ? '#2C3E50' : 'none',
                  color: showFilters ? 'white' : '#9ca3af',
                  border: showFilters ? 'none' : '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  transition: 'all 0.2s'
                }}
              >
                <FiFilter size={14} />
                Filters
              </button>
              <button
                onClick={() => {
                  if (activeTab === 'deals') exportToCSV(deals, 'transcend_deals');
                  else if (activeTab === 'companies') exportToCSV(companies, 'transcend_companies');
                  else if (activeTab === 'investors') exportToCSV(investors, 'transcend_investors');
                }}
                style={{
                  background: 'none',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  color: '#6b7280',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px'
                }}
                title="Export to CSV"
              >
                <FiDownload size={14} />
              </button>
            </div>
          </div>

          {/* Advanced Search Options - Collapsible */}
          {showAdvancedSearch && (
            <div style={{
              marginTop: '12px',
              padding: '16px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Search Mode:</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['strict', 'semantic', 'hybrid'].map(mode => (
                    <button
                      key={mode}
                      onClick={() => setSearchMode(mode)}
                      style={{
                        background: searchMode === mode ? '#2C3E50' : 'white',
                        color: searchMode === mode ? 'white' : '#374151',
                        border: '1px solid',
                        borderColor: searchMode === mode ? '#2C3E50' : '#d1d5db',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                        transition: 'all 0.2s'
                      }}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              {searchMode === 'hybrid' && (
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Semantic</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={searchAlpha}
                    onChange={(e) => setSearchAlpha(parseFloat(e.target.value))}
                    style={{ flex: 1, maxWidth: '200px' }}
                  />
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Keyword</span>
                </div>
              )}
            </div>
          )}


          {/* Less-Likely Results Popup */}
          {showLessLikelyPrompt && (
            <div style={{
              marginTop: '12px',
              padding: '12px 16px',
              background: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #fbbf24',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: '13px', color: '#92400e' }}>No exact matches. Show related results?</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleShowLessLikely} style={{ background: '#2C3E50', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>Yes</button>
                <button onClick={() => setShowLessLikelyPrompt(false)} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>No</button>
              </div>
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <div className="filters-container">
              {activeTab === 'deals' && (
                <div className="filter-row">
                  <MultiSelectDropdown
                    label="Deal Type"
                    filterType="dealType"
                    options={uniqueValues.dealTypes}
                    selectedValues={dealFilters.dealType}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    backgroundLoading={backgroundLoading}
                    dropdownRef={dropdownRef}
                    onClearSelection={handleClearSelection}
                    onToggleCheckbox={toggleCheckbox}
                  />

                  <MultiSelectDropdown
                    label="Deal Type 2"
                    filterType="dealType2"
                    options={uniqueValues.dealTypes2}
                    selectedValues={dealFilters.dealType2}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    backgroundLoading={backgroundLoading}
                    dropdownRef={dropdownRef}
                    onClearSelection={handleClearSelection}
                    onToggleCheckbox={toggleCheckbox}
                  />

                  <MultiSelectDropdown
                    label="Financing Status"
                    filterType="financingStatus"
                    options={uniqueValues.financingStatuses}
                    selectedValues={dealFilters.financingStatus}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    backgroundLoading={backgroundLoading}
                    dropdownRef={dropdownRef}
                    onClearSelection={handleClearSelection}
                    onToggleCheckbox={toggleCheckbox}
                  />

                  <MultiSelectDropdown
                    label="Investors"
                    filterType="investors"
                    options={uniqueValues.dealInvestors}
                    selectedValues={dealFilters.investors}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    backgroundLoading={backgroundLoading}
                    dropdownRef={dropdownRef}
                    onClearSelection={handleClearSelection}
                    onToggleCheckbox={toggleCheckbox}
                  />

                  <div className="filter-item">
                    <label>Min Size ($M)</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={dealFilters.minSize}
                      onChange={(e) => setDealFilters({...dealFilters, minSize: e.target.value})}
                    />
                  </div>

                  <div className="filter-item">
                    <label>Max Size ($M)</label>
                    <input
                      type="number"
                      placeholder="1000"
                      value={dealFilters.maxSize}
                      onChange={(e) => setDealFilters({...dealFilters, maxSize: e.target.value})}
                    />
                  </div>

                  <div className="filter-item">
                    <label>Date From</label>
                    <input
                      type="date"
                      value={dealFilters.dateFrom}
                      onChange={(e) => setDealFilters({...dealFilters, dateFrom: e.target.value})}
                    />
                  </div>

                  <div className="filter-item">
                    <label>Date To</label>
                    <input
                      type="date"
                      value={dealFilters.dateTo}
                      onChange={(e) => setDealFilters({...dealFilters, dateTo: e.target.value})}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'companies' && (
                <div className="filter-row">
                  <MultiSelectDropdown
                    label="Industry"
                    filterType="industry"
                    options={uniqueValues.industries}
                    selectedValues={companyFilters.industry}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    backgroundLoading={backgroundLoading}
                    dropdownRef={dropdownRef}
                    onClearSelection={handleClearSelection}
                    onToggleCheckbox={toggleCheckbox}
                  />

                  <HierarchicalLocationDropdown
                    label="Location"
                    options={uniqueValues.locations}
                    selectedValues={companyFilters.location}
                    onSelectionChange={(newValues) => setCompanyFilters({...companyFilters, location: newValues})}
                    isDisabled={backgroundLoading}
                    isOpen={openDropdown === 'location'}
                    onToggle={() => setOpenDropdown(openDropdown === 'location' ? null : 'location')}
                  />

                  <div className="filter-item">
                    <label>Min Revenue ($M)</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={companyFilters.minRevenue}
                      onChange={(e) => setCompanyFilters({...companyFilters, minRevenue: e.target.value})}
                    />
                  </div>

                  <div className="filter-item">
                    <label>Max Revenue ($M)</label>
                    <input
                      type="number"
                      placeholder="1000"
                      value={companyFilters.maxRevenue}
                      onChange={(e) => setCompanyFilters({...companyFilters, maxRevenue: e.target.value})}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'investors' && (
                <div className="filter-row">
                  <MultiSelectDropdown
                    label="Investor Type"
                    filterType="investorType"
                    options={uniqueValues.investorTypes}
                    selectedValues={investorFilters.investorType}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    backgroundLoading={backgroundLoading}
                    dropdownRef={dropdownRef}
                    onClearSelection={handleClearSelection}
                    onToggleCheckbox={toggleCheckbox}
                  />

                  <div className="filter-item">
                    <label>Min AUM ($B)</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={investorFilters.minAUM}
                      onChange={(e) => setInvestorFilters({...investorFilters, minAUM: e.target.value})}
                    />
                  </div>

                  <div className="filter-item">
                    <label>Max AUM ($B)</label>
                    <input
                      type="number"
                      placeholder="10000"
                      value={investorFilters.maxAUM}
                      onChange={(e) => setInvestorFilters({...investorFilters, maxAUM: e.target.value})}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floating Loading Notification Badge - Top Right */}
        {(backgroundLoading || showFiltersReady) && (
          <div
            className={`loading-notification-badge ${loadingBadgeExpanded ? 'expanded' : ''} ${showFiltersReady ? 'complete' : ''}`}
            onClick={() => setLoadingBadgeExpanded(!loadingBadgeExpanded)}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 1000,
              background: showFiltersReady
                ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                : 'linear-gradient(135deg, #2C3E50 0%, #34495e 100%)',
              color: 'white',
              borderRadius: loadingBadgeExpanded ? '12px' : '24px',
              padding: loadingBadgeExpanded ? '16px 20px' : '10px 16px',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: loadingBadgeExpanded ? '280px' : 'auto',
              maxWidth: '320px'
            }}
          >
            {!loadingBadgeExpanded ? (
              <>
                {backgroundLoading ? (
                  <>
                    <div className="subtle-game-loader" style={{ transform: 'scale(0.7)' }}>
                      <div className="t-pixel"></div>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>Loading Data...</span>
                  </>
                ) : (
                  <>
                    <FiCheckCircle size={16} />
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>Data Ready</span>
                  </>
                )}
              </>
            ) : (
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700 }}>
                    {backgroundLoading ? 'Loading Data...' : '✓ All Data Loaded'}
                  </span>
                  <FiX size={16} style={{ opacity: 0.7 }} />
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  <div>Deals: {loadingProgress.deals.toLocaleString()}</div>
                  <div>Companies: {loadingProgress.companies.toLocaleString()}</div>
                  <div>Investors: {loadingProgress.investors.toLocaleString()}</div>
                </div>
                {showFiltersReady && (
                  <div style={{ fontSize: '11px', marginTop: '8px', opacity: 0.85 }}>
                    Filters and search ready to use!
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Minimized Agent Badge - Floating on right side */}
        {agentMinimized && (
          <div
            className={`agent-minimized-badge ${aiSearchLoading ? 'loading' : ''} ${aiSearchResults && !aiSearchLoading ? 'ready' : ''}`}
            onClick={() => { setAgentMinimized(false); setShowAISearch(true); }}
            title={aiSearchLoading ? 'Research in progress...' : aiSearchResults ? 'Results ready - Click to view' : 'Research Agent'}
          >
            <div className="minimized-badge-icon">
              <span className="badge-t">T</span>
              {aiSearchLoading && <div className="badge-spinner"></div>}
            </div>
            <div className="minimized-badge-info">
              <span className="badge-title">Agent</span>
              {aiSearchLoading && <span className="badge-status loading">Researching...</span>}
              {aiSearchResults && !aiSearchLoading && (
                <span className="badge-status ready">
                  {aiSearchResults.total_found || 0} results ✓
                </span>
              )}
            </div>
            {aiSearchResults && !aiSearchLoading && (
              <div className="badge-notification-dot"></div>
            )}
          </div>
        )}

        {/* Research Agent Panel */}
        {showAISearch && !agentMinimized && (
          <div className="ai-search-modal-overlay" onClick={(e) => e.target === e.currentTarget && setAgentMinimized(true)}>
            <div className={`ai-search-modal ${agentPanelExpanded ? 'expanded' : ''}`}>
              <div className="ai-search-modal-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  {/* Animated Transcend T Logo */}
                  <div className={`transcend-logo-animated ${aiSearchLoading ? 'searching' : ''} ${aiSearchResults ? 'complete' : ''}`}>
                    <span className="logo-letter">T</span>
                    <div className="logo-glow"></div>
                  </div>
                  <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: '8px'}}>
                    Research Agent
                  </h2>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  {/* Analytics Toggle */}
                  {aiSearchResults && (
                    <button
                      className={`header-action-btn ${showAgentAnalytics ? 'active' : ''}`}
                      onClick={() => setShowAgentAnalytics(!showAgentAnalytics)}
                      title="Toggle Analytics"
                    >
                      <FiPieChart size={18} />
                    </button>
                  )}
                  {/* Expand/Collapse Toggle */}
                  <button
                    className="header-action-btn"
                    onClick={() => setAgentPanelExpanded(!agentPanelExpanded)}
                    title={agentPanelExpanded ? 'Collapse Panel' : 'Expand Panel'}
                  >
                    {agentPanelExpanded ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
                  </button>
                  {/* Minimize Button - Slide to minimized badge */}
                  <button
                    className="header-action-btn"
                    onClick={() => { setAgentMinimized(true); setShowAISearch(false); }}
                    title="Minimize to badge"
                  >
                    <FiMinus size={18} />
                  </button>
                  {/* Close Button - Close completely */}
                  <button className="modal-close-btn" onClick={() => setShowAISearch(false)} title="Close panel">
                    <FiX size={20} />
                  </button>
                </div>
              </div>
              <div className="ai-search-modal-content">
              <div className="ai-search-input-group">
                <FiSearch className="ai-search-icon" />
                <input
                  type="text"
                  placeholder="Describe what you're looking for in natural language..."
                  value={aiSearchQuery}
                  onChange={(e) => setAISearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !aiSearchLoading && handleAISearch()}
                  className="ai-search-input"
                  disabled={aiSearchLoading}
                />
                <button onClick={handleAISearch} className="ai-search-btn" disabled={aiSearchLoading}>
                  {aiSearchLoading ? 'Researching...' : 'Research'}
                </button>
              </div>

              {/* AI Agent Logs / Thinking Panel - Collapsible */}
              {(aiSearchLoading || aiSearchProgress.length > 0 || aiSearchResults) && (
                <div style={{marginTop: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden'}}>
                  {/* Header with toggle */}
                  <div
                    onClick={() => setShowThinking(!showThinking)}
                    style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #1e3a6e 0%, #2a5298 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                      {/* Animated T logo during loading */}
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: aiSearchLoading ? 'linear-gradient(135deg, #4a7bc7 0%, #2a5298 50%, #1e3a6e 100%)' : 'rgba(255,255,255,0.15)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '800',
                        fontSize: '14px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        animation: aiSearchLoading ? 'pulse 1.5s ease-in-out infinite' : 'none',
                        boxShadow: aiSearchLoading ? '0 0 12px rgba(42, 82, 152, 0.5)' : 'none'
                      }}>
                        T
                      </div>
                      <span style={{fontWeight: '600', fontSize: '14px'}}>Agent Logs</span>
                      {aiSearchLoading && (
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 8px',
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: '10px',
                          fontWeight: '600',
                          letterSpacing: '0.5px'
                        }}>
                          PROCESSING
                        </span>
                      )}
                      {aiSearchResults && !aiSearchLoading && (
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 8px',
                          background: 'rgba(34, 197, 94, 0.3)',
                          borderRadius: '10px',
                          fontWeight: '600',
                          color: '#86efac'
                        }}>
                          COMPLETE
                        </span>
                      )}
                    </div>
                    <span style={{fontSize: '16px', opacity: 0.8}}>{showThinking ? '▼' : '▶'}</span>
                  </div>

                  {/* Logs content - collapsible */}
                  {showThinking && (
                    <div style={{padding: '16px', background: '#0f172a', maxHeight: '400px', overflowY: 'auto'}}>
                      {aiSearchProgress.map((step, idx) => (
                        <div key={idx} style={{
                          marginBottom: '12px',
                          paddingBottom: '12px',
                          borderBottom: idx < aiSearchProgress.length - 1 ? '1px solid #1e293b' : 'none',
                          animation: 'fadeIn 0.3s ease-in'
                        }}>
                          {/* Main message */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            marginBottom: '4px'
                          }}>
                            <div style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: step.type === 'error' ? '#ef4444' :
                                         step.type === 'success' ? '#22c55e' :
                                         step.type === 'init' ? '#8b5cf6' : '#2a5298',
                              marginTop: '6px',
                              flexShrink: 0,
                              boxShadow: '0 0 8px currentColor'
                            }} />
                            <div style={{flex: 1}}>
                              <div style={{
                                color: step.type === 'error' ? '#fca5a5' :
                                       step.type === 'success' ? '#86efac' :
                                       step.type === 'init' ? '#c4b5fd' : '#93c5fd',
                                fontSize: '13px',
                                fontWeight: '500',
                                fontFamily: 'monospace'
                              }}>
                                [{step.timestamp}] {step.message}
                              </div>
                              {/* Detail text */}
                              {step.detail && (
                                <div style={{
                                  color: '#94a3b8',
                                  fontSize: '12px',
                                  marginTop: '4px',
                                  paddingLeft: '8px',
                                  borderLeft: '2px solid #334155',
                                  fontFamily: 'monospace'
                                }}>
                                  {step.detail}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Current processing indicator */}
                      {aiSearchLoading && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: '#1e293b',
                          borderRadius: '6px'
                        }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            background: 'linear-gradient(135deg, #4a7bc7 0%, #2a5298 100%)',
                            borderRadius: '3px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '800',
                            fontSize: '11px',
                            color: 'white',
                            animation: 'pulse 1s ease-in-out infinite',
                            boxShadow: '0 0 12px rgba(42, 82, 152, 0.6)'
                          }}>
                            T
                          </div>
                          <span style={{color: '#93c5fd', fontSize: '13px', fontFamily: 'monospace', fontWeight: '500'}}>
                            Waiting for agent response...
                          </span>
                        </div>
                      )}

                      {/* Agent summary when results are ready */}
                      {aiSearchResults && !aiSearchLoading && (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: '1px solid #334155'
                        }}>
                          {/* Sources and stats */}
                          <div style={{
                            display: 'flex',
                            gap: '16px',
                            flexWrap: 'wrap',
                            marginBottom: '12px',
                            fontSize: '12px',
                            fontFamily: 'monospace'
                          }}>
                            <div style={{color: '#94a3b8'}}>
                              <span style={{color: '#64748b'}}>Sources:</span>{' '}
                              <span style={{color: '#86efac'}}>{aiSearchResults.sources_used?.join(', ') || 'None'}</span>
                            </div>
                            <div style={{color: '#94a3b8'}}>
                              <span style={{color: '#64748b'}}>Found:</span>{' '}
                              <span style={{color: '#fbbf24'}}>{aiSearchResults.total_found} results</span>
                            </div>
                            {aiSearchResults.tiered_results && (
                              <div style={{color: '#94a3b8'}}>
                                <span style={{color: '#64748b'}}>Tiers:</span>{' '}
                                <span style={{color: '#22c55e'}}>{aiSearchResults.tiered_results.tier1?.length || 0}</span>
                                <span style={{color: '#64748b'}}> / </span>
                                <span style={{color: '#60a5fa'}}>{aiSearchResults.tiered_results.tier2?.length || 0}</span>
                                <span style={{color: '#64748b'}}> / </span>
                                <span style={{color: '#a855f7'}}>{aiSearchResults.tiered_results.tier3?.length || 0}</span>
                              </div>
                            )}
                          </div>

                          {/* Agent thinking summary - expandable with markdown */}
                          {aiSearchResults.thinking && (
                            <div style={{
                              padding: '12px',
                              background: '#1e293b',
                              borderRadius: '6px',
                              borderLeft: '3px solid #1a1f3a'
                            }}>
                              <div
                                onClick={() => setExpandedSummary(!expandedSummary)}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  marginBottom: expandedSummary ? '10px' : '6px'
                                }}
                              >
                                <div style={{
                                  fontSize: '11px',
                                  color: '#64748b',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Agent Summary
                                </div>
                                <span style={{fontSize: '12px', color: '#64748b'}}>
                                  {expandedSummary ? '▼ Collapse' : '▶ Expand'}
                                </span>
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: '#cbd5e1',
                                lineHeight: '1.6',
                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                maxHeight: expandedSummary ? 'none' : '80px',
                                overflow: 'hidden',
                                position: 'relative'
                              }}>
                                {expandedSummary ? (
                                  <ReactMarkdown
                                    components={{
                                      p: ({children}) => <p style={{marginBottom: '8px', color: '#cbd5e1'}}>{children}</p>,
                                      strong: ({children}) => <strong style={{color: '#93c5fd', fontWeight: '600'}}>{children}</strong>,
                                      em: ({children}) => <em style={{color: '#a5b4fc'}}>{children}</em>,
                                      ul: ({children}) => <ul style={{marginLeft: '16px', marginBottom: '8px', color: '#cbd5e1'}}>{children}</ul>,
                                      ol: ({children}) => <ol style={{marginLeft: '16px', marginBottom: '8px', color: '#cbd5e1'}}>{children}</ol>,
                                      li: ({children}) => <li style={{marginBottom: '4px'}}>{children}</li>,
                                      h1: ({children}) => <h3 style={{fontWeight: '700', marginBottom: '8px', color: '#93c5fd', fontSize: '15px'}}>{children}</h3>,
                                      h2: ({children}) => <h4 style={{fontWeight: '600', marginBottom: '8px', color: '#93c5fd', fontSize: '14px'}}>{children}</h4>,
                                      h3: ({children}) => <h5 style={{fontWeight: '600', marginBottom: '6px', color: '#a5b4fc', fontSize: '13px'}}>{children}</h5>,
                                      code: ({children}) => <code style={{background: '#334155', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', color: '#fbbf24'}}>{children}</code>,
                                    }}
                                  >
                                    {aiSearchResults.thinking}
                                  </ReactMarkdown>
                                ) : (
                                  <>
                                    {aiSearchResults.thinking.substring(0, 200)}
                                    {aiSearchResults.thinking.length > 200 && '...'}
                                    <div style={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: '30px',
                                      background: 'linear-gradient(transparent, #1e293b)'
                                    }}></div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Agent Notes - Contextual caveats about database limitations */}
                          {aiSearchResults.agent_notes && (
                            <div style={{
                              padding: '10px 12px',
                              background: 'linear-gradient(135deg, #1a1f3a 0%, #242b4a 100%)',
                              borderRadius: '6px',
                              border: '1px solid #3d4663',
                              marginTop: '12px'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px'
                              }}>
                                <span style={{fontSize: '14px', marginTop: '1px'}}>💡</span>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#b8a888',
                                  lineHeight: '1.5',
                                  fontStyle: 'italic'
                                }}>
                                  {aiSearchResults.agent_notes}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* AI Search Error - Enhanced with details */}
              {aiSearchError && (
                <div style={{
                  padding: '16px',
                  margin: '16px 0',
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  border: '1px solid #ef4444',
                  borderRadius: '10px',
                  borderLeft: '4px solid #dc2626'
                }}>
                  <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                    <span style={{fontSize: '20px'}}>⚠️</span>
                    <div style={{flex: 1}}>
                      <div style={{fontWeight: '600', color: '#991b1b', fontSize: '14px', marginBottom: '4px'}}>
                        {typeof aiSearchError === 'object' ? aiSearchError.message : aiSearchError}
                      </div>
                      {typeof aiSearchError === 'object' && aiSearchError.details && (
                        <div style={{fontSize: '13px', color: '#b91c1c', lineHeight: '1.5'}}>
                          {aiSearchError.details}
                        </div>
                      )}
                      <div style={{marginTop: '10px', display: 'flex', gap: '8px'}}>
                        <button
                          onClick={() => { setAISearchError(null); setAISearchProgress([]); }}
                          style={{
                            padding: '6px 12px',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Dismiss
                        </button>
                        <button
                          onClick={() => {
                            setAISearchError(null);
                            setAISearchProgress([]);
                            handleAISearch();
                          }}
                          style={{
                            padding: '6px 12px',
                            background: 'white',
                            color: '#dc2626',
                            border: '1px solid #dc2626',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Research Agent Results */}
              {aiSearchResults && !aiSearchLoading && (
                <div style={{marginTop: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px'}}>
                  {aiSearchResults.results && aiSearchResults.results.length > 0 ? (
                    <div>
                      {/* Results Header with View Toggle */}
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px'}}>
                        <div>
                          <span style={{fontWeight: '600', color: '#374151', fontSize: '15px'}}>
                            Results ({aiSearchResults.total_found || aiSearchResults.results.length})
                          </span>
                          <span style={{fontSize: '13px', fontWeight: '400', color: '#6b7280', marginLeft: '8px'}}>
                            Ranked by relevance
                          </span>
                        </div>
                        {/* View Toggle + Open in Data Explorer */}
                        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                          <div style={{display: 'flex', background: '#e5e7eb', borderRadius: '6px', padding: '2px'}}>
                            <button
                              onClick={() => setAgentResultsView('cards')}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                background: agentResultsView === 'cards' ? 'white' : 'transparent',
                                color: agentResultsView === 'cards' ? '#1a1f3a' : '#6b7280',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px',
                                fontWeight: '500',
                                boxShadow: agentResultsView === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                              }}
                            >
                              <FiGrid size={14} /> Cards
                            </button>
                            <button
                              onClick={() => setAgentResultsView('table')}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                background: agentResultsView === 'table' ? 'white' : 'transparent',
                                color: agentResultsView === 'table' ? '#1a1f3a' : '#6b7280',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px',
                                fontWeight: '500',
                                boxShadow: agentResultsView === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                              }}
                            >
                              <FiList size={14} /> Table
                            </button>
                          </div>
                          <button
                            onClick={exportAgentResultsToDataExplorer}
                            style={{
                              padding: '6px 12px',
                              background: '#1a1f3a',
                              color: 'white',
                              borderRadius: '6px',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            <FiExternalLink size={14} /> Export Results
                          </button>
                        </div>
                      </div>

                      {/* Tier Summary Bar */}
                      {aiSearchResults.tiered_results && (
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          marginBottom: '20px',
                          padding: '12px 16px',
                          background: 'white',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <span style={{width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e'}}></span>
                            <span style={{fontSize: '13px', color: '#374151', fontWeight: '600'}}>
                              {aiSearchResults.tiered_results.tier1?.length || 0} Direct
                            </span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <span style={{width: '12px', height: '12px', borderRadius: '50%', background: '#1a1f3a'}}></span>
                            <span style={{fontSize: '13px', color: '#374151', fontWeight: '600'}}>
                              {aiSearchResults.tiered_results.tier2?.length || 0} Related
                            </span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <span style={{width: '12px', height: '12px', borderRadius: '50%', background: '#a855f7'}}></span>
                            <span style={{fontSize: '13px', color: '#374151', fontWeight: '600'}}>
                              {aiSearchResults.tiered_results.tier3?.length || 0} Exploratory
                            </span>
                          </div>
                          {/* AI Reasoning Indicator - Shows when ≤10 direct matches have reasoning */}
                          {aiSearchResults.record_reasoning && Object.keys(aiSearchResults.record_reasoning).length > 0 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginLeft: 'auto',
                              padding: '4px 12px',
                              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
                              border: '1px solid rgba(168, 85, 247, 0.3)',
                              borderRadius: '16px'
                            }}>
                              <span style={{fontSize: '14px'}}>🧠</span>
                              <span style={{fontSize: '12px', color: '#7c3aed', fontWeight: '600'}}>
                                AI Reasoning for {Object.keys(aiSearchResults.record_reasoning).length} results
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Display results - Cards or Table View */}
                      {(() => {
                        // Use tiered_results if available, otherwise fall back to results array
                        const tier1 = aiSearchResults.tiered_results?.tier1 || [];
                        const tier2 = aiSearchResults.tiered_results?.tier2 || [];
                        const tier3 = aiSearchResults.tiered_results?.tier3 || [];
                        const allResults = [...tier1, ...tier2, ...tier3];

                        // Helper to generate link URL
                        const getLinkUrl = (result) => {
                          const entityType = result.entity_type || result.type || 'company';
                          if (entityType === 'company' && result.pitchbook_id) {
                            return `/company/${result.pitchbook_id}`;
                          } else if (entityType === 'deal' && (result.pitchbook_deal_id || result.pitchbook_id)) {
                            return `/deal/${result.pitchbook_deal_id || result.pitchbook_id}`;
                          } else if (entityType === 'investor' && (result.pitchbook_investor_id || result.pitchbook_id)) {
                            return `/investor/${result.pitchbook_investor_id || result.pitchbook_id}`;
                          }
                          return '#';
                        };

                        // TABLE VIEW
                        if (agentResultsView === 'table') {
                          return (
                            <div style={{overflowX: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: 'white'}}>
                                <thead>
                                  <tr style={{background: '#f8f9fa'}}>
                                    <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#374151'}}>Name</th>
                                    <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#374151'}}>Type</th>
                                    <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#374151'}}>Tier</th>
                                    <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#374151'}}>Details</th>
                                    <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#374151'}}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allResults.slice(0, agentResultsLimit).map((result, idx) => {
                                    const tier = tier1.includes(result) ? 1 : tier2.includes(result) ? 2 : 3;
                                    const tierColor = tier === 1 ? '#22c55e' : tier === 2 ? '#1a1f3a' : '#a855f7';
                                    const tierLabel = tier === 1 ? 'Direct' : tier === 2 ? 'Related' : 'Exploratory';
                                    return (
                                      <tr key={idx} style={{borderBottom: '1px solid #f3f4f6'}}>
                                        <td style={{padding: '12px', fontWeight: '500', color: '#111827'}}>
                                          {result.name || result.companies || 'Unknown'}
                                        </td>
                                        <td style={{padding: '12px', color: '#6b7280', textTransform: 'capitalize'}}>
                                          {result.entity_type || 'N/A'}
                                        </td>
                                        <td style={{padding: '12px'}}>
                                          <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            background: `${tierColor}15`,
                                            color: tierColor
                                          }}>
                                            <span style={{width: '6px', height: '6px', borderRadius: '50%', background: tierColor}}></span>
                                            {tierLabel}
                                          </span>
                                        </td>
                                        <td style={{padding: '12px', color: '#6b7280', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                          {result.description || result.deal_synopsis || result.primary_industry || '—'}
                                        </td>
                                        <td style={{padding: '12px', textAlign: 'center'}}>
                                          <a
                                            href={getLinkUrl(result)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              padding: '6px 12px',
                                              background: '#1a1f3a',
                                              color: 'white',
                                              borderRadius: '4px',
                                              textDecoration: 'none',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                              display: 'inline-block'
                                            }}
                                          >
                                            View
                                          </a>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {allResults.length > agentResultsLimit && (
                                <div style={{padding: '12px', textAlign: 'center', borderTop: '1px solid #e5e7eb', background: '#f9fafb'}}>
                                  <button
                                    onClick={() => setAgentResultsLimit(prev => prev + 20)}
                                    style={{
                                      padding: '8px 20px',
                                      background: 'white',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      color: '#374151'
                                    }}
                                  >
                                    Show more ({allResults.length - agentResultsLimit} remaining)
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        }

                        // CARD VIEW (existing)
                        return (
                          <>
                            {/* TIER 1: Direct Matches */}
                            {tier1.length > 0 && (
                              <div style={{marginBottom: '28px'}}>
                                <div style={{
                                  padding: '12px 16px',
                                  background: 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)',
                                  borderLeft: '4px solid #22c55e',
                                  borderRadius: '8px',
                                  marginBottom: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#22c55e',
                                    boxShadow: '0 0 8px #22c55e'
                                  }}></div>
                                  <div>
                                    <span style={{fontSize: '15px', fontWeight: '700', color: '#15803d'}}>Direct Matches</span>
                                    <span style={{
                                      marginLeft: '10px',
                                      fontSize: '12px',
                                      color: '#166534',
                                      background: 'white',
                                      padding: '2px 10px',
                                      borderRadius: '12px',
                                      fontWeight: '600'
                                    }}>{tier1.length}</span>
                                  </div>
                                  <span style={{fontSize: '11px', color: '#166534', marginLeft: 'auto'}}>
                                    Best matches for your query
                                  </span>
                                </div>
                                {tier1.map((result, idx) => (
                                  <SQLStructuredCard
                                    key={`tier1_${idx}`}
                                    result={result}
                                    isExpanded={expandedResults[`tier1_${idx}`]}
                                    onToggle={() => setExpandedResults(prev => ({...prev, [`tier1_${idx}`]: !prev[`tier1_${idx}`]}))}
                                    linkUrl={getLinkUrl(result)}
                                    tier={1}
                                  />
                                ))}
                              </div>
                            )}

                            {/* TIER 2: Related */}
                            {tier2.length > 0 && (
                              <div style={{marginBottom: '28px'}}>
                                <div
                                  onClick={() => setShowVDBDense(!showVDBDense)}
                                  style={{
                                    padding: '12px 16px',
                                    background: showVDBDense
                                      ? 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)'
                                      : 'linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%)',
                                    borderLeft: '4px solid #1a1f3a',
                                    borderRadius: '8px',
                                    marginBottom: showVDBDense ? '14px' : '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <span style={{fontSize: '12px', color: '#6b7280'}}>{showVDBDense ? '▼' : '▶'}</span>
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#1a1f3a',
                                    boxShadow: '0 0 6px #1a1f3a'
                                  }}></div>
                                  <div>
                                    <span style={{fontSize: '14px', fontWeight: '600', color: '#1a1f3a'}}>Related Results</span>
                                    <span style={{
                                      marginLeft: '10px',
                                      fontSize: '12px',
                                      color: '#1a1f3a',
                                      background: 'white',
                                      padding: '2px 10px',
                                      borderRadius: '12px',
                                      fontWeight: '600'
                                    }}>{tier2.length}</span>
                                  </div>
                                  <span style={{fontSize: '11px', color: '#6b7280', marginLeft: 'auto'}}>
                                    {showVDBDense ? 'Click to collapse' : 'Companies/deals related to your search'}
                                  </span>
                                </div>
                                {showVDBDense && tier2.map((result, idx) => (
                                  <SQLStructuredCard
                                    key={`tier2_${idx}`}
                                    result={result}
                                    isExpanded={expandedResults[`tier2_${idx}`]}
                                    onToggle={() => setExpandedResults(prev => ({...prev, [`tier2_${idx}`]: !prev[`tier2_${idx}`]}))}
                                    linkUrl={getLinkUrl(result)}
                                    tier={2}
                                  />
                                ))}
                              </div>
                            )}

                            {/* TIER 3: Exploratory */}
                            {tier3.length > 0 && (
                              <div style={{marginBottom: '28px'}}>
                                <div
                                  onClick={() => setShowVDBSparse(!showVDBSparse)}
                                  style={{
                                    padding: '12px 16px',
                                    background: showVDBSparse
                                      ? 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%)'
                                      : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                    borderLeft: '4px solid #a855f7',
                                    borderRadius: '8px',
                                    marginBottom: showVDBSparse ? '14px' : '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <span style={{fontSize: '12px', color: '#6b7280'}}>{showVDBSparse ? '▼' : '▶'}</span>
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#a855f7',
                                    boxShadow: '0 0 6px #a855f7'
                                  }}></div>
                                  <div>
                                    <span style={{fontSize: '14px', fontWeight: '600', color: '#7c3aed'}}>Exploratory</span>
                                    <span style={{
                                      marginLeft: '10px',
                                      fontSize: '12px',
                                      color: '#6b21a8',
                                      background: 'white',
                                      padding: '2px 10px',
                                      borderRadius: '12px',
                                      fontWeight: '600'
                                    }}>{tier3.length}</span>
                                  </div>
                                  <span style={{fontSize: '11px', color: '#9ca3af', marginLeft: 'auto'}}>
                                    {showVDBSparse ? 'Click to collapse' : 'Tangentially related'}
                                  </span>
                                </div>
                                {showVDBSparse && tier3.map((result, idx) => (
                                  <SQLStructuredCard
                                    key={`tier3_${idx}`}
                                    result={result}
                                    isExpanded={expandedResults[`tier3_${idx}`]}
                                    onToggle={() => setExpandedResults(prev => ({...prev, [`tier3_${idx}`]: !prev[`tier3_${idx}`]}))}
                                    linkUrl={getLinkUrl(result)}
                                    tier={3}
                                  />
                                ))}
                              </div>
                            )}

                            {/* Fallback if no tiered_results (old API response) */}
                            {!aiSearchResults.tiered_results && aiSearchResults.results.slice(0, agentResultsLimit).map((result, idx) => (
                              <SQLStructuredCard
                                key={`result_${idx}`}
                                result={result}
                                isExpanded={expandedResults[`result_${idx}`]}
                                onToggle={() => setExpandedResults(prev => ({...prev, [`result_${idx}`]: !prev[`result_${idx}`]}))}
                                linkUrl={getLinkUrl(result)}
                                tier={2}
                              />
                            ))}

                            {/* Show more button for card view */}
                            {allResults.length > agentResultsLimit && (
                              <div style={{padding: '16px', textAlign: 'center', marginTop: '12px'}}>
                                <button
                                  onClick={() => setAgentResultsLimit(prev => prev + 20)}
                                  style={{
                                    padding: '10px 24px',
                                    background: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginRight: '12px'
                                  }}
                                >
                                  Show {Math.min(20, allResults.length - agentResultsLimit)} more
                                </button>
                                <button
                                  onClick={exportAgentResultsToDataExplorer}
                                  style={{
                                    padding: '10px 24px',
                                    background: '#1a1f3a',
                                    color: 'white',
                                    borderRadius: '8px',
                                    border: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <FiExternalLink size={14} /> Export to Data Explorer
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div style={{color: '#6b7280', textAlign: 'center'}}>No results found</div>
                  )}
                </div>
              )}

              {/* Research Agent Analytics Section */}
              {showAgentAnalytics && aiSearchResults && (
                <div className="agent-analytics-section">
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <FiPieChart size={18} /> Result Analytics
                    </h3>
                    <span style={{fontSize: '12px', color: '#9ca3af'}}>
                      Based on {aiSearchResults.total_found || 0} results
                    </span>
                  </div>

                  <div className={`agent-analytics-grid ${agentPanelExpanded ? 'expanded' : ''}`}>
                    {/* Source Distribution Pie Chart */}
                    <div className="agent-chart-card">
                      <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                        Source Distribution
                      </h4>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={(() => {
                              const sources = {};
                              const allResults = [
                                ...(aiSearchResults.tiered_results?.tier1 || []),
                                ...(aiSearchResults.tiered_results?.tier2 || []),
                                ...(aiSearchResults.tiered_results?.tier3 || []),
                                ...(aiSearchResults.results || [])
                              ];
                              allResults.forEach(r => {
                                const src = r.source?.includes('sql') ? 'SQL' :
                                           r.source?.includes('dense') ? 'Dense (Semantic)' :
                                           r.source?.includes('sparse') ? 'Sparse (SPLADE)' : 'Other';
                                sources[src] = (sources[src] || 0) + 1;
                              });
                              return Object.entries(sources).map(([name, value]) => ({ name, value }));
                            })()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                            label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            <Cell fill="#f59e0b" /> {/* SQL - Amber */}
                            <Cell fill="#1a1f3a" /> {/* Dense - Navy */}
                            <Cell fill="#ec4899" /> {/* Sparse - Pink */}
                            <Cell fill="#9ca3af" /> {/* Other - Gray */}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Entity Type Bar Chart */}
                    <div className="agent-chart-card">
                      <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                        Entity Types
                      </h4>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart
                          data={(() => {
                            const types = { company: 0, deal: 0, investor: 0 };
                            const allResults = [
                              ...(aiSearchResults.tiered_results?.tier1 || []),
                              ...(aiSearchResults.tiered_results?.tier2 || []),
                              ...(aiSearchResults.tiered_results?.tier3 || []),
                              ...(aiSearchResults.results || [])
                            ];
                            allResults.forEach(r => {
                              const type = r.entity_type || 'other';
                              types[type] = (types[type] || 0) + 1;
                            });
                            return [
                              { name: 'Companies', value: types.company, fill: '#1a1f3a' },
                              { name: 'Deals', value: types.deal, fill: '#22c55e' },
                              { name: 'Investors', value: types.investor, fill: '#a855f7' }
                            ];
                          })()}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                        >
                          <XAxis type="number" hide />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {[
                              { name: 'Companies', fill: '#1a1f3a' },
                              { name: 'Deals', fill: '#22c55e' },
                              { name: 'Investors', fill: '#a855f7' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Tier Distribution Donut */}
                    <div className="agent-chart-card">
                      <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                        Relevance Tiers
                      </h4>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Direct', value: aiSearchResults.tiered_results?.tier1?.length || 0, fill: '#22c55e' },
                              { name: 'Related', value: aiSearchResults.tiered_results?.tier2?.length || 0, fill: '#1a1f3a' },
                              { name: 'Exploratory', value: aiSearchResults.tiered_results?.tier3?.length || 0, fill: '#a855f7' }
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            <Cell fill="#22c55e" />
                            <Cell fill="#1a1f3a" />
                            <Cell fill="#a855f7" />
                          </Pie>
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36} iconSize={10} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Match Quality Stats */}
                    <div className="agent-chart-card">
                      <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                        Match Quality
                      </h4>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0'}}>
                        {(() => {
                          const allResults = [
                            ...(aiSearchResults.tiered_results?.tier1 || []),
                            ...(aiSearchResults.tiered_results?.tier2 || []),
                            ...(aiSearchResults.tiered_results?.tier3 || [])
                          ];
                          const scores = allResults.map(r => r.relevance_score || 0).filter(s => s > 0);
                          const avgScore = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length * 100).toFixed(1) : 0;
                          const maxScore = scores.length > 0 ? (Math.max(...scores) * 100).toFixed(1) : 0;
                          const directCount = aiSearchResults.tiered_results?.tier1?.length || 0;
                          const totalCount = allResults.length;
                          const directPct = totalCount > 0 ? ((directCount / totalCount) * 100).toFixed(0) : 0;

                          return (
                            <>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{fontSize: '13px', color: '#6b7280'}}>Avg Relevance</span>
                                <span style={{fontSize: '18px', fontWeight: '700', color: '#1a1f3a'}}>{avgScore}%</span>
                              </div>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{fontSize: '13px', color: '#6b7280'}}>Top Match</span>
                                <span style={{fontSize: '18px', fontWeight: '700', color: '#22c55e'}}>{maxScore}%</span>
                              </div>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{fontSize: '13px', color: '#6b7280'}}>Direct Matches</span>
                                <span style={{fontSize: '18px', fontWeight: '700', color: '#f59e0b'}}>{directPct}%</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Funding Range Distribution - Only show in expanded mode */}
                    {agentPanelExpanded && (
                      <div className="agent-chart-card">
                        <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                          Funding Distribution
                        </h4>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart
                            data={(() => {
                              const ranges = { '<$1M': 0, '$1-5M': 0, '$5-20M': 0, '$20-50M': 0, '$50M+': 0 };
                              const allResults = [
                                ...(aiSearchResults.tiered_results?.tier1 || []),
                                ...(aiSearchResults.tiered_results?.tier2 || []),
                                ...(aiSearchResults.tiered_results?.tier3 || []),
                                ...(aiSearchResults.results || [])
                              ];
                              allResults.forEach(r => {
                                const amount = r.total_raised_usd || r.deal_size_usd || 0;
                                if (amount < 1000000) ranges['<$1M']++;
                                else if (amount < 5000000) ranges['$1-5M']++;
                                else if (amount < 20000000) ranges['$5-20M']++;
                                else if (amount < 50000000) ranges['$20-50M']++;
                                else ranges['$50M+']++;
                              });
                              return Object.entries(ranges).map(([name, value]) => ({ name, value }));
                            })()}
                            margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                          >
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis hide />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Location Distribution - Only show in expanded mode */}
                    {agentPanelExpanded && (
                      <div className="agent-chart-card">
                        <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                          Geographic Distribution
                        </h4>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={(() => {
                                const locations = {};
                                const allResults = [
                                  ...(aiSearchResults.tiered_results?.tier1 || []),
                                  ...(aiSearchResults.tiered_results?.tier2 || []),
                                  ...(aiSearchResults.tiered_results?.tier3 || []),
                                  ...(aiSearchResults.results || [])
                                ];
                                allResults.forEach(r => {
                                  const loc = r.hq_country || r.hq_countryterritoryregion || r.country || 'Unknown';
                                  if (loc && loc !== 'Unknown') {
                                    locations[loc] = (locations[loc] || 0) + 1;
                                  }
                                });
                                return Object.entries(locations)
                                  .sort((a, b) => b[1] - a[1])
                                  .slice(0, 5)
                                  .map(([name, value]) => ({ name, value }));
                              })()}
                              cx="50%"
                              cy="50%"
                              outerRadius={60}
                              dataKey="value"
                              label={({name}) => name.substring(0, 12)}
                              labelLine={false}
                            >
                              <Cell fill="#1a1f3a" />
                              <Cell fill="#3b82f6" />
                              <Cell fill="#60a5fa" />
                              <Cell fill="#93c5fd" />
                              <Cell fill="#bfdbfe" />
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Industry Distribution - Only show in expanded mode */}
                    {agentPanelExpanded && (
                      <div className="agent-chart-card">
                        <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                          Industry Breakdown
                        </h4>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart
                            data={(() => {
                              const industries = {};
                              const allResults = [
                                ...(aiSearchResults.tiered_results?.tier1 || []),
                                ...(aiSearchResults.tiered_results?.tier2 || []),
                                ...(aiSearchResults.tiered_results?.tier3 || []),
                                ...(aiSearchResults.results || [])
                              ];
                              allResults.forEach(r => {
                                const ind = r.primary_industry || r.primary_industry_code || 'Other';
                                if (ind && ind !== 'Other') {
                                  const shortInd = ind.length > 15 ? ind.substring(0, 15) + '...' : ind;
                                  industries[shortInd] = (industries[shortInd] || 0) + 1;
                                }
                              });
                              return Object.entries(industries)
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 5)
                                .map(([name, value]) => ({ name, value }));
                            })()}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                          >
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Top Keywords - Only show in expanded mode */}
                    {agentPanelExpanded && (
                      <div className="agent-chart-card">
                        <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151'}}>
                          Top Keywords
                        </h4>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 0'}}>
                          {(() => {
                            const keywords = {};
                            const allResults = [
                              ...(aiSearchResults.tiered_results?.tier1 || []),
                              ...(aiSearchResults.tiered_results?.tier2 || []),
                              ...(aiSearchResults.tiered_results?.tier3 || []),
                              ...(aiSearchResults.results || [])
                            ];
                            allResults.forEach(r => {
                              const kws = Array.isArray(r.keywords) ? r.keywords :
                                (typeof r.keywords === 'string' ? r.keywords.split(',').map(k => k.trim()) : []);
                              kws.forEach(kw => {
                                if (kw && kw.length > 2 && kw.length < 25) {
                                  keywords[kw.toLowerCase()] = (keywords[kw.toLowerCase()] || 0) + 1;
                                }
                              });
                            });
                            return Object.entries(keywords)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 12)
                              .map(([kw, count], i) => (
                                <span key={i} style={{
                                  padding: '4px 10px',
                                  background: count > 3 ? '#1a1f3a' : count > 1 ? '#3b82f6' : '#93c5fd',
                                  color: count > 1 ? 'white' : '#1e40af',
                                  borderRadius: '12px',
                                  fontSize: count > 3 ? '12px' : '11px',
                                  fontWeight: count > 3 ? '600' : '500'
                                }}>
                                  {kw}
                                </span>
                              ));
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        <div className="results-section">
          {loading || tabLoading ? (
            <GamingLoader
              message={tabLoading ? `Loading ${activeTab}...` : "Loading data..."}
            />
          ) : (
            <>
              {activeTab === 'deals' && (
                <>
                  {/* Innovative Hybrid Banner with Tooltip */}
                  <div className="hybrid-info-banner">
                    <div className="banner-left">
                      <strong>{allDeals.length.toLocaleString()}</strong> deals loaded
                      {deals.length < allDeals.length && (
                        <span className="filtered-text"> • Showing {deals.length.toLocaleString()} filtered</span>
                      )}
                    </div>
                    <div className="banner-right">
                      {(dealFilters.dealType.length > 0 || dealFilters.dealType2.length > 0 || dealFilters.financingStatus.length > 0) && (
                        <div className="active-filters-badge">
                          <FiFilter size={12} />
                          <span>{dealFilters.dealType.length + dealFilters.dealType2.length + dealFilters.financingStatus.length} active</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="table-container" ref={tableContainerRef}>
                    <table className="data-table deals-table-compact">
                      <thead>
                        <tr>
                          <th>Company Name</th>
                          <th>Deal Date</th>
                          <th>Deal Type</th>
                          <th>Deal Type 2</th>
                          <th>Deal Size</th>
                          <th>Valuation</th>
                          <th>Description</th>
                          <th>Verticals</th>
                          <th>Keywords</th>
                          <th>Lead/Sole Investors</th>
                          <th>Investors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deals.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map((deal, idx) => {
                          // Find company pitchbook_id by matching company name
                          const companyMatch = allCompanies.find(c =>
                            c.name?.toLowerCase() === deal.companies?.toLowerCase()
                          );
                          const companyId = companyMatch?.pitchbook_id || deal.companies;

                          const isExpanded = expandedDescriptions.has(deal.id || idx);
                          const description = deal.deal_synopsis || 'N/A';
                          const truncatedDescription = description.length > 80
                            ? description.substring(0, 80) + '...'
                            : description;

                          return (
                            <tr key={deal.id || idx}>
                              <td>
                                {deal.pitchbook_deal_id ? (
                                  <Link to={`/deal/${deal.pitchbook_deal_id}`} className="deal-link" target="_blank" rel="noopener noreferrer">
                                    {deal.companies || 'N/A'}
                                  </Link>
                                ) : (
                                  <span>{deal.companies || 'N/A'}</span>
                                )}
                              </td>
                              <td className="date-cell">{formatDate(deal.deal_date)}</td>
                              <td>{deal.deal_type || 'N/A'}</td>
                              <td>{deal.deal_type_2 || 'N/A'}</td>
                              <td className="number-cell">{formatCurrency(deal.deal_size_usd)}</td>
                              <td className="number-cell">{formatCurrency(deal.post_valuation)}</td>
                              <td
                                className="description-cell"
                                onDoubleClick={() => toggleDescription(deal.id || idx)}
                                title="Double-click to expand/collapse"
                              >
                                {isExpanded ? description : truncatedDescription}
                              </td>
                              <td className="verticals-cell">{deal.verticals?.join(', ') || 'N/A'}</td>
                              <td className="keywords-cell">{deal.keywords || 'N/A'}</td>
                              <td className="investors-cell">
                                {deal.lead_sole_investors?.join(', ') || deal.lead_investors?.join(', ') || 'N/A'}
                              </td>
                              <td className="investors-cell">
                                {deal.investors?.join(', ') || 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {deals.length === 0 && !loading && (
                      <div className="no-results">
                        <p>No deals found. Try adjusting your filters.</p>
                      </div>
                    )}
                  </div>

                  {/* INNOVATIVE HYBRID PAGINATION - The Transcend Way! */}
                  {deals.length > 0 && (
                    <div className="hybrid-pagination-section">
                      {/* Top row: Pagination controls + Export */}
                      <div className="pagination-controls-row">
                        <div className="pagination-buttons">
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                          <span className="page-indicator">
                            Page {currentPage} of {Math.ceil(deals.length / rowsPerPage)}
                          </span>
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(deals.length / rowsPerPage), prev + 1))}
                            disabled={currentPage >= Math.ceil(deals.length / rowsPerPage)}
                          >
                            Next
                          </button>
                        </div>
                        <button
                          className="csv-export-btn"
                          onClick={() => exportToCSV(deals, 'transcend_deals')}
                          title="Export current filtered view to CSV"
                        >
                          <FiDownload size={16} />
                          Export CSV ({deals.length} records)
                        </button>
                      </div>

                      {/* Bottom row: Smart messages */}
                      <div className="pagination-hints">
                        {hasMore ? (
                          <div className="hint-with-icon">
                            <span className="hint-icon" title="Scroll down to auto-load more deals">ℹ️</span>
                            <span>
                              {isNearBottom ? (
                                <span className="loading-indicator">🔄 Auto-loading more deals...</span>
                              ) : (
                                <span>Scroll down or click Next • Auto-loads when near bottom</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="all-loaded-message">
                            <FiCheckCircle size={16} color="#28a745" />
                            <span>All {allDeals.length.toLocaleString()} deals loaded</span>
                          </div>
                        )}

                        <span
                          className="tooltip-trigger-inline"
                          title="You have 3 ways to navigate: (1) Click Previous/Next for page-by-page, (2) Scroll down to auto-load seamlessly, (3) Click 'Load 50 More' to fetch new data. Filters apply instantly!"
                        >
                          <span className="question-icon">?</span>
                          <span className="tooltip-label">How it works</span>
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'companies' && (
                <>
                  <div className="hybrid-info-banner">
                    <div className="banner-left">
                      <strong>{allCompanies.length.toLocaleString()}</strong> companies loaded
                      {companies.length < allCompanies.length && (
                        <span className="filtered-text"> • Showing {companies.length.toLocaleString()} filtered</span>
                      )}
                    </div>
                  </div>

                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Industry</th>
                          <th>Location</th>
                          <th>Total Raised</th>
                          <th>Employees</th>
                          <th>Founded</th>
                          <th className="transcend-col">Segment</th>
                          <th className="transcend-col">Vertical</th>
                          <th className="transcend-col">Tech Core</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map(company => (
                          <tr key={company.id}>
                            <td>
                              <Link to={`/company/${company.pitchbook_id}`} className="company-link" target="_blank" rel="noopener noreferrer">
                                {company.name}
                              </Link>
                            </td>
                            <td>{company.primary_industry_code || company.primary_industry || 'N/A'}</td>
                            <td>{company.hq_city || company.hq_location || 'N/A'}</td>
                            <td>{formatCurrency(company.total_raised || company.total_raised_usd)}</td>
                            <td>{company.employees || 'N/A'}</td>
                            <td>{formatYear(company.year_founded)}</td>
                            <td className="transcend-col">{company.transcend_segment || '-'}</td>
                            <td className="transcend-col" title={Array.isArray(company.transcend_vertical) ? company.transcend_vertical.join(', ') : company.transcend_vertical}>
                              {Array.isArray(company.transcend_vertical) ? company.transcend_vertical[0] : company.transcend_vertical || '-'}
                            </td>
                            <td className="transcend-col" title={Array.isArray(company.transcend_technology_core) ? company.transcend_technology_core.join(', ') : company.transcend_technology_core}>
                              {Array.isArray(company.transcend_technology_core) ? company.transcend_technology_core[0] : company.transcend_technology_core || '-'}
                            </td>
                            <td>
                              <span className={`status-pill ${company.business_status?.toLowerCase()}`}>
                                {company.business_status || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {companies.length === 0 && (
                      <div className="no-results">
                        <p>No companies match your criteria</p>
                      </div>
                    )}
                  </div>

                  {companies.length > 0 && (
                    <div className="hybrid-pagination-section">
                      <div className="pagination-controls-row">
                        <div className="pagination-buttons">
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                          <span className="page-indicator">
                            Page {currentPage} of {Math.ceil(companies.length / rowsPerPage)}
                          </span>
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(companies.length / rowsPerPage), prev + 1))}
                            disabled={currentPage >= Math.ceil(companies.length / rowsPerPage)}
                          >
                            Next
                          </button>
                        </div>
                        <button
                          className="csv-export-btn"
                          onClick={() => exportToCSV(companies, 'transcend_companies')}
                          title="Export current filtered view to CSV"
                        >
                          <FiDownload size={16} />
                          Export CSV ({companies.length} records)
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'investors' && (
                <>
                  <div className="hybrid-info-banner">
                    <div className="banner-left">
                      <strong>{allInvestors.length.toLocaleString()}</strong> investors loaded
                      {investors.length < allInvestors.length && (
                        <span className="filtered-text"> • Showing {investors.length.toLocaleString()} filtered</span>
                      )}
                    </div>
                  </div>

                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Location</th>
                          <th>AUM</th>
                          <th>Investments</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investors.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map(investor => (
                          <tr key={investor.id}>
                            <td>
                              <Link to={`/investor/${investor.pitchbook_investor_id}`} className="company-link" target="_blank" rel="noopener noreferrer">
                                {investor.name}
                              </Link>
                            </td>
                            <td>{investor.investor_type || 'N/A'}</td>
                            <td>{investor.hq_location || 'N/A'}</td>
                            <td>{formatCurrency(investor.aum)}</td>
                            <td>{investor.investments_count || 'N/A'}</td>
                            <td>
                              <span className={`status-pill ${investor.investor_status?.toLowerCase()}`}>
                                {investor.investor_status || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {investors.length === 0 && (
                      <div className="no-results">
                        <p>No investors match your criteria</p>
                      </div>
                    )}
                  </div>

                  {investors.length > 0 && (
                    <div className="hybrid-pagination-section">
                      <div className="pagination-controls-row">
                        <div className="pagination-buttons">
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                          <span className="page-indicator">
                            Page {currentPage} of {Math.ceil(investors.length / rowsPerPage)}
                          </span>
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(investors.length / rowsPerPage), prev + 1))}
                            disabled={currentPage >= Math.ceil(investors.length / rowsPerPage)}
                          >
                            Next
                          </button>
                        </div>
                        <button
                          className="csv-export-btn"
                          onClick={() => exportToCSV(investors, 'transcend_investors')}
                          title="Export current filtered view to CSV"
                        >
                          <FiDownload size={16} />
                          Export CSV ({investors.length} records)
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        </div>

        {/* Database Status - Below Results */}
        <div className="database-status-section" style={{ marginTop: '30px' }}>
          <div className="status-card">
            <div className="status-header">
              <FiDatabase size={24} className="status-icon" />
              <h3>Database Status</h3>
            </div>

            {apiHealth ? (
              <div className="status-body">
                <div className={`status-badge ${apiHealth.database_connected ? 'connected' : 'disconnected'}`}>
                  {apiHealth.database_connected ? (
                    <>
                      <FiCheckCircle size={18} />
                      <span>Connected</span>
                    </>
                  ) : (
                    <>
                      <FiAlertCircle size={18} />
                      <span>Disconnected</span>
                    </>
                  )}
                </div>

                <div className="data-counts">
                  <div className="count-item">
                    <div className="count-value">{allCompanies.length.toLocaleString()}</div>
                    <div className="count-label">Companies</div>
                  </div>
                  <div className="count-divider"></div>
                  <div className="count-item">
                    <div className="count-value">{allDeals.length.toLocaleString()}</div>
                    <div className="count-label">Deals</div>
                  </div>
                  <div className="count-divider"></div>
                  <div className="count-item">
                    <div className="count-value">{allInvestors.length.toLocaleString()}</div>
                    <div className="count-label">Investors</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="status-loading">
                <div className="subtle-game-loader">
                  <div className="t-pixel"></div>
                </div>
                <span>Checking connection...</span>
              </div>
            )}
          </div>
        </div>
          </>
        )}
      </section>

      {/* Filter Values Modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Active Filters</h3>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}>
                <FiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              {getActiveFilters().map((filter, idx) => (
                <div key={idx} className="filter-detail">
                  <strong>{filter.type.charAt(0).toUpperCase() + filter.type.slice(1)}:</strong>
                  <span className="filter-value">{filter.label.split(': ')[1] || filter.label}</span>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="clear-all-btn" onClick={() => {
                clearFilters();
                setShowFilterModal(false);
              }}>
                Clear All Filters
              </button>
              <button className="close-modal-btn" onClick={() => setShowFilterModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contextual Analytics */}
      <ContextualAnalytics
        activeTab={activeTab}
        filteredData={activeTab === 'deals' ? deals : activeTab === 'companies' ? companies : investors}
        allData={{ deals: allDeals, companies: allCompanies, investors: allInvestors }}
      />

      {/* Tabulate Modal - Full Data Explorer */}
      {showTabulateModal && heroSearchResults && (
        <div className="tabulate-modal-overlay" onClick={() => setShowTabulateModal(false)}>
          <div className="tabulate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tabulate-modal-header">
              <h2>Data Explorer</h2>
              <div className="tabulate-modal-controls">
                <span className="tabulate-result-count">
                  {heroSearchResults.total_found || heroSearchResults.results?.length || 0} results
                </span>
                <button
                  className="tabulate-export-btn"
                  onClick={() => {
                    // Get all results and separate by entity type
                    const allResults = heroSearchResults.tiered_results
                      ? [
                          ...(heroSearchResults.tiered_results.tier1 || []),
                          ...(heroSearchResults.tiered_results.tier2 || []),
                          ...(heroSearchResults.tiered_results.tier3 || [])
                        ]
                      : (heroSearchResults.results || []);

                    const companies = allResults.filter(r => (r.entity_type || r.type) === 'company');
                    const deals = allResults.filter(r => (r.entity_type || r.type) === 'deal');
                    const investors = allResults.filter(r => (r.entity_type || r.type) === 'investor');

                    // Store in localStorage for Data Explorer
                    localStorage.setItem('transcend_search_export', JSON.stringify({
                      timestamp: new Date().toISOString(),
                      query: heroSearchQuery,
                      searchMode: heroSearchMode,
                      results: { companies, deals, investors },
                      totalFound: heroSearchResults.total_found || allResults.length
                    }));

                    // Open Data Explorer in new tab
                    window.open('/data-explorer?source=search', '_blank');
                  }}
                  title="Analyze in Data Explorer Workbench (opens in new tab)"
                >
                  <FiExternalLink size={16} />
                  <span>View in Data Explorer</span>
                </button>
                <button className="tabulate-close-btn" onClick={() => setShowTabulateModal(false)}>
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="tabulate-modal-toolbar">
              <div className="tabulate-sort-controls">
                <span className="sort-label">Sort by:</span>
                <select
                  value={heroTableSort.field || ''}
                  onChange={(e) => setHeroTableSort(prev => ({ field: e.target.value || null, direction: 'asc' }))}
                  className="tabulate-sort-select"
                >
                  <option value="">Default (Relevance)</option>
                  <option value="name">Name (A-Z)</option>
                  <option value="date">Date</option>
                  <option value="amount">Amount</option>
                </select>
                <button
                  className={`sort-direction-btn ${heroTableSort.direction === 'desc' ? 'desc' : ''}`}
                  onClick={() => setHeroTableSort(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  disabled={!heroTableSort.field}
                  title={heroTableSort.direction === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {heroTableSort.direction === 'asc' ? '↑' : '↓'}
                </button>
                <div className="tabulate-sort-info">
                  <FiInfo size={14} className="tabulate-sort-info-icon" />
                  <div className="tabulate-sort-tooltip">
                    <strong>Sorting Variables by Entity:</strong>
                    <p><span className="entity-label">Name:</span> Company name, Deal company, or Investor name</p>
                    <p><span className="entity-label">Date:</span> Deal date (deals) or Year founded (companies)</p>
                    <p><span className="entity-label">Amount:</span> Deal size (deals), Total raised (companies), or AUM (investors)</p>
                    <p className="sort-note">Null/missing values are placed at the end of sorted results.</p>
                  </div>
                </div>
              </div>
              <div className="tabulate-entity-filter">
                <span className="filter-label">Type:</span>
                <select
                  value={heroEntityFilter}
                  onChange={(e) => setHeroEntityFilter(e.target.value)}
                  className="tabulate-entity-select"
                >
                  <option value="all">All Types</option>
                  <option value="companies">Companies</option>
                  <option value="deals">Deals</option>
                  <option value="investors">Investors</option>
                </select>
              </div>
            </div>
            <div className="tabulate-modal-body">
              <table className="tabulate-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th
                      className={`sortable ${heroTableSort.field === 'name' ? 'sorted' : ''}`}
                      onClick={() => setHeroTableSort(prev => ({
                        field: 'name',
                        direction: prev.field === 'name' && prev.direction === 'asc' ? 'desc' : 'asc'
                      }))}
                    >
                      Name {heroTableSort.field === 'name' && (heroTableSort.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Industry/Type</th>
                    <th>Location</th>
                    <th
                      className={`sortable ${heroTableSort.field === 'date' ? 'sorted' : ''}`}
                      onClick={() => setHeroTableSort(prev => ({
                        field: 'date',
                        direction: prev.field === 'date' && prev.direction === 'asc' ? 'desc' : 'asc'
                      }))}
                    >
                      Date {heroTableSort.field === 'date' && (heroTableSort.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className={`sortable ${heroTableSort.field === 'amount' ? 'sorted' : ''}`}
                      onClick={() => setHeroTableSort(prev => ({
                        field: 'amount',
                        direction: prev.field === 'amount' && prev.direction === 'asc' ? 'desc' : 'asc'
                      }))}
                    >
                      Amount {heroTableSort.field === 'amount' && (heroTableSort.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    {heroSearchMode !== 'strict' && <th>Score</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Get all results (use tiered if available, otherwise raw results)
                    let allResults = heroSearchResults.tiered_results
                      ? [
                          ...(heroSearchResults.tiered_results.tier1 || []),
                          ...(heroSearchResults.tiered_results.tier2 || []),
                          ...(heroSearchResults.tiered_results.tier3 || [])
                        ]
                      : (heroSearchResults.results || []);

                    // Filter by entity type
                    if (heroEntityFilter !== 'all') {
                      const typeMap = { companies: 'company', deals: 'deal', investors: 'investor' };
                      allResults = allResults.filter(r => (r.entity_type || r.type) === typeMap[heroEntityFilter]);
                    }

                    // Robust sorting with entity-aware field extraction
                    if (heroTableSort.field) {
                      allResults = [...allResults].sort((a, b) => {
                        const aType = a.entity_type || a.type || 'company';
                        const bType = b.entity_type || b.type || 'company';
                        let aVal, bVal;

                        if (heroTableSort.field === 'name') {
                          // Name: companies have 'name', deals have 'companies' field
                          aVal = (a.name || a.companies || '').toString().toLowerCase().trim();
                          bVal = (b.name || b.companies || '').toString().toLowerCase().trim();
                        } else if (heroTableSort.field === 'date') {
                          // Date: deals have 'deal_date', companies have 'year_founded'
                          if (aType === 'deal' && a.deal_date) {
                            aVal = new Date(a.deal_date).getTime();
                          } else if (a.year_founded) {
                            aVal = new Date(`${a.year_founded}-01-01`).getTime();
                          } else {
                            aVal = 0;
                          }
                          if (bType === 'deal' && b.deal_date) {
                            bVal = new Date(b.deal_date).getTime();
                          } else if (b.year_founded) {
                            bVal = new Date(`${b.year_founded}-01-01`).getTime();
                          } else {
                            bVal = 0;
                          }
                        } else if (heroTableSort.field === 'amount') {
                          // Amount: deals have 'deal_size_usd', companies have 'total_raised_usd', investors have 'aum'
                          if (aType === 'deal') {
                            aVal = parseFloat(a.deal_size_usd) || 0;
                          } else if (aType === 'company') {
                            // total_raised might be string like "$100M" - try to parse
                            const raised = a.total_raised_usd || a.total_raised;
                            aVal = typeof raised === 'number' ? raised : parseFloat(String(raised).replace(/[^0-9.]/g, '')) || 0;
                          } else if (aType === 'investor') {
                            aVal = parseFloat(a.aum) || 0;
                          } else {
                            aVal = 0;
                          }
                          if (bType === 'deal') {
                            bVal = parseFloat(b.deal_size_usd) || 0;
                          } else if (bType === 'company') {
                            const raised = b.total_raised_usd || b.total_raised;
                            bVal = typeof raised === 'number' ? raised : parseFloat(String(raised).replace(/[^0-9.]/g, '')) || 0;
                          } else if (bType === 'investor') {
                            bVal = parseFloat(b.aum) || 0;
                          } else {
                            bVal = 0;
                          }
                        }

                        // Handle undefined/null - push to end
                        if (aVal === undefined || aVal === null || (typeof aVal === 'number' && isNaN(aVal))) aVal = heroTableSort.direction === 'asc' ? Infinity : -Infinity;
                        if (bVal === undefined || bVal === null || (typeof bVal === 'number' && isNaN(bVal))) bVal = heroTableSort.direction === 'asc' ? Infinity : -Infinity;

                        // Compare
                        if (aVal < bVal) return heroTableSort.direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return heroTableSort.direction === 'asc' ? 1 : -1;
                        return 0;
                      });
                    }

                    return allResults.map((result, idx) => {
                      const entityType = result.entity_type || result.type || 'company';
                      return (
                        <tr
                          key={result.pitchbook_id || result.pitchbook_deal_id || result.pitchbook_investor_id || idx}
                          className="tabulate-row"
                          onDoubleClick={() => handleHeroResultClick(result)}
                        >
                          <td><span className={`entity-badge-mini ${entityType}`}>{entityType}</span></td>
                          <td
                            className="tabulate-name clickable"
                            onClick={() => handleHeroResultClick(result)}
                          >
                            {result.name || result.companies || 'Unknown'}
                          </td>
                          <td>{result.primary_industry || result.deal_type || result.investor_type || '-'}</td>
                          <td>{result.hq_location || '-'}</td>
                          <td>{result.deal_date ? formatDate(result.deal_date) : result.year_founded || '-'}</td>
                          <td className="tabulate-amount">
                            {result.deal_size_usd ? `$${result.deal_size_usd.toFixed(1)}M` : result.total_raised || '-'}
                          </td>
                          {heroSearchMode !== 'strict' && (
                            <td className="tabulate-score">{result.score ? `${(result.score * 100).toFixed(0)}%` : '-'}</td>
                          )}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default LandingPage;